import { useState, useEffect, useMemo, useCallback, useRef, Component } from 'react'
import { supabase } from './supabaseClient'
import { gregorianToHebrew } from './hebrewCalendar'
import CalendarView from './CalendarView'
import ReportsView from './ReportsView'
import QRModal from './QRModal'
import { printUnitList, printTodoList, printChangeLog, printInventorySheet } from './PrintUtils'

const TODAY = new Date(); TODAY.setHours(0,0,0,0)
const addDays = (d,n) => { const r=new Date(d); r.setDate(r.getDate()+n); return r }
const fmt = d => d ? new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'
const fmtShort = d => d ? new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric'}) : '—'
const fmtInput = d => { const dt=new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}` }

function getInterval(u, season) { return season==='winter' ? u.winter_interval??null : u.summer_interval }
function getStatus(u, season) {
  const iv=getInterval(u,season)
  if(!iv) return {label:'OFF',color:'#8896a8',bg:'#f0f2f5',days:null}
  if(!u.last_changed) return {label:'NO DATE',color:'#8896a8',bg:'#f0f2f5',days:null}
  const due=addDays(new Date(u.last_changed),iv), days=Math.round((due-TODAY)/864e5)
  if(days<0)  return {label:'OVERDUE', color:'#c0392b',bg:'#fdecea',days}
  if(days<=3) return {label:'DUE SOON',color:'#d35400',bg:'#fef0e6',days}
  if(days<=7) return {label:'UPCOMING',color:'#b7860b',bg:'#fefae6',days}
  return         {label:'OK',          color:'#1e8449',bg:'#eafaf1',days}
}
function getStockStatus(i) {
  if(i.stock===0)            return {label:'Out of Stock', color:'#c0392b',bg:'#fdecea'}
  if(i.stock<i.min_stock)   return {label:'Low Stock — Reorder',color:'#d35400',bg:'#fef0e6'}
  if(i.stock===i.min_stock) return {label:'At Minimum Level',   color:'#b7860b',bg:'#fefae6'}
  return                           {label:'OK',           color:'#1e8449',bg:'#eafaf1'}
}
function getSeasonSplit(months) {
  // Summer = April(3) through September(8), Winter = October(9) through March(2)
  const today = new Date()
  let summerDays = 0, winterDays = 0
  for(let i=0; i<months*30; i++){
    const d = new Date(today)
    d.setDate(d.getDate() + i)
    const m = d.getMonth() // 0=Jan
    if(m>=3&&m<=8) summerDays++ // Apr-Sep = summer
    else winterDays++
  }
  return {
    summerMonths: Math.round(summerDays/30*10)/10,
    winterMonths: Math.round(winterDays/30*10)/10,
    summerDays, winterDays
  }
}

function calcOrderNeeds(units,inventory,months) {
  const {summerMonths,winterMonths} = getSeasonSplit(months)
  const needed={}
  units.forEach(u=>{
    if(u.filter_type==='washable') return
    const summerIv = u.summer_interval
    const winterIv = u.winter_interval
    if(summerIv&&summerMonths>0) needed[u.size]=(needed[u.size]||0)+Math.ceil(summerMonths*30/summerIv)*u.qty
    if(winterIv&&winterMonths>0) needed[u.size]=(needed[u.size]||0)+Math.ceil(winterMonths*30/winterIv)*u.qty
  })
  return inventory.map(inv=>{
    const total=needed[inv.size]||0,deficit=Math.max(0,total-inv.stock),boxes=Math.ceil(deficit/12)
    return {...inv,totalNeeded:total,deficit,boxes}
  }).filter(r=>r.boxes>0)
}
function exportToCSV(rows,filename) {
  if(!rows.length) return
  const keys=Object.keys(rows[0])
  const csv=[keys.join(','),...rows.map(r=>keys.map(k=>JSON.stringify(r[k]??'')).join(','))].join('\n')
  const a=document.createElement('a'); a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download=filename; a.click()
}

// Building hierarchy — maps area names to buildings
const BUILDING_MAP = {
  // Big Shul
  'Big Shul Unit #1': 'Big Shul', 'Big Shul Unit #2': 'Big Shul',
  'Big Shul Unit #3': 'Big Shul', 'Big Shul Unit #4': 'Big Shul',
  'Big Shul Unit #5': 'Big Shul', 'Big Shul Unit #6': 'Big Shul',
  'Big Shul Unit #7': 'Big Shul', 'Big Shul Unit #8': 'Big Shul',
  'Big ShulUnit #1':  'Big Shul',
  '2nd FL Shul Office': 'Big Shul',
  'Kiddush Room X 3': 'Big Shul',
  'Ladies Entrance 48 St': 'Big Shul',
  'Beis Horuah': 'Big Shul',
  // Old Shul / Coffee Room
  'Air Handler': 'Coffee Room',
  'Old Shul unit': 'Old Shul', 'Old Shul': 'Old Shul',
  // Individual rooms
  'Rabbi Room': 'Rabbi Room',
  'Cheder Daled': 'Cheder Daled',
  'Mishnas Yonah': 'Mishnas Yonah',
  'Cheder Schlaff': 'Cheder Schlaff', 'Schlaff Office': 'Cheder Schlaff',
  'New Extension - 47 St': '47th Street',
  // Sukah
  'Sukah Unit #1': 'Sukah', 'Sukah Unit #2': 'Sukah', 'Sukah Unit #3': 'Sukah',
  'Sukah Unit #4': 'Sukah', 'Sukah Unit #5': 'Sukah', 'Sukah Unit #6': 'Sukah',
  'Sukah Unit #7': 'Sukah', 'Ladies Sukah': 'Sukah',
  // Entrances
  '15 Ave Lobby Entrance': 'Main Entrance',
  // Mikva
  'Mikva 15 Ave Side': 'Mikva', 'Mikva 16 Ave Side': 'Mikva',
  // Pulish
  'Pulish': 'Pulish', 'Obstfeld Front': 'Pulish', 'Obstfeld Back': 'Pulish',
  'Pulish Front': 'Pulish', 'Pulish Back': 'Pulish',
  // Other
  'New Kolel Basement': 'New Kolel',
  'Basement Bathrooms': 'Basement',
}
function getBuilding(area) { return BUILDING_MAP[area] || area }

const C={navy:'#0f2237',blue:'#1a3d5c',steel:'#2d6a9f',bg:'#f4f6f9',card:'#fff',border:'#e2e8f0',text:'#1e2a3a',muted:'#6b7c93'}
const TH={padding:'8px 12px',fontWeight:700,fontSize:11,color:C.muted,textTransform:'uppercase',letterSpacing:'.06em',borderBottom:`1px solid ${C.border}`,background:'#f8fafc',whiteSpace:'nowrap'}
const TD={padding:'9px 12px',fontSize:12,color:C.text,borderBottom:`1px solid ${C.border}`}
const NAV=[{key:'dashboard',label:'Dashboard',icon:'⊡'},{key:'log',label:'Log',icon:'✓'},{key:'calendar',label:'Calendar',icon:'◫'},{key:'inventory',label:'Inventory',icon:'⊞'},{key:'orders',label:'Orders',icon:'⊕'},{key:'history',label:'Log History',icon:'≡'},{key:'reports',label:'Reports',icon:'◑'},{key:'config',label:'Settings',icon:'⚙'}]
const NAV_FULL={dashboard:'Dashboard',log:'Log Replacement',calendar:'Calendar',inventory:'Inventory',orders:'Orders',history:'Change Log',reports:'Reports',config:'Settings'}
const PO_STATUS={pending_approval:{label:'Pending Approval',color:'#b7860b',bg:'#fefae6'},approved:{label:'Approved',color:'#1e6b44',bg:'#eafaf1'},sent:{label:'Sent',color:'#1a5c9a',bg:'#eef5fb'},partial:{label:'Partial',color:'#d35400',bg:'#fef0e6'},complete:{label:'Complete',color:'#1e8449',bg:'#d4edda'},rejected:{label:'Rejected',color:'#c0392b',bg:'#fdecea'},draft:{label:'Draft',color:'#6b7c93',bg:'#f0f2f5'}}

function Badge({label,color,bg,small=false}) {
  return <span style={{background:bg,color,padding:small?'2px 7px':'3px 9px',borderRadius:20,fontSize:small?9:10,fontWeight:700,whiteSpace:'nowrap'}}>{label}</span>
}
function Avatar({name='?',size=28}) {
  const colors=['#1a6b8a','#5b4a8a','#1e6b44','#8a4a1a','#2d6a9f','#7a3060']
  return <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:size,height:size,borderRadius:'50%',background:colors[name.charCodeAt(0)%colors.length],color:'#fff',fontWeight:700,fontSize:size*.38,flexShrink:0}}>{name.split(' ').map(p=>p[0]).join('').toUpperCase().slice(0,2)}</span>
}
function Card({children,style={}}) { return <div style={{background:C.card,borderRadius:12,boxShadow:'0 1px 4px rgba(0,0,0,.07)',overflow:'hidden',...style}}>{children}</div> }
function Btn({children,onClick,color=C.navy,disabled=false,small=false,outline=false,danger=false,style={}}) {
  const bg=danger?'#c0392b':disabled?'#ccc':outline?'transparent':color
  return <button onClick={onClick} disabled={disabled} style={{background:bg,color:outline?(danger?'#c0392b':color):'#fff',border:outline?`2px solid ${danger?'#c0392b':color}`:'none',borderRadius:8,padding:small?'6px 12px':'10px 20px',fontSize:small?11:13,fontWeight:600,cursor:disabled?'not-allowed':'pointer',opacity:disabled?.65:1,...style}}>{children}</button>
}
function SLabel({children}) { return <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>{children}</div> }
function ScrollTable({children}) { return <div style={{overflowX:'auto',WebkitOverflowScrolling:'touch'}}>{children}</div> }
function SeasonToggle({season,onToggle,canToggle}) {
  const s=season==='summer'
  return <button onClick={canToggle?onToggle:undefined} style={{display:'flex',alignItems:'center',gap:8,background:s?'#fff8e1':'#e8f4fd',border:`2px solid ${s?'#f59e0b':'#3b82f6'}`,borderRadius:20,padding:'6px 14px',cursor:canToggle?'pointer':'default'}}>
    <span style={{fontSize:16}}>{s?'☀️':'❄️'}</span>
    <span style={{fontSize:12,fontWeight:700,color:s?'#92400e':'#1e40af'}}>{s?'SUMMER':'WINTER'}</span>
    {canToggle&&<span style={{fontSize:10,color:C.muted}}>(tap)</span>}
  </button>
}

// ── Login ─────────────────────────────────────────────────────
function LoginScreen() {
  const [mode,setMode]=useState('login')
  const [email,setEmail]=useState('')
  const [password,setPassword]=useState('')
  const [loading,setLoading]=useState(false)
  const [error,setError]=useState('')
  const [message,setMessage]=useState('')
  const inp={width:'100%',border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 14px',fontSize:16,outline:'none',background:'#fff',marginBottom:12}

  async function handleLogin(e) {
    e.preventDefault(); setError(''); setLoading(true)
    const {error}=await supabase.auth.signInWithPassword({email,password})
    if(error) setError(error.message)
    setLoading(false)
  }
  async function handleForgot(e) {
    e.preventDefault(); setError(''); setLoading(true)
    const {error}=await supabase.auth.resetPasswordForEmail(email,{redirectTo:window.location.origin})
    if(error) setError(error.message)
    else setMessage('Password reset email sent! Check your inbox.')
    setLoading(false)
  }

  return <div style={{minHeight:'100vh',background:`linear-gradient(135deg, #0f2237 0%, #1a3d5c 100%)`,display:'flex',alignItems:'center',justifyContent:'center',padding:20,fontFamily:"'Sora',system-ui,sans-serif"}}>
    <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
    <div style={{background:'#fff',borderRadius:20,padding:36,width:'100%',maxWidth:420,boxShadow:'0 24px 80px rgba(0,0,0,.4)'}}>
      <div style={{textAlign:'center',marginBottom:28}}>
        <div style={{width:64,height:64,background:'#0f2237',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,margin:'0 auto 14px'}}>⚙️</div>
        <div style={{fontSize:22,fontWeight:800,color:C.navy}}>HVAC Filter Manager</div>
        <div style={{fontSize:12,color:C.muted,marginTop:4}}>{mode==='login'?'Sign in to your account':'Reset your password'}</div>
      </div>
      {error&&<div style={{background:'#fdecea',border:'1px solid #f5c6c6',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#c0392b',marginBottom:14}}>{error}</div>}
      {message&&<div style={{background:'#eafaf1',border:'1px solid #a9dfbf',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#1e8449',marginBottom:14}}>{message}</div>}
      <form onSubmit={mode==='login'?handleLogin:handleForgot}>
        <SLabel>Email address</SLabel>
        <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" required style={inp}/>
        {mode==='login'&&<><SLabel>Password</SLabel><input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="Your password" required style={{...inp,marginBottom:20}}/></>}
        <Btn disabled={loading} style={{width:'100%',textAlign:'center',padding:'13px',fontSize:14}}>
          {loading?'Please wait…':mode==='login'?'Sign In →':'Send Reset Email'}
        </Btn>
      </form>
      <div style={{textAlign:'center',marginTop:16}}>
        {mode==='login'
          ?<button onClick={()=>{setMode('forgot');setError('');setMessage('')}} style={{background:'none',border:'none',color:C.steel,fontSize:12,cursor:'pointer',textDecoration:'underline'}}>Forgot password?</button>
          :<button onClick={()=>{setMode('login');setError('');setMessage('')}} style={{background:'none',border:'none',color:C.steel,fontSize:12,cursor:'pointer',textDecoration:'underline'}}>← Back to sign in</button>}
      </div>
    </div>
  </div>
}

// ── Set Password (after invite/reset) ─────────────────────────
function SetPasswordScreen() {
  const [password,setPassword]=useState('')
  const [confirm,setConfirm]=useState('')
  const [loading,setLoading]=useState(false)
  const [sessionLoading,setSessionLoading]=useState(true)
  const [error,setError]=useState('')
  const [done,setDone]=useState(false)
  const inp={width:'100%',border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 14px',fontSize:16,outline:'none',background:'#fff',marginBottom:12}

  // Exchange the hash token for a session on mount
  useEffect(()=>{
    async function exchangeToken() {
      const hash = window.location.hash
      if(hash && hash.includes('access_token')) {
        // Supabase automatically picks up the hash token
        const {error} = await supabase.auth.getSession()
        if(error) setError('Invalid or expired link. Please request a new one.')
      }
      setSessionLoading(false)
    }
    exchangeToken()
  },[])

  async function handleSet(e) {
    e.preventDefault()
    if(password!==confirm){setError('Passwords do not match');return}
    if(password.length<8){setError('Password must be at least 8 characters');return}
    setError(''); setLoading(true)
    const {error}=await supabase.auth.updateUser({password})
    if(error) setError(error.message)
    else {
      setDone(true)
      // Clear the hash from URL
      window.history.replaceState(null,'',window.location.pathname)
    }
    setLoading(false)
  }

  if(sessionLoading) return <div style={{minHeight:'100vh',background:'linear-gradient(135deg,#0f2237,#1a3d5c)',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16}}>
    <div style={{width:32,height:32,border:'3px solid rgba(255,255,255,.2)',borderTop:'3px solid #fff',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
    <div style={{color:'#fff',fontSize:13}}>Setting up your account…</div>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>

  return <div style={{minHeight:'100vh',background:`linear-gradient(135deg, #0f2237 0%, #1a3d5c 100%)`,display:'flex',alignItems:'center',justifyContent:'center',padding:20,fontFamily:"'Sora',system-ui,sans-serif"}}>
    <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
    <div style={{background:'#fff',borderRadius:20,padding:36,width:'100%',maxWidth:420,boxShadow:'0 24px 80px rgba(0,0,0,.4)'}}>
      <div style={{textAlign:'center',marginBottom:28}}>
        <div style={{width:64,height:64,background:'#0f2237',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,margin:'0 auto 14px'}}>🔐</div>
        <div style={{fontSize:22,fontWeight:800,color:C.navy}}>Set Your Password</div>
        <div style={{fontSize:12,color:C.muted,marginTop:4}}>Welcome to HVAC Filter Manager</div>
      </div>
      {done
        ?<div style={{textAlign:'center'}}>
          <div style={{fontSize:48,marginBottom:12}}>✅</div>
          <div style={{fontWeight:700,color:'#1e8449',marginBottom:8,fontSize:16}}>Password set!</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:20}}>You can now sign in with your email and password.</div>
          <Btn onClick={()=>window.location.href='/'} style={{width:'100%',textAlign:'center'}}>Go to Sign In</Btn>
        </div>
        :<form onSubmit={handleSet}>
          {error&&<div style={{background:'#fdecea',border:'1px solid #f5c6c6',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#c0392b',marginBottom:14}}>{error}</div>}
          <SLabel>New Password</SLabel>
          <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="Min. 8 characters" required style={inp}/>
          <SLabel>Confirm Password</SLabel>
          <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="Repeat password" required style={{...inp,marginBottom:20}}/>
          <Btn disabled={loading} style={{width:'100%',textAlign:'center',padding:'13px'}}>
            {loading?'Setting password…':'Set Password & Sign In'}
          </Btn>
        </form>}
    </div>
  </div>
}

// ── Change Password Modal ──────────────────────────────────────
function ChangePasswordModal({onClose}) {
  // eslint-disable-next-line no-unused-vars
  const [current,setCurrent]=useState('')
  const [next,setNext]=useState('')
  const [confirm,setConfirm]=useState('')
  const [loading,setLoading]=useState(false)
  const [error,setError]=useState('')
  const [done,setDone]=useState(false)
  const inp={width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'11px 13px',fontSize:16,outline:'none',background:'#fff',marginBottom:12}

  async function handleChange(e) {
    e.preventDefault()
    if(next!==confirm){setError('Passwords do not match');return}
    if(next.length<8){setError('Password must be at least 8 characters');return}
    setError(''); setLoading(true)
    const {error}=await supabase.auth.updateUser({password:next})
    if(error) setError(error.message)
    else setDone(true)
    setLoading(false)
  }

  return <div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,backdropFilter:'blur(3px)',padding:20}}>
    <div style={{background:'#fff',borderRadius:18,padding:28,width:'100%',maxWidth:400,boxShadow:'0 12px 48px rgba(0,0,0,.25)'}}>
      <h2 style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:4}}>Change Password</h2>
      <p style={{fontSize:12,color:C.muted,marginBottom:20}}>Choose a strong password of at least 8 characters.</p>
      {done
        ?<div style={{textAlign:'center',padding:'12px 0'}}>
          <div style={{fontSize:36,marginBottom:8}}>✅</div>
          <div style={{fontWeight:700,color:'#1e8449',marginBottom:16}}>Password changed!</div>
          <Btn onClick={onClose} style={{width:'100%',textAlign:'center'}}>Done</Btn>
        </div>
        :<form onSubmit={handleChange}>
          {error&&<div style={{background:'#fdecea',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#c0392b',marginBottom:12}}>{error}</div>}
          <SLabel>New Password</SLabel>
          <input type="password" value={next} onChange={e=>setNext(e.target.value)} placeholder="Min. 8 characters" required style={inp}/>
          <SLabel>Confirm New Password</SLabel>
          <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="Repeat new password" required style={{...inp,marginBottom:20}}/>
          <div style={{display:'flex',gap:8}}>
            <Btn outline color={C.muted} onClick={onClose} style={{flex:1}}>Cancel</Btn>
            <Btn disabled={loading} style={{flex:2,textAlign:'center'}}>{loading?'Saving…':'Change Password'}</Btn>
          </div>
        </form>}
    </div>
  </div>
}

// ── Unit History Modal ─────────────────────────────────────────
function UnitHistoryModal({unit, changelog, onClose}) {
  const history = changelog.filter(l=>l.unit_id===unit.id).sort((a,b)=>new Date(b.change_date)-new Date(a.change_date))
  return <div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.7)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:999,backdropFilter:'blur(3px)'}}>
    <div style={{background:'#fff',borderRadius:'18px 18px 0 0',padding:'24px 20px',width:'100%',maxWidth:600,maxHeight:'85vh',overflowY:'auto'}}>
      <div style={{width:40,height:4,background:'#e2e8f0',borderRadius:4,margin:'0 auto 16px'}}/>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
        <div>
          <h2 style={{fontSize:16,fontWeight:800,color:C.text}}>{unit.location}</h2>
          <div style={{fontSize:12,color:C.muted}}>{unit.area} · {unit.filter_type==='washable'?'♻️ Washable':`${unit.size} × ${unit.qty}`}</div>
        </div>
        <button onClick={onClose} style={{background:'#f4f6f9',border:'none',borderRadius:8,padding:'6px 12px',cursor:'pointer',fontSize:12,color:C.muted}}>Close</button>
      </div>
      {history.length===0
        ?<div style={{textAlign:'center',padding:32,color:C.muted}}>No service history yet for this unit.</div>
        :<div>
          {history.map((l,i)=><div key={l.id} style={{display:'flex',gap:14,marginBottom:16,paddingBottom:16,borderBottom:i<history.length-1?`1px solid ${C.border}`:'none'}}>
            <div style={{width:40,height:40,borderRadius:10,background:'#eef5fb',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>{l.season==='summer'?'☀️':'❄️'}</div>
            <div style={{flex:1}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                <span style={{fontWeight:700,fontSize:13,color:C.text}}>{fmt(l.change_date)}</span>
                <Badge label={l.condition||'—'} color={C.steel} bg="#eef5fb"/>
              </div>
              <div style={{fontSize:12,color:C.muted}}>Replaced by <strong style={{color:C.text}}>{l.worker_name}</strong> · Logged by {l.logged_by_name}</div>
              {l.notes&&<div style={{fontSize:11,color:C.muted,marginTop:4,fontStyle:'italic'}}>{l.notes}</div>}
            </div>
          </div>)}
        </div>}
    </div>
  </div>
}

// ── Unit Form Modal ────────────────────────────────────────────
function UnitFormModal({unit,onClose,onSave}) {
  const blank={area:'',location:'',filter_type:'standard',size:'',qty:1,summer_interval:30,winter_interval:'',notes:''}
  const [form,setForm]=useState(unit?{area:unit.area,location:unit.location,filter_type:unit.filter_type||'standard',size:unit.size||'',qty:unit.qty,summer_interval:unit.summer_interval,winter_interval:unit.winter_interval||'',notes:unit.notes||''}:blank)
  const [saving,setSaving]=useState(false)
  const inp={width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'11px 13px',fontSize:16,outline:'none',background:'#fff'}
  async function save() {
    if(!form.area.trim()||!form.location.trim()) return
    if(form.filter_type==='standard'&&!form.size.trim()) return
    setSaving(true)
    const p={area:form.area.trim(),location:form.location.trim(),unit_name:form.area.trim(),physical_location:form.location.trim(),filter_type:form.filter_type,size:form.filter_type==='washable'?'Washable':form.size.trim(),qty:parseInt(form.qty)||1,summer_interval:parseInt(form.summer_interval)||30,winter_interval:form.winter_interval===''?null:parseInt(form.winter_interval),notes:form.notes.trim(),active:true}
    if(unit) {
      await supabase.from('units').update(p).eq('id',unit.id)
      // Also update unit_filters for this unit
      const {data:existingFilters} = await supabase.from('unit_filters').select('*').eq('unit_id',unit.id)
      if(existingFilters&&existingFilters.length===1) {
        // Single filter - update it
        await supabase.from('unit_filters').update({
          size:p.size,
          qty:p.qty,
          filter_type:p.filter_type,
          summer_interval:p.summer_interval,
          winter_interval:p.winter_interval,
        }).eq('unit_id',unit.id)
      }
    }
    else await supabase.from('units').insert(p)
    setSaving(false); onSave()
  }
  return <div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.7)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:999,backdropFilter:'blur(3px)'}}>
    <div style={{background:'#fff',borderRadius:'18px 18px 0 0',padding:'24px 20px',width:'100%',maxWidth:560,maxHeight:'92vh',overflowY:'auto'}}>
      <div style={{width:40,height:4,background:'#e2e8f0',borderRadius:4,margin:'0 auto 20px'}}/>
      <h2 style={{fontSize:17,fontWeight:800,color:C.text,marginBottom:20}}>{unit?'Edit Unit':'Add New Unit'}</h2>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
        <div><SLabel>Area / Building *</SLabel><input value={form.area} onChange={e=>setForm({...form,area:e.target.value})} placeholder="e.g. Coffee Room" style={inp}/></div>
        <div><SLabel>Location *</SLabel><input value={form.location} onChange={e=>setForm({...form,location:e.target.value})} placeholder="e.g. Air Handler" style={inp}/></div>
      </div>
      <div style={{marginBottom:12}}>
        <SLabel>Filter Type</SLabel>
        <div style={{display:'flex',gap:8}}>
          {['standard','washable'].map(t=><button key={t} onClick={()=>setForm({...form,filter_type:t})} style={{flex:1,padding:'10px 0',border:`2px solid ${form.filter_type===t?C.navy:C.border}`,borderRadius:9,background:form.filter_type===t?C.navy:'#fff',color:form.filter_type===t?'#fff':C.muted,fontWeight:600,fontSize:13,cursor:'pointer'}}>{t==='standard'?'🔲 Standard':'♻️ Washable'}</button>)}
        </div>
      </div>
      {form.filter_type==='standard'&&<div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,marginBottom:12}}>
        <div><SLabel>Filter Size *</SLabel><input value={form.size} onChange={e=>setForm({...form,size:e.target.value})} placeholder="e.g. 20x20x2" style={inp}/></div>
        <div><SLabel>Qty</SLabel><input type="number" min={1} value={form.qty} onChange={e=>setForm({...form,qty:e.target.value})} style={inp}/></div>
      </div>}
      {form.filter_type==='washable'&&<div style={{marginBottom:12}}><SLabel>Qty</SLabel><input type="number" min={1} value={form.qty} onChange={e=>setForm({...form,qty:e.target.value})} style={inp}/></div>}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
        <div><SLabel>Summer Interval (days)</SLabel><input type="number" min={1} value={form.summer_interval} onChange={e=>setForm({...form,summer_interval:e.target.value})} style={inp}/></div>
        <div><SLabel>Winter Interval (days)</SLabel><input type="number" min={1} value={form.winter_interval} onChange={e=>setForm({...form,winter_interval:e.target.value})} placeholder="Leave blank to disable" style={inp}/></div>
      </div>
      <div style={{marginBottom:20}}><SLabel>Notes</SLabel><textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} rows={2} style={{...inp,resize:'vertical'}}/></div>
      <div style={{display:'flex',gap:10}}>
        <Btn outline color={C.muted} onClick={onClose} style={{flex:1}}>Cancel</Btn>
        <Btn disabled={saving||!form.area.trim()||!form.location.trim()||(form.filter_type==='standard'&&!form.size.trim())} onClick={save} style={{flex:2}}>{saving?'Saving…':unit?'Save Changes':'Add Unit'}</Btn>
      </div>
    </div>
  </div>
}

// ── Bulk Date Modal ────────────────────────────────────────────
function BulkDateModal({units, onClose, onSave}) {
  const noDateUnits = units.filter(u=>!u.last_changed)
  const [date,setDate]=useState(fmtInput(TODAY))
  const [saving,setSaving]=useState(false)
  const [progress,setProgress]=useState(0)

  async function handleSave() {
    setSaving(true)
    for(let i=0;i<noDateUnits.length;i++){
      await supabase.from('units').update({last_changed:date}).eq('id',noDateUnits[i].id)
      setProgress(i+1)
    }
    setSaving(false)
    onSave()
  }

  return <div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,backdropFilter:'blur(3px)',padding:20}}>
    <div style={{background:'#fff',borderRadius:18,padding:28,width:'100%',maxWidth:440,boxShadow:'0 12px 48px rgba(0,0,0,.25)'}}>
      <h2 style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:6}}>Set Service Date for All Units</h2>
      <p style={{fontSize:12,color:C.muted,marginBottom:20}}>{noDateUnits.length} units have no date. Set a date below — this is the date of the last filter change or the installation date.</p>
      <SLabel>Date of Last Service</SLabel>
      <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'11px 13px',fontSize:16,outline:'none',marginBottom:8}}/>
      <div style={{fontSize:11,color:C.muted,marginBottom:20}}>Next due dates will be calculated from this date based on each unit's interval.</div>
      {saving&&<div style={{marginBottom:16}}>
        <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:C.muted,marginBottom:6}}><span>Setting dates…</span><span>{progress}/{noDateUnits.length}</span></div>
        <div style={{background:'#e2e8f0',borderRadius:20,height:8,overflow:'hidden'}}><div style={{width:`${(progress/noDateUnits.length)*100}%`,background:C.steel,height:'100%',borderRadius:20,transition:'width .2s'}}/></div>
      </div>}
      <div style={{display:'flex',gap:8}}>
        <Btn outline color={C.muted} onClick={onClose} style={{flex:1}} disabled={saving}>Cancel</Btn>
        <Btn disabled={saving||!date} onClick={handleSave} style={{flex:2,textAlign:'center'}}>{saving?'Setting dates…':`Set Date for All ${noDateUnits.length} Units`}</Btn>
      </div>
    </div>
  </div>
}

// ═══════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(e) { return { error: e } }
  componentDidCatch(e, info) { console.error('App crashed:', e, info) }
  render() {
    if(this.state.error) return <div style={{padding:32,fontFamily:'monospace',background:'#fdecea',minHeight:'100vh'}}>
      <h2 style={{color:'#c0392b'}}>Something went wrong. Please refresh the page.</h2>
      <pre style={{whiteSpace:'pre-wrap',fontSize:11,marginTop:12,color:'#666'}}>{this.state.error.toString()}</pre>
      <button onClick={()=>window.location.reload()} style={{marginTop:16,padding:'10px 20px',background:'#0f2237',color:'#fff',border:'none',borderRadius:8,cursor:'pointer'}}>Reload App</button>
    </div>
    return this.props.children
  }
}

// ── Qty Control Component ──────────────────────────────────────
function QtyControl({value, max, onChange, onReset, originalQty}) {
  const boxes = Math.floor(value/12)
  const singles = value%12
  const stockLabel = [boxes>0?`${boxes} box${boxes>1?'es':''}`:'',singles>0?`${singles} single${singles>1?'s':''}`:''].filter(Boolean).join(' + ') || '0 filters'
  const isPartial = value < max
  const isOriginal = value === originalQty
  return <div>
    <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
      <button onClick={()=>onChange(Math.max(1,value-1))} disabled={value<=1}
        style={{width:44,height:44,borderRadius:10,border:'1.5px solid #e2e8f0',background:value<=1?'#f4f6f9':'#fdecea',fontSize:22,fontWeight:700,cursor:value<=1?'not-allowed':'pointer',color:value<=1?'#ccc':'#c0392b',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>−</button>
      <input type="number" min={1} max={max} value={value}
        onChange={e=>onChange(Math.min(max,Math.max(1,parseInt(e.target.value)||1)))}
        style={{width:64,height:44,textAlign:'center',fontSize:20,fontWeight:800,color:isPartial?'#d35400':'#0f2237',border:`2px solid ${isPartial?'#f0c070':'#a9dfbf'}`,borderRadius:10,outline:'none',background:isPartial?'#fffdf0':'#f5fdf8'}}/>
      <button onClick={()=>onChange(Math.min(max,value+1))} disabled={value>=max}
        style={{width:44,height:44,borderRadius:10,border:'1.5px solid #e2e8f0',background:value>=max?'#f4f6f9':'#eafaf1',fontSize:22,fontWeight:700,cursor:value>=max?'not-allowed':'pointer',color:value>=max?'#ccc':'#1e8449',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>+</button>
      <span style={{fontSize:12,color:'#6b7c93',whiteSpace:'nowrap'}}>of <strong>{max}</strong></span>
      <span style={{fontSize:11,fontWeight:700,padding:'3px 9px',borderRadius:20,background:isPartial?'#fff0d0':'#eafaf1',color:isPartial?'#d35400':'#1e8449',whiteSpace:'nowrap'}}>{isPartial?'⚠️ Partial':'✓ Full'}</span>
      {!isOriginal&&originalQty&&<button onClick={onReset} style={{fontSize:11,fontWeight:700,color:'#2d6a9f',background:'#eef5fb',border:'none',borderRadius:7,padding:'5px 10px',cursor:'pointer',whiteSpace:'nowrap'}}>↺ Reset to {originalQty}</button>}
    </div>
    <div style={{fontSize:11,color:'#6b7c93',marginTop:5}}>📦 {stockLabel}</div>
  </div>
}

function App() {
  const [authState,setAuthState]=useState('loading')
  const [authUser,setAuthUser]=useState(null)
  const [currentUser,setUser]=useState(null)
  const [tab,setTab]=useState('dashboard')
  const [loading,setLoading]=useState(true) // eslint-disable-line
  const [season,setSeason]=useState('summer')
  const [isMobile,setIsMobile]=useState(window.innerWidth<768)
  const [showChangePassword,setShowChangePassword]=useState(false)
  const [showUserMenu,setShowUserMenu]=useState(false)

  const [units,setUnits]=useState([])
  const [unitFilters,setUnitFilters]=useState([]) // eslint-disable-line no-unused-vars
  const [schedulingRules,setSchedulingRules]=useState({blocked_dow:[0,6],block_yomtov:true,block_chol_hamoed:true,block_us_holidays:false,push_direction:'earlier'})
  const [unitGroups,setUnitGroups]=useState([])
  const [schedulingEvents,setSchedulingEvents]=useState([])

  const [inventory,setInv]=useState([])
  const [changelog,setCL]=useState([])
  const [orders,setOrders]=useState([])
  const [poItems,setPOItems]=useState([])
  const [users,setUsers]=useState([])
  const [unitOrder,setUnitOrder]=useState({}) // persisted to supabase

  const [selectedUnits,setSelectedUnits]=useState([])
  const [logSearch,setLogSearch]=useState('')
  const [logForm,setLogForm]=useState({date:fmtInput(TODAY),worker:'',condition:'Moderate',notes:''})
  const [logSaving,setLogSaving]=useState(false)
  const [logStep,setLogStep]=useState(1)
  const [lastWorker,setLastWorker]=useState('')
  const [logSort,setLogSort]=useState('due')
  const [logFilterStatus,setLogFilterStatus]=useState(new Set())   // multi-select
  const [logFilterBuilding,setLogFilterBuilding]=useState(new Set()) // multi-select
  const [logFilterType,setLogFilterType]=useState(new Set())        // multi-select
  const [logFilterAssigned,setLogFilterAssigned]=useState(new Set()) // multi-select
  const [showLogFilters,setShowLogFilters]=useState(false) // expanded filters hidden by default
  const [stockPrompt,setStockPrompt]=useState(null) // {units, pendingLog} - asks about first-time inventory deduction
  const [techFilter,setTechFilter]=useState('all') // manager filter by technician
  const [dashView,setDashView]=useState(null) // null=auto, 'my'=my units, 'all'=all units

  const [dashExpand,setDashExpand]=useState(null)
  const [invSearch,setInvSearch]=useState('')
  // eslint-disable-next-line no-unused-vars
  const [showAddFilter,setShowAddFilter]=useState(false)
  const [newFilterForm,setNewFilterForm]=useState({size:'',min_stock:0,brand:'',notes:''})
  // eslint-disable-next-line no-unused-vars
  const [addingFilter,setAddingFilter]=useState(false)
  const [editingFilter,setEditingFilter]=useState(null) // {id, size, min_stock, brand, notes}
  // eslint-disable-next-line no-unused-vars
  const [savingFilter,setSavingFilter]=useState(false)
  const [unitHistoryModal,setUnitHistoryModal]=useState(null)
  const [editLogModal,setEditLogModal]=useState(null)   // log entry being edited
  const [editLogForm,setEditLogForm]=useState({change_date:'',worker_name:'',condition:'Moderate',notes:''})
  const [deleteLogModal,setDeleteLogModal]=useState(null) // log entry being deleted
  const [deleteLogReason,setDeleteLogReason]=useState('')
  // eslint-disable-next-line no-unused-vars
  const [showDeleted,setShowDeleted]=useState(false)
  const [showInspectModal,setShowInspectModal]=useState(false)
  const [showGroupModal,setShowGroupModal]=useState(false)
  const [editGroup,setEditGroup]=useState(null)
  const [showEventModal,setShowEventModal]=useState(false)
  const [editEvent,setEditEvent]=useState(null)

  const [inspectUnit,setInspectUnit]=useState(null)
  const [inspectForm,setInspectForm]=useState({condition:'Light',postpone_days:14,notes:'',photo:null})
  const [inspecting,setInspecting]=useState(false)
  const [histTab,setHistTab]=useState('active') // 'active' | 'deleted'
  const [showBulkDate,setShowBulkDate]=useState(false)
  const [stockConfirm,setStockConfirm]=useState(null) // {item, delta}

  const [histSearch,setHistSearch]=useState('')
  const [histSort,setHistSort]=useState('date-desc')
  const [histArea,setHistArea]=useState('') // eslint-disable-line no-unused-vars
  const [histWorker,setHistWorker]=useState('')
  const [histDateFrom,setHistDateFrom]=useState('')
  const [histDateTo,setHistDateTo]=useState('')

  const [orderMonths,setOrderMo]=useState(6)
  const [orderSupplier,setOrderSup]=useState({name:'',email:''})
  const [orderNotes,setOrderNotes]=useState('')
  const [orderItems,setOrderItems]=useState([])
  const [showOrderForm,setShowOF]=useState(false)
  const [receiveModal,setReceiveModal]=useState(null)
  const [receiveForm,setReceiveForm]=useState({})
  // eslint-disable-next-line no-unused-vars
  const [rejectModal,setRejectModal]=useState(null)
  // eslint-disable-next-line no-unused-vars
  const [rejectReason,setRejectReason]=useState('')
  const [followupPrompt,setFollowupPrompt]=useState(null) // {shortItems} after partial receive
  // eslint-disable-next-line no-unused-vars
  const [deleteConfirmPO,setDeleteConfirmPO]=useState(null)

  const [showUserForm,setShowUserForm]=useState(false)
  const [userForm,setUserForm]=useState({name:'',email:'',role:'Technician'})
  const [inviting,setInviting]=useState(false)
  const [inviteMsg,setInviteMsg]=useState('')
  const [showUnitForm,setShowUnitForm]=useState(false)
  const [editingUnit,setEditingUnit]=useState(null)
  const [configTab,setConfigTab]=useState('units')
  const [configSearch,setConfigSearch]=useState('') // eslint-disable-line
  const [configBuildingFilter,setConfigBuildingFilter]=useState('all') // eslint-disable-line
  const [showQRModal,setShowQRModal]=useState(false)
  const [showTodoPrint,setShowTodoPrint]=useState(false)
  const [todoPeriod,setTodoPeriod]=useState('today')
  const [todoWorker,setTodoWorker]=useState('all')
  const [bulkAssignSelected,setBulkAssignSelected]=useState(new Set())
  const [bulkAssignTo,setBulkAssignTo]=useState('')

  const dragItem=useRef(null)
  const dragOver=useRef(null)

  const isAdmin=currentUser?.role==='Admin'
  const isManager=currentUser?.role==='Manager'||isAdmin
  const canEditUnits=isAdmin

  useEffect(()=>{
    const h=()=>setIsMobile(window.innerWidth<768)
    window.addEventListener('resize',h)
    return()=>window.removeEventListener('resize',h)
  },[])

  // Auth
  useEffect(()=>{
    function getHashType() {
      const hash = window.location.hash
      const search = window.location.search
      const hashParams = new URLSearchParams(hash.replace('#',''))
      const queryParams = new URLSearchParams(search)
      return hashParams.get('type') || queryParams.get('type') || ''
    }

    // Check on initial load
    const hashType = getHashType()
    if(['invite','recovery','magiclink'].includes(hashType)){
      setAuthState('set-password')
      return
    }

    supabase.auth.getSession().then(({data:{session}})=>{
      if(!session){setAuthState('login');return}
      setAuthUser(session.user); setAuthState('app')
    })

    const {data:{subscription}}=supabase.auth.onAuthStateChange((event,session)=>{
      // PASSWORD_RECOVERY = reset link clicked
      if(event==='PASSWORD_RECOVERY'){setAuthState('set-password');return}
      // SIGNED_IN via invite link = show set password
      if(event==='SIGNED_IN'){
        const type=getHashType()
        if(['invite','magiclink'].includes(type)){setAuthState('set-password');return}
      }
      // USER_UPDATED = password was just set, go to app
      if(event==='USER_UPDATED'){
        if(session){setAuthUser(session.user);setAuthState('app')}
        return
      }
      if(!session){setAuthState('login');setUser(null);return}
      setAuthUser(session.user); setAuthState('app')
    })
    return()=>subscription.unsubscribe()
  },[])

  const loadAll=useCallback(async()=>{
    try {
      const [{data:un},{data:uf},{data:sr},{data:ug},{data:se},{data:inv},{data:cl},{data:po},{data:poi},{data:us},{data:sett}]=await Promise.all([
        supabase.from('units').select('*').eq('active',true).order('area').order('location'),
        supabase.from('unit_filters').select('*').order('created_at'),
        supabase.from('scheduling_rules').select('*').limit(1),
        supabase.from('unit_groups').select('*').order('name'),
        supabase.from('scheduling_events').select('*').order('name'),
        supabase.from('inventory').select('*').order('size'),
        supabase.from('change_log').select('*').order('change_date',{ascending:false}).limit(500),
        supabase.from('purchase_orders').select('*').order('created_at',{ascending:false}),
        supabase.from('po_items').select('*'),
        supabase.from('users').select('*').eq('active',true).order('name'),
        supabase.from('settings').select('*'),
        supabase.from('scheduling_rules').select('*').limit(1),
        supabase.from('unit_groups').select('*').order('name'),
        supabase.from('scheduling_events').select('*').order('name'),
      ])
      // Expand units: one row per unit_filter
      const expandedUnits = (un||[]).flatMap(unit => {
        const filters = (uf||[]).filter(f=>f.unit_id===unit.id)
        if(filters.length===0) return [{
          ...unit,
          area: unit.unit_name || unit.area,
          location: unit.physical_location || unit.location,
          unit_filter_id: null
        }]
        return filters.map((f,i)=>({
          ...unit,
          area: unit.unit_name || unit.area,
          location: unit.physical_location || unit.location,
          size: f.size,
          qty: f.qty,
          filter_type: f.filter_type,
          summer_interval: f.summer_interval,
          winter_interval: f.winter_interval,
          last_changed: f.last_changed || unit.last_changed,
          unit_filter_id: f.id,
          _filter_index: i,
          _filter_count: filters.length
        }))
      })
      setUnits(expandedUnits)
      if(sr&&sr[0]) setSchedulingRules(sr[0])
      setUnitGroups(ug||[])
      setSchedulingEvents(se||[])
      setInv(inv||[]); setCL(cl||[]); setOrders(po||[]); setPOItems(poi||[]); setUsers(us||[])
      if(sr&&sr.length>0) setSchedulingRules(sr[0])
      setUnitGroups(ug||[])
      setSchedulingEvents(se||[])
      if(sett){
        const s=sett.find(r=>r.key==='season'); if(s) setSeason(s.value)
        const o=sett.find(r=>r.key==='unit_order'); if(o) { try{setUnitOrder(JSON.parse(o.value))}catch{} }
      }
      setLoading(false)
    } catch(e){setLoading(false)}
  },[])

  useEffect(()=>{
    if(authState!=='app'||!authUser) return
    supabase.from('users').select('*').eq('auth_id',authUser.id).single().then(({data})=>{
      if(data) setUser(data)
      else supabase.from('users').select('*').eq('email',authUser.email).single().then(({data:d})=>{
        if(d){supabase.from('users').update({auth_id:authUser.id}).eq('id',d.id);setUser(d)}
      })
    })
    loadAll().then(()=>{
      // Check for QR code unit parameter in URL
      const params = new URLSearchParams(window.location.search)
      const unitId = params.get('unit')
      if(unitId){
        // Clear the URL param without reload
        window.history.replaceState(null,'',window.location.pathname)
        // Store for after units load
        window._qrUnitId = unitId
      }
    })
  },[authState,authUser]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    if(authState!=='app') return
    const ch=supabase.channel('rt')
      .on('postgres_changes',{event:'*',schema:'public',table:'units'},loadAll)
      .on('postgres_changes',{event:'*',schema:'public',table:'inventory'},loadAll)
      .on('postgres_changes',{event:'*',schema:'public',table:'change_log'},loadAll)
      .on('postgres_changes',{event:'*',schema:'public',table:'purchase_orders'},loadAll)
      .on('postgres_changes',{event:'*',schema:'public',table:'settings'},loadAll)
      .subscribe()
    return()=>supabase.removeChannel(ch)
  },[authState,loadAll])

  // Handle QR code deep link after units load
  useEffect(()=>{
    if(!units.length||!window._qrUnitId) return
    const unitId = window._qrUnitId
    window._qrUnitId = null
    const unit = units.find(u=>u.id===unitId)
    if(unit){
      setSelectedUnits([{...unit,status:getStatus(unit,season)}])
      setLogStep(2)
      setLogForm({date:fmtInput(TODAY),worker:currentUser?.name||'',condition:'Moderate',notes:''})
      setTab('log')
    }
  },[units]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleLogout(){await supabase.auth.signOut();setAuthState('login');setUser(null)}
  async function toggleSeason(){
    const next=season==='summer'?'winter':'summer'
    await supabase.from('settings').upsert({key:'season',value:next,updated_by:currentUser?.name,updated_at:new Date().toISOString()})
    setSeason(next)
  }

  // Save unit order to Supabase settings
  async function saveUnitOrder(newOrder) {
    setUnitOrder(newOrder)
    await supabase.from('settings').upsert({key:'unit_order',value:JSON.stringify(newOrder),updated_at:new Date().toISOString()})
  }

  const withStatus=useMemo(()=>units.map(u=>({...u,status:getStatus(u,season)})),[units,season])
  const overdue=withStatus.filter(u=>u.status.label==='OVERDUE')
  const dueSoon=withStatus.filter(u=>u.status.label==='DUE SOON')
  const upcoming=withStatus.filter(u=>u.status.label==='UPCOMING')
  const noDate=withStatus.filter(u=>u.status.label==='NO DATE')
  const lowStock=inventory.filter(i=>getStockStatus(i).label!=='OK')

  // Two-level grouping: building → area → units
  const buildingGroups=useMemo(()=>{
    const g={} // building → {area → [units]}
    withStatus.forEach(u=>{
      const building=getBuilding(u.area)
      if(!g[building]) g[building]={}
      if(!g[building][u.area]) g[building][u.area]=[]
      g[building][u.area].push(u)
    })
    // Apply custom order within each area
    Object.keys(g).forEach(building=>{
      Object.keys(g[building]).forEach(area=>{
        if(unitOrder[area]){
          const idxMap={}; unitOrder[area].forEach((id,i)=>idxMap[id]=i)
          g[building][area].sort((a,b)=>(idxMap[a.id]??999)-(idxMap[b.id]??999))
        }
      })
    })
    return g
  },[withStatus,unitOrder])

  const ordersWithItems=useMemo(()=>orders.map(o=>({...o,items:(poItems||[]).filter(i=>i.po_id===o.id)})),[orders,poItems])
  const filteredLog=useMemo(()=>{
    const filtered=changelog.filter(l=>{
      // Show deleted or active based on tab
      if(histTab==='deleted') { if(!l.deleted) return false }
      else { if(l.deleted) return false }
      if(histSearch&&!`${l.area} ${l.location} ${l.size} ${l.worker_name} ${l.logged_by_name}`.toLowerCase().includes(histSearch.toLowerCase()))return false
      if(histArea&&l.area!==histArea)return false
      if(histWorker&&l.worker_name!==histWorker)return false
      if(histDateFrom&&l.change_date<histDateFrom)return false
      if(histDateTo&&l.change_date>histDateTo)return false
      return true
    })
    return [...filtered].sort((a,b)=>{
      if(histSort==='date-desc') return new Date(b.change_date)-new Date(a.change_date)
      if(histSort==='date-asc')  return new Date(a.change_date)-new Date(b.change_date)
      if(histSort==='worker')    return a.worker_name.localeCompare(b.worker_name)||new Date(b.change_date)-new Date(a.change_date)
      if(histSort==='logged-by') return a.logged_by_name.localeCompare(b.logged_by_name)||new Date(b.change_date)-new Date(a.change_date)
      if(histSort==='area')      return a.area.localeCompare(b.area)||a.location.localeCompare(b.location)||new Date(b.change_date)-new Date(a.change_date)
      if(histSort==='size')      return a.size.localeCompare(b.size)||new Date(b.change_date)-new Date(a.change_date)
      if(histSort==='condition'){
        const rank={'Damaged':0,'Very Dirty':1,'Dirty':2,'Moderate':3,'Light':4}
        const ra=rank[a.condition]??5, rb=rank[b.condition]??5
        return ra-rb||new Date(b.change_date)-new Date(a.change_date)
      }
      if(histSort==='due'){
        const getNext=l=>{
          const u=units.find(x=>x.id===l.unit_id)
          if(!u||!u.last_changed) return 9999
          const iv=getInterval(u,season)
          if(!iv) return 9999
          return Math.round((addDays(new Date(u.last_changed),iv)-TODAY)/864e5)
        }
        return getNext(a)-getNext(b)
      }
      return 0
    })
  },[changelog,histSearch,histArea,histWorker,histDateFrom,histDateTo,histSort,histTab,units,season])

  function toggleUnit(u){setSelectedUnits(prev=>prev.find(x=>x.id===u.id)?prev.filter(x=>x.id!==u.id):[...prev,u])}
  function openQuickLog(u){
    setSelectedUnits([u]); setLogStep(2)
    setLogForm({date:fmtInput(TODAY),worker:lastWorker||currentUser?.name||'',condition:'Moderate',notes:'',partialQty:{},filterStates:{},notDirtyMode:{},recheckDays:{}})
    setTab('log')
  }

  async function submitLog(){
    if(!logForm.worker.trim()||!logForm.date||!selectedUnits.length) return

    // Only check first-time for REPLACE state (not_dirty/follow_up never deduct stock)
    const firstTimeUnits = selectedUnits.filter(u => {
      const fState = logForm.filterStates?.[u.unit_filter_id] || 'replace'
      if(fState !== 'replace') return false
      const hasHistory = changelog.some(l => l.unit_id === u.id && l.log_type==='change')
      return !hasHistory && u.filter_type !== 'washable'
    })

    if(firstTimeUnits.length > 0) {
      // Ask about inventory deduction before proceeding
      setStockPrompt({units: firstTimeUnits, pendingLog: {...logForm}, deduct: true})
      return
    }

    // No first-time units — proceed normally with inventory deduction
    await doSubmitLog(true)
  }

  // ── SCHEDULING UTILITIES ────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const US_HOLIDAYS = useMemo(()=>{
    // US Federal holidays - approximate dates for current year range
    const h = []
    for(let y=2024;y<=2030;y++){
      h.push(`${y}-01-01`) // New Year
      h.push(`${y}-07-04`) // Independence Day
      h.push(`${y}-11-11`) // Veterans Day
      h.push(`${y}-12-25`) // Christmas
      // MLK: 3rd Monday Jan
      const mlk=new Date(y,0,1); while(mlk.getDay()!==1)mlk.setDate(mlk.getDate()+1); mlk.setDate(mlk.getDate()+14)
      h.push(mlk.toISOString().slice(0,10))
      // Presidents: 3rd Monday Feb
      const pres=new Date(y,1,1); while(pres.getDay()!==1)pres.setDate(pres.getDate()+1); pres.setDate(pres.getDate()+14)
      h.push(pres.toISOString().slice(0,10))
      // Memorial: last Monday May
      const mem=new Date(y,4,31); while(mem.getDay()!==1)mem.setDate(mem.getDate()-1)
      h.push(mem.toISOString().slice(0,10))
      // Labor: 1st Monday Sep
      const lab=new Date(y,8,1); while(lab.getDay()!==1)lab.setDate(lab.getDate()+1)
      h.push(lab.toISOString().slice(0,10))
      // Thanksgiving: 4th Thursday Nov
      const tg=new Date(y,10,1); while(tg.getDay()!==4)tg.setDate(tg.getDate()+1); tg.setDate(tg.getDate()+21)
      h.push(tg.toISOString().slice(0,10))
    }
    return new Set(h)
  },[])


  // eslint-disable-next-line no-unused-vars
  function getUnitPreEventDeadlines(unitId) {
    const deadlines = []
    const today = new Date(); today.setHours(0,0,0,0)
    for(const evt of schedulingEvents) {
      if(!evt.active) continue
      // Check if this unit is covered by this event
      let covered = false
      if(evt.apply_to==='all') covered = true
      else if(evt.apply_to==='units') covered = evt.unit_ids?.includes(unitId)
      else if(evt.apply_to==='groups') {
        covered = unitGroups.some(g=>evt.group_ids?.includes(g.id)&&g.unit_ids?.includes(unitId))
      }
      else if(evt.apply_to==='both') {
        covered = evt.unit_ids?.includes(unitId) ||
          unitGroups.some(g=>evt.group_ids?.includes(g.id)&&g.unit_ids?.includes(unitId))
      }
      if(!covered) continue
      // Calculate event date for current/next year
      const evtDate = getNextEventDate(evt)
      if(!evtDate) continue
      const deadline = new Date(evtDate)
      deadline.setDate(deadline.getDate()-(evt.days_before||7))
      deadline.setHours(0,0,0,0)
      if(deadline>=today) deadlines.push({event:evt, deadline, eventDate:evtDate})
    }
    return deadlines.sort((a,b)=>a.deadline-b.deadline)
  }

  function getNextEventDate(evt) {
    const today=new Date(); today.setHours(0,0,0,0)
    if(evt.trigger_type==='hebrew_date') {
      const [hm,hd]=evt.trigger_value.split('-').map(Number)
      for(let i=0;i<400;i++){
        const d=new Date(today); d.setDate(d.getDate()+i)
        const h=gregorianToHebrew(d.getFullYear(),d.getMonth()+1,d.getDate())
        if(h.month===hm&&h.day===hd) return d
      }
    } else if(evt.trigger_type==='gregorian_date'&&evt.trigger_value) {
      const d=new Date(evt.trigger_value)
      if(d>=today) return d
      if(evt.recurrence==='annual'){d.setFullYear(today.getFullYear());if(d<today)d.setFullYear(today.getFullYear()+1);return d}
    }
    return null
  }

  async function saveSchedRules(updates) {
    const newRules={...schedulingRules,...updates}
    setSchedulingRules(newRules)
    await supabase.from('scheduling_rules').update(newRules).eq('id',schedulingRules.id)
  }

  async function saveUnitGroup(group) {
    if(group.id) {
      await supabase.from('unit_groups').update(group).eq('id',group.id)
      setUnitGroups(unitGroups.map(g=>g.id===group.id?group:g))
    } else {
      const {data}=await supabase.from('unit_groups').insert({...group,created_by:currentUser?.name}).select().single()
      if(data) setUnitGroups([...unitGroups,data])
    }
  }

  async function deleteUnitGroup(id) {
    await supabase.from('unit_groups').delete().eq('id',id)
    setUnitGroups(unitGroups.filter(g=>g.id!==id))
  }

  async function saveSchedEvent(evt) {
    if(evt.id) {
      await supabase.from('scheduling_events').update(evt).eq('id',evt.id)
      setSchedulingEvents(schedulingEvents.map(e=>e.id===evt.id?evt:e))
    } else {
      const {data}=await supabase.from('scheduling_events').insert({...evt,created_by:currentUser?.name}).select().single()
      if(data) setSchedulingEvents([...schedulingEvents,data])
    }
  }

  async function deleteSchedEvent(id) {
    await supabase.from('scheduling_events').delete().eq('id',id)
    setSchedulingEvents(schedulingEvents.filter(e=>e.id!==id))
  }

  // ── Scheduling Rules ─────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  async function saveSchedulingRules(updates) {
    const newRules = {...schedulingRules, ...updates, updated_at: new Date().toISOString()}
    setSchedulingRules(newRules)
    await supabase.from('scheduling_rules').update(newRules).eq('id', schedulingRules.id)
  }

  async function saveSchedulingEvent(ev) {
    if(ev.id) {
      await supabase.from('scheduling_events').update(ev).eq('id', ev.id)
      setSchedulingEvents(schedulingEvents.map(e => e.id===ev.id ? ev : e))
    } else {
      const {data} = await supabase.from('scheduling_events').insert({...ev, created_by: currentUser?.name}).select().single()
      if(data) setSchedulingEvents([...schedulingEvents, data])
    }
  }

  // eslint-disable-next-line no-unused-vars
  async function deleteSchedulingEvent(id) {
    await supabase.from('scheduling_events').delete().eq('id', id)
    setSchedulingEvents(schedulingEvents.filter(e => e.id !== id))
  }

  async function saveUserPreferences(prefs) {
    if(!currentUser) return
    const newPrefs = {...(currentUser.preferences||{}), ...prefs}
    await supabase.from('users').update({preferences:newPrefs}).eq('id',currentUser.id)
    setUser({...currentUser, preferences:newPrefs})
    setUsers(users.map(u=>u.id===currentUser.id?{...u,preferences:newPrefs}:u))
  }

  async function doSubmitLog(deductStock){
    setLogSaving(true)
    setLastWorker(logForm.worker)
    for(const u of selectedUnits){
      const fState = logForm.filterStates?.[u.unit_filter_id] || 'replace'

      // ── REPLACE ──────────────────────────────────────────────
      if(fState==='replace'){
        const qtyToLog = (u.unit_filter_id && logForm.partialQty?.[u.unit_filter_id] !== undefined) ? logForm.partialQty[u.unit_filter_id] : u.qty
        const stockDeducted = (deductStock && u.filter_type!=='washable') ? qtyToLog : 0
        await supabase.from('change_log').insert({unit_id:u.id,area:u.area,location:u.location,size:u.size,unit_filter_id:u.unit_filter_id||null,qty_used:qtyToLog,change_date:logForm.date,logged_by_name:currentUser.name,worker_name:logForm.worker,condition:logForm.condition,season,notes:logForm.notes,log_type:'change',stock_deducted:stockDeducted,deleted:false})
        if(u.unit_filter_id) await supabase.from('unit_filters').update({last_changed:logForm.date}).eq('id',u.unit_filter_id)
        await supabase.from('units').update({last_changed:logForm.date}).eq('id',u.id)
        if(deductStock&&u.filter_type!=='washable'&&stockDeducted>0){
          const inv=inventory.find(i=>i.size===u.size)
          if(inv) await supabase.from('inventory').update({stock:Math.max(0,inv.stock-stockDeducted),updated_at:new Date().toISOString()}).eq('id',inv.id)
        }
      }

      // ── NOT DIRTY ────────────────────────────────────────────
      else if(fState==='not_dirty'){
        const mode = logForm.notDirtyMode?.[u.unit_filter_id] || 'reset'
        if(mode==='reset'){
          // Observed clean — reset clock, full interval from today
          await supabase.from('change_log').insert({unit_id:u.id,area:u.area,location:u.location,size:u.size,unit_filter_id:u.unit_filter_id||null,qty_used:0,change_date:logForm.date,logged_by_name:currentUser.name,worker_name:logForm.worker,condition:logForm.condition,season,notes:logForm.notes,log_type:'inspection',skip_reason:'not_dirty',stock_deducted:0,deleted:false})
          if(u.unit_filter_id) await supabase.from('unit_filters').update({last_changed:logForm.date}).eq('id',u.unit_filter_id)
          await supabase.from('units').update({last_changed:logForm.date}).eq('id',u.id)
        } else {
          // Observed clean — check back in X days, clock untouched
          const recheckDate = fmtInput(addDays(new Date(logForm.date), logForm.recheckDays?.[u.unit_filter_id]??14))
          await supabase.from('change_log').insert({unit_id:u.id,area:u.area,location:u.location,size:u.size,unit_filter_id:u.unit_filter_id||null,qty_used:0,change_date:logForm.date,logged_by_name:currentUser.name,worker_name:logForm.worker,condition:logForm.condition,season,notes:logForm.notes,log_type:'inspection',skip_reason:'not_dirty',recheck_date:recheckDate,stock_deducted:0,deleted:false})
        }
      }

      // ── RAN OUT OF TIME ──────────────────────────────────────
      else if(fState==='follow_up'){
        const recheckDate = fmtInput(addDays(new Date(logForm.date), logForm.recheckDays?.[u.unit_filter_id]??7))
        await supabase.from('change_log').insert({unit_id:u.id,area:u.area,location:u.location,size:u.size,unit_filter_id:u.unit_filter_id||null,qty_used:0,change_date:logForm.date,logged_by_name:currentUser.name,worker_name:logForm.worker,condition:logForm.condition,season,notes:logForm.notes,log_type:'skipped_followup',skip_reason:'ran_out_of_time',recheck_date:recheckDate,stock_deducted:0,deleted:false})
        // last_changed deliberately NOT touched — schedule stays as-is
      }
    }
    await loadAll()
    setLogSaving(false)
    setStockPrompt(null)
    setSelectedUnits([])
    setLogSearch('')
    setLogForm({date:fmtInput(TODAY),worker:lastWorker||currentUser?.name||'',condition:'Moderate',notes:'',partialQty:{},filterStates:{},notDirtyMode:{},recheckDays:{}})
    setLogStep(1)
    setTab('dashboard')
  }

  async function deleteLogEntry(entry, reason){
    // Soft delete
    await supabase.from('change_log').update({
      deleted:true,
      deleted_by:currentUser.name,
      deleted_at:new Date().toISOString(),
      delete_reason:reason||''
    }).eq('id',entry.id)

    // Restore stock if it was deducted
    if(entry.stock_deducted>0){
      const inv=inventory.find(i=>i.size===entry.size)
      if(inv) await supabase.from('inventory').update({
        stock:inv.stock+entry.stock_deducted,
        updated_at:new Date().toISOString()
      }).eq('id',inv.id)
    }

    // Roll back last_changed for this specific filter (per unit_filter_id)
    // Only change/inspection entries actually move the clock
    if(entry.unit_filter_id) {
      let q = supabase.from('change_log').select('change_date')
        .eq('unit_filter_id', entry.unit_filter_id)
        .eq('deleted', false)
        .in('log_type', ['change','inspection'])
        .neq('id', entry.id)
        .order('change_date', {ascending:false})
        .limit(1)
      const {data:prevF} = await q
      const prevFilterDate = prevF&&prevF.length>0 ? prevF[0].change_date : null
      await supabase.from('unit_filters').update({last_changed:prevFilterDate}).eq('id',entry.unit_filter_id)
    }
    // units.last_changed = most recent remaining log across ALL filters on this unit
    const {data:unitLogs} = await supabase.from('change_log').select('change_date')
      .eq('unit_id', entry.unit_id).eq('deleted', false)
      .in('log_type', ['change','inspection']).neq('id', entry.id)
      .order('change_date', {ascending:false}).limit(1)
    await supabase.from('units').update({last_changed: unitLogs&&unitLogs.length>0 ? unitLogs[0].change_date : null}).eq('id', entry.unit_id)

    await loadAll()
    setDeleteLogModal(null)
    setDeleteLogReason('')
  }

  async function restoreLogEntry(entry){
    await supabase.from('change_log').update({
      deleted:false,deleted_by:null,deleted_at:null,delete_reason:null
    }).eq('id',entry.id)
    // Re-deduct stock if it was originally deducted
    if(entry.stock_deducted>0){
      const inv=inventory.find(i=>i.size===entry.size)
      if(inv) await supabase.from('inventory').update({
        stock:Math.max(0,inv.stock-entry.stock_deducted),
        updated_at:new Date().toISOString()
      }).eq('id',inv.id)
    }
    await loadAll()
    }

  async function saveLogEdit(entry, updates){
    await supabase.from('change_log').update({
      ...updates,
      edited_by:currentUser.name,
      edited_at:new Date().toISOString()
    }).eq('id',entry.id)
    // Adjust inventory if checkbox is checked
    if(updates.deduct_stock && entry.size && entry.size!=='Washable') {
      const inv = inventory.find(i=>i.size===entry.size)
      if(inv) {
        const oldDeducted = entry.stock_deducted||0
        const newDeducted = updates.qty_used||entry.qty_used||0
        const delta = newDeducted - oldDeducted
        if(delta !== 0) {
          const newStock = Math.max(0, inv.stock - delta)
          await supabase.from('inventory').update({stock:newStock, updated_at:new Date().toISOString()}).eq('id',inv.id)
          // Also update stock_deducted in change_log
          await supabase.from('change_log').update({stock_deducted:newDeducted}).eq('id',entry.id)
        }
      }
    }
    // Refresh local state
    await loadAll()
    setEditLogModal(null)
  }

  async function submitInspection(){
    if(!inspectUnit) return
    setInspecting(true)
    const postponeDays = Math.min(30, parseInt(inspectForm.postpone_days)||14)
    // Push next due date forward
    const unit = units.find(u=>u.id===inspectUnit.id)
    const currentLastChanged = unit?.last_changed ? new Date(unit.last_changed) : new Date()
    const newLastChanged = addDays(currentLastChanged, postponeDays)
    await supabase.from('change_log').insert({
      unit_id:inspectUnit.id, area:inspectUnit.area, location:inspectUnit.location,
      size:inspectUnit.size, qty_used:0, change_date:fmtInput(TODAY),
      logged_by_name:currentUser.name, worker_name:currentUser.name,
      condition:inspectForm.condition, season, notes:inspectForm.notes,
      log_type:'inspection', stock_deducted:0, postpone_days:postponeDays,
      deleted:false
    })
    // Update last_changed to effectively postpone
    await supabase.from('units').update({last_changed:fmtInput(newLastChanged)}).eq('id',inspectUnit.id)
    setInspecting(false)
    setShowInspectModal(false)
    setInspectUnit(null)
    setInspectForm({condition:'Light',postpone_days:14,notes:'',photo:null})
    setSelectedUnits([])
    setLogStep(1)
    await loadAll()
     setLogStep(1)
    // Return to log step 1 and clear selection
    setSelectedUnits([])
    setLogStep(1)
  }

  function handleDragStart(area,idx){dragItem.current={area,idx}}
  function handleDragEnter(area,idx){dragOver.current={area,idx}}
  function handleDrop(area){
    if(!dragItem.current||!dragOver.current||dragItem.current.area!==area)return
    const building=getBuilding(area)
    const areaUnits=[...(buildingGroups[building]?.[area]||[])]
    const from=dragItem.current.idx,to=dragOver.current.idx
    if(from===to)return
    const moved=areaUnits.splice(from,1)[0]; areaUnits.splice(to,0,moved)
    saveUnitOrder({...unitOrder,[area]:areaUnits.map(u=>u.id)})
    dragItem.current=null; dragOver.current=null
  }

  function openNewOrder(){
    const suggested=calcOrderNeeds(units,inventory,orderMonths)
    setOrderItems(suggested.map(r=>({size:r.size,brand:r.brand||'',boxes:r.boxes,filtersOrdered:r.boxes*12,cost_per_box:r.cost_per_box||0})))
    setShowOF(true)
  }
  async function submitOrder(){
    const items=orderItems.filter(i=>i.boxes>0); if(!items.length) return
    const poNum=`PO-${new Date().getFullYear()}-${String(orders.length+1).padStart(3,'0')}`
    const{data:po}=await supabase.from('purchase_orders').insert({po_number:poNum,status:'pending_approval',supplier_name:orderSupplier.name,supplier_email:orderSupplier.email,created_by:currentUser.name,months_planned:orderMonths,season,notes:orderNotes}).select().single()
    if(po)await supabase.from('po_items').insert(items.map(i=>({po_id:po.id,size:i.size,brand:i.brand,boxes_ordered:i.boxes,filters_ordered:i.filtersOrdered,cost_per_box:i.cost_per_box||0})))
    setShowOF(false); setOrderSup({name:'',email:''}); setOrderNotes('')
  }
  async function approvePO(id){await supabase.from('purchase_orders').update({status:'approved',approved_by:currentUser.name,approved_at:new Date().toISOString()}).eq('id',id)}
  // eslint-disable-next-line no-unused-vars
  async function rejectPO(id,reason){
    await supabase.from('purchase_orders').update({status:'rejected',rejected_by:currentUser.name,rejected_at:new Date().toISOString(),rejection_reason:reason}).eq('id',id)
    setRejectModal(null); setRejectReason('')
  }
  // eslint-disable-next-line no-unused-vars
  async function deletePO(po){
    await supabase.from('po_items').delete().eq('po_id',po.id)
    await supabase.from('purchase_orders').delete().eq('id',po.id)
    setDeleteConfirmPO(null)
  }
  async function resubmitPO(id){await supabase.from('purchase_orders').update({status:'pending_approval',rejected_by:null,rejected_at:null,rejection_reason:null}).eq('id',id)}
  async function sendPO(po){
    const lines=po.items.map(i=>`  • ${i.boxes_ordered} box(es) of ${i.size}`).join('\n')
    window.location.href=`mailto:${po.supplier_email}?subject=${encodeURIComponent(`PO ${po.po_number}`)}&body=${encodeURIComponent(`Dear ${po.supplier_name},\n\nPlease process PO ${po.po_number}:\n\n${lines}\n\nThank you.`)}`
    await supabase.from('purchase_orders').update({status:'sent',sent_at:new Date().toISOString()}).eq('id',po.id)
  }
  function openReceive(po){setReceiveModal(po);const init={};po.items.forEach(i=>{init[i.size]=i.boxes_received??''});setReceiveForm(init)}
  async function submitReceive(){
    const updates=receiveModal.items.map(i=>({...i,boxes_received:parseInt(receiveForm[i.size])||0}))
    const allFull=updates.every(i=>i.boxes_received>=i.boxes_ordered)
    const anyRec=updates.some(i=>i.boxes_received>0)
    const shortItems=updates.filter(i=>i.boxes_received<i.boxes_ordered)
    for(const item of updates){
      await supabase.from('po_items').update({boxes_received:item.boxes_received}).eq('id',item.id)
      const prev=receiveModal.items.find(x=>x.size===item.size)?.boxes_received||0
      const delta=(item.boxes_received-prev)*12
      if(delta!==0){const inv=inventory.find(i=>i.size===item.size);if(inv)await supabase.from('inventory').update({stock:Math.max(0,inv.stock+delta),updated_at:new Date().toISOString()}).eq('id',inv.id)}
    }
    await supabase.from('purchase_orders').update({
      status:allFull?'complete':anyRec?'partial':'sent',
      received_by:currentUser.name,
      received_at:new Date().toISOString()
    }).eq('id',receiveModal.id)
    setReceiveModal(null)
    // Prompt for follow-up PO if items are short
    if(!allFull&&shortItems.length>0){
      setFollowupPrompt({shortItems, originalPO:receiveModal})
    }
  }
  // eslint-disable-next-line no-unused-vars
  async function createFollowupPO(){
    if(!followupPrompt) return
    const {shortItems,originalPO}=followupPrompt
    const poNum=`PO-${new Date().getFullYear()}-${String(orders.length+1).padStart(3,'0')}-F`
    const{data:po}=await supabase.from('purchase_orders').insert({
      po_number:poNum, status:'pending_approval',
      supplier_name:originalPO.supplier_name, supplier_email:originalPO.supplier_email,
      created_by:currentUser.name, months_planned:originalPO.months_planned,
      season, notes:`Follow-up for ${originalPO.po_number} — missing items`
    }).select().single()
    if(po){
      await supabase.from('po_items').insert(shortItems.map(i=>({
        po_id:po.id, size:i.size, brand:i.brand||'',
        boxes_ordered:i.boxes_ordered-i.boxes_received,
        filters_ordered:(i.boxes_ordered-i.boxes_received)*12,
        cost_per_box:i.cost_per_box||0
      })))
    }
    setFollowupPrompt(null)
  }

  async function saveFilterEdit(){
    if(!editingFilter) return
    setSavingFilter(true)
    await supabase.from('inventory').update({
      stock:Math.max(0,parseInt(editingFilter.stock)||0),
      min_stock:parseInt(editingFilter.min_stock)||0,
      brand:editingFilter.brand||null,
      notes:editingFilter.notes||null,
      updated_at:new Date().toISOString()
    }).eq('id',editingFilter.id)
    await loadAll()
    setEditingFilter(null)
    setSavingFilter(false)
  }

  async function importInventoryCSV(e){
    const file=e.target.files[0]; if(!file) return
    const text=await file.text()
    const NL=String.fromCharCode(10)
    const lines=text.split(NL).filter(l=>l.trim())
    const headers=lines[0].split(',').map(h=>h.replace(/"/g,'').trim())
    for(let i=1;i<lines.length;i++){
      const vals=lines[i].split(',').map(v=>v.replace(/"/g,'').trim())
      const row={}; headers.forEach((h,j)=>row[h]=vals[j])
      if(!row.Size) continue
      const existing=inventory.find(x=>x.size===row.Size)
      if(existing){
        await supabase.from('inventory').update({
          stock:row.Stock!==undefined?parseInt(row.Stock):existing.stock,
          min_stock:row.MinStock!==undefined?parseInt(row.MinStock):existing.min_stock,
          brand:row.Brand||existing.brand,
          notes:row.Notes||existing.notes,
          updated_at:new Date().toISOString()
        }).eq('id',existing.id)
      } else {
        await supabase.from('inventory').insert({
          size:row.Size,stock:parseInt(row.Stock)||0,
          min_stock:parseInt(row.MinStock)||0,
          brand:row.Brand||null,notes:row.Notes||null
        })
      }
    }
    e.target.value=''
    alert('Import complete!')
  }

  // eslint-disable-next-line no-unused-vars
  async function addFilterSize(){
    if(!newFilterForm.size.trim()) return
    setAddingFilter(true)
    await supabase.from('inventory').insert({
      size:newFilterForm.size.trim(),
      stock:0,
      min_stock:parseInt(newFilterForm.min_stock)||0,
      brand:newFilterForm.brand.trim()||null,
      notes:newFilterForm.notes.trim()||null,
      updated_at:new Date().toISOString()
    })
    setNewFilterForm({size:'',min_stock:0,brand:'',notes:''})
    setShowAddFilter(false)
    setAddingFilter(false)
  }

  async function inviteUser(){
    if(!userForm.name.trim()||!userForm.email.trim()) return
    setInviting(true); setInviteMsg('')
    try {
      const email = userForm.email.trim().toLowerCase()
      const name = userForm.name.trim()
      const role = userForm.role

      // Create profile in users table
      const{error:profileErr}=await supabase.from('users').upsert({
        name, role, email, active:true,
        avatar:name.split(' ').map(p=>p[0]).join('').toUpperCase().slice(0,2)
      },{onConflict:'email'})
      if(profileErr) throw profileErr

      // Send invite via bright-task Edge Function (already working)
      const{data:{session}}=await supabase.auth.getSession()
      const res=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/bright-task`,{
        method:'POST',
        headers:{
          'Content-Type':'application/json',
          'Authorization':`Bearer ${session.access_token}`,
          'apikey':process.env.REACT_APP_SUPABASE_ANON_KEY
        },
        body:JSON.stringify({action:'invite', email, name, role, redirect:window.location.origin})
      })
      const result=await res.json().catch(()=>({}))
      if(result.error) throw new Error(result.error)

      setInviteMsg(`✅ Invite sent to ${email}`)
      setUserForm({name:'',email:'',role:'Technician'})
      setShowUserForm(false)
    } catch(e){setInviteMsg(`❌ ${e.message}`)}
    setInviting(false)
  }

  async function deleteUnit(unit){if(!window.confirm(`Remove ${unit.location}?`))return;await supabase.from('units').update({active:false}).eq('id',unit.id)}

  async function adjustStock(item, delta) {
    const newStock = Math.max(0, item.stock + delta)
    await supabase.from('inventory').update({stock:newStock,updated_at:new Date().toISOString()}).eq('id',item.id)
    setStockConfirm(null)
  }

  // Auth screens
  if(authState==='loading') return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'linear-gradient(135deg,#0f2237,#1a3d5c)',flexDirection:'column',gap:16}}>
    <div style={{fontSize:36}}>⚙️</div>
    <div style={{fontWeight:700,color:'#fff',fontSize:16}}>HVAC Filter Manager</div>
    <div style={{width:32,height:32,border:'3px solid rgba(255,255,255,.2)',borderTop:'3px solid #fff',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
  if(authState==='login') return <LoginScreen/>
  if(authState==='set-password') return <SetPasswordScreen/>

  const inp16={width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'10px 13px',fontSize:16,outline:'none',background:'#fff'}
  // Effective dashboard view: technicians default to 'my', managers default to 'all'
  const effectiveView = dashView ?? (isManager ? 'all' : 'my')
  // Tech filter for managers
  
  const logOptions=withStatus.filter(u=>{
    if(u.status.label==='OFF') return false
    if(logSearch&&!`${u.area} ${u.location} ${u.size} ${u.description||''}`.toLowerCase().includes(logSearch.toLowerCase())) return false
    if(logFilterStatus.size>0&&!logFilterStatus.has(u.status.label)) return false
    if(logFilterBuilding.size>0&&!logFilterBuilding.has(getBuilding(u.area))) return false
    if(logFilterType.size>0&&!logFilterType.has(u.filter_type)) return false
    if(logFilterAssigned.size>0){
      if(logFilterAssigned.has('mine')&&u.assigned_to!==currentUser?.id) {
        if(!logFilterAssigned.has('unassigned')||u.assigned_to) return false
      }
      if(logFilterAssigned.has('unassigned')&&u.assigned_to&&!logFilterAssigned.has('mine')) return false
      if([...logFilterAssigned].some(v=>v!=='mine'&&v!=='unassigned')&&
         ![...logFilterAssigned].includes(u.assigned_to)) return false
    }
    return true
  })
  const activeFilterCount = logFilterStatus.size+logFilterBuilding.size+logFilterType.size+logFilterAssigned.size



  function toggleFilter(setter, key) {
    setter(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }
  function clearAllFilters() {
    setLogFilterStatus(new Set())
    setLogFilterBuilding(new Set())
    setLogFilterType(new Set())
    setLogFilterAssigned(new Set())
  }

  const buildings = [...new Set(withStatus.filter(u=>u.status.label!=='OFF').map(u=>getBuilding(u.area)))]

  // Count units per filter option (from unfiltered list)
  const allActive = withStatus.filter(u=>u.status.label!=='OFF')
  const statusCounts = {
    'OVERDUE': allActive.filter(u=>u.status.label==='OVERDUE').length,
    'DUE SOON': allActive.filter(u=>u.status.label==='DUE SOON').length,
    'UPCOMING': allActive.filter(u=>u.status.label==='UPCOMING').length,
    'NO DATE': allActive.filter(u=>u.status.label==='NO DATE').length,
    'OK': allActive.filter(u=>u.status.label==='OK').length,
  }
  
  // My assigned units
  const myUnits = withStatus.filter(u=>u.assigned_to===currentUser?.id&&u.status.label!=='OFF')
  const myOverdue = myUnits.filter(u=>u.status.label==='OVERDUE')
  const myDueSoon = myUnits.filter(u=>u.status.label==='DUE SOON')
  const myUpcoming = myUnits.filter(u=>u.status.label==='UPCOMING')
  const myNoDate = myUnits.filter(u=>u.status.label==='NO DATE')
  
  // Units filtered by tech (for manager view)
  const techFilteredUnits = techFilter==='all' ? withStatus : withStatus.filter(u=>
    techFilter==='unassigned' ? !u.assigned_to : u.assigned_to===techFilter
  )
  const techFilteredOverdue = techFilteredUnits.filter(u=>u.status.label==='OVERDUE')
  const techFilteredDueSoon = techFilteredUnits.filter(u=>u.status.label==='DUE SOON')
  const techFilteredUpcoming = techFilteredUnits.filter(u=>u.status.label==='UPCOMING')
  const techFilteredNoDate = techFilteredUnits.filter(u=>u.status.label==='NO DATE')
  
  // Dashboard units shown depend on role and view
  const dashOverdue = isManager ? techFilteredOverdue : (effectiveView==='my' ? myOverdue : overdue)
  const dashDueSoon = isManager ? techFilteredDueSoon : (effectiveView==='my' ? myDueSoon : dueSoon)
  const dashUpcoming = isManager ? techFilteredUpcoming : (effectiveView==='my' ? myUpcoming : upcoming)
  const dashNoDate = isManager ? techFilteredNoDate : (effectiveView==='my' ? myNoDate : noDate)
  const unassignedCount = withStatus.filter(u=>!u.assigned_to&&u.status.label!=='OFF').length
  // eslint-disable-next-line no-unused-vars
  const areas=[...new Set(units.map(u=>u.area))]
  // eslint-disable-next-line no-unused-vars
  const workers=[...new Set(changelog.map(l=>l.worker_name))]
  // eslint-disable-next-line no-unused-vars

  return <div style={{fontFamily:"'Sora',system-ui,sans-serif",background:C.bg,minHeight:'100vh',display:'flex',flexDirection:'column',paddingBottom:isMobile?64:0}}>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;600&display=swap');
      *{box-sizing:border-box;margin:0;padding:0}
      button,input,select,textarea{font-family:inherit}
      ::-webkit-scrollbar{width:4px;height:4px}
      ::-webkit-scrollbar-thumb{background:#c5cfd9;border-radius:3px}
      .rh:hover{background:#f0f5fa!important}
      .ucard:hover{border-color:#4a9fd4!important;background:#f8fbff!important}
      .ucard.sel{border-color:#0f2237!important;background:#e8f0fa!important}
      .nav-btn{transition:all .15s}
      .nav-btn:hover{background:#1a3d5c!important;color:#fff!important}
    `}</style>

    {/* ── Header ── */}
    <header style={{background:C.navy,display:'flex',alignItems:'center',padding:'0 20px',height:52,flexShrink:0,position:'sticky',top:0,zIndex:100,boxShadow:'0 2px 8px rgba(0,0,0,.2)'}}>
      <div style={{fontSize:15,fontWeight:800,color:'#fff',marginRight:'auto',letterSpacing:'-.02em'}}>⚙ HVAC Filter</div>
      {!isMobile&&<nav style={{display:'flex',gap:2,marginRight:20}}>
        {NAV.map(n=><button key={n.key} className="nav-btn" onClick={()=>{setTab(n.key);if(n.key==='log'){setLogStep(1);setSelectedUnits([]);setLogSearch('');}}} style={{background:tab===n.key?'#1a3d5c':'transparent',color:tab===n.key?'#fff':'#7a9bbf',border:'none',borderBottom:tab===n.key?'2px solid #4a9fd4':'2px solid transparent',borderRadius:'6px 6px 0 0',padding:'0 16px',height:52,fontSize:12,fontWeight:tab===n.key?700:400,cursor:'pointer',display:'flex',alignItems:'center',gap:6}}>
          {NAV_FULL[n.key]}
          {n.key==='log'&&(overdue.length+dueSoon.length)>0&&<span style={{background:'#c0392b',color:'#fff',borderRadius:20,padding:'1px 7px',fontSize:10,fontWeight:700}}>{overdue.length+dueSoon.length}</span>}
        </button>)}
      </nav>}
      <div style={{position:'relative'}}>
        <button onClick={()=>setShowUserMenu(!showUserMenu)} style={{background:'rgba(255,255,255,.1)',border:'1px solid rgba(255,255,255,.15)',borderRadius:10,padding:'6px 12px',display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
          <Avatar name={currentUser?.name||'?'} size={26}/>
          {!isMobile&&<div style={{textAlign:'left'}}>
            <div style={{fontSize:11,fontWeight:700,color:'#fff'}}>{currentUser?.name}</div>
            <div style={{fontSize:10,color:'#5a8ab0'}}>{currentUser?.role}</div>
          </div>}
          <span style={{color:'#5a8ab0',fontSize:10}}>▼</span>
        </button>
        {showUserMenu&&<div style={{position:'absolute',top:'calc(100% + 6px)',right:0,background:'#fff',borderRadius:12,boxShadow:'0 8px 32px rgba(0,0,0,.2)',overflow:'hidden',minWidth:180,zIndex:200}}>
          <div style={{padding:'10px 14px',borderBottom:`1px solid ${C.border}`}}>
            <div style={{fontSize:12,fontWeight:700,color:C.text}}>{currentUser?.name}</div>
            <div style={{fontSize:11,color:C.muted}}>{currentUser?.email}</div>
          </div>
          <button onClick={()=>{setShowChangePassword(true);setShowUserMenu(false)}} style={{width:'100%',display:'flex',alignItems:'center',gap:8,background:'transparent',border:'none',padding:'10px 14px',cursor:'pointer',fontSize:12,color:C.text,textAlign:'left'}}>
            🔐 Change Password
          </button>
          <button onClick={handleLogout} style={{width:'100%',display:'flex',alignItems:'center',gap:8,background:'transparent',border:'none',padding:'10px 14px',cursor:'pointer',fontSize:12,color:'#c0392b',textAlign:'left',borderTop:`1px solid ${C.border}`}}>
            Sign Out
          </button>
        </div>}
      </div>
    </header>

    {/* ── Mobile bottom nav ── */}
    {isMobile&&<nav style={{position:'fixed',bottom:0,left:0,right:0,background:'#0a1a2e',display:'flex',zIndex:100,borderTop:'2px solid #2a5a8a',height:62,paddingBottom:'env(safe-area-inset-bottom)'}}>
      {NAV.map(n=>{
        const active=tab===n.key
        return <button key={n.key} onClick={()=>{setTab(n.key);if(n.key==='log'){setLogStep(1);setSelectedUnits([]);setLogSearch('');}}} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:2,background:active?'rgba(74,159,212,.15)':'transparent',border:'none',color:active?'#4a9fd4':'#7a9ab8',cursor:'pointer',position:'relative',padding:'6px 0',borderTop:active?'3px solid #4a9fd4':'3px solid transparent'}}>
          <span style={{fontSize:19,lineHeight:1}}>{n.icon}</span>
          <span style={{fontSize:10,fontWeight:active?800:500,letterSpacing:'.01em',color:active?'#fff':'#7a9ab8'}}>{n.key==='history'?'History':n.key==='orders'?'Orders':NAV_FULL[n.key].split(' ')[0]}</span>
          {n.key==='log'&&(overdue.length+dueSoon.length)>0&&<span style={{position:'absolute',top:4,right:'calc(50% - 14px)',background:'#e74c3c',color:'#fff',borderRadius:10,padding:'1px 6px',fontSize:9,fontWeight:800,minWidth:16,textAlign:'center'}}>{overdue.length+dueSoon.length}</span>}
        </button>
      })}
    </nav>}

    <main style={{flex:1,padding:isMobile?'16px 14px':'24px 28px',overflowX:'hidden',maxWidth:1400,width:'100%',margin:'0 auto'}}>

      {/* ══ DASHBOARD ══ */}
      {tab==='dashboard'&&<>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:10}}>
          <div>
            <h1 style={{fontSize:isMobile?18:22,fontWeight:800,color:C.text,letterSpacing:'-.02em'}}>Dashboard</h1>
            <p style={{color:C.muted,fontSize:11,marginTop:2}}>{fmt(TODAY)} · {currentUser?.name}</p>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <Btn small outline color={C.steel} onClick={()=>setShowTodoPrint(true)}>🖨️ Print To-Do</Btn>
            <SeasonToggle season={season} onToggle={toggleSeason} canToggle={isManager}/>
          </div>
        </div>


        {/* Onboarding banner - show when most units have no date */}
        {withStatus.filter(u=>u.status.label==='NO DATE').length > withStatus.length*0.5 && withStatus.length>0&&<div style={{background:'linear-gradient(135deg,#1a3d5c,#2d6a9f)',borderRadius:12,padding:'16px 20px',marginBottom:16,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
          <div>
            <div style={{fontWeight:800,color:'#fff',fontSize:14,marginBottom:4}}>👋 Welcome to HVAC Filter Manager!</div>
            <div style={{fontSize:12,color:'#a8c8e8'}}>Start by logging the last service date for each unit so the system can track replacement schedules.</div>
          </div>
          <button onClick={()=>setTab('log')} style={{background:'#fff',color:C.navy,border:'none',borderRadius:9,padding:'10px 18px',fontWeight:700,fontSize:13,cursor:'pointer',whiteSpace:'nowrap'}}>
            Log Service Dates →
          </button>
        </div>}

        {/* ⏰ Follow-up flags — filters that need a recheck */}
        {(()=>{
          const followUps = changelog.filter(l=>!l.deleted&&l.recheck_date&&new Date(l.recheck_date+'T00:00:00')<=TODAY)
          if(!followUps.length) return null
          return <div style={{background:'#fff8e1',border:'1px solid #f0d060',borderRadius:12,padding:'14px 18px',marginBottom:16}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
              <span style={{fontSize:18}}>⏰</span>
              <div style={{fontWeight:800,color:'#7a5c00',fontSize:14}}>{followUps.length} filter{followUps.length>1?'s':''} need{followUps.length===1?'s':''} follow-up</div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {followUps.slice(0,5).map(l=>{
                const u=units.find(x=>x.id===l.unit_id)
                return <div key={l.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:'#fff',borderRadius:8,padding:'8px 12px',fontSize:12}}>
                  <div>
                    <span style={{fontWeight:700,color:C.text}}>{l.area}</span>
                    <span style={{color:C.muted,marginLeft:6,fontFamily:'JetBrains Mono,monospace'}}>{l.size}</span>
                    <span style={{color:'#b7860b',marginLeft:8,fontWeight:600}}>{l.skip_reason==='ran_out_of_time'?'Out of time':'Recheck'} · due {fmtShort(l.recheck_date)}</span>
                  </div>
                  {u&&<button onClick={()=>openQuickLog(u)} style={{background:C.navy,color:'#fff',border:'none',borderRadius:7,padding:'5px 12px',fontSize:11,fontWeight:700,cursor:'pointer'}}>Log</button>}
                </div>
              })}
              {followUps.length>5&&<div style={{fontSize:11,color:'#7a5c00',textAlign:'center',marginTop:2}}>+{followUps.length-5} more</div>}
            </div>
          </div>
        })()}

        {/* View toggle for technicians */}
        {!isManager&&myUnits.length>0&&<div style={{display:'flex',gap:6,marginBottom:12}}>
          {['my','all'].map(v=><button key={v} onClick={()=>setDashView(v)} style={{flex:1,padding:'8px 0',border:`2px solid ${effectiveView===v?C.navy:C.border}`,borderRadius:10,background:effectiveView===v?C.navy:'#fff',color:effectiveView===v?'#fff':C.muted,fontWeight:600,fontSize:12,cursor:'pointer'}}>
            {v==='my'?`My Units (${myUnits.length})`:'All Units'}
          </button>)}
        </div>}

        {/* Tech filter for managers */}
        {isManager&&<div style={{marginBottom:12,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
          <span style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.06em'}}>Viewing:</span>
          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
            <button onClick={()=>setTechFilter('all')} style={{padding:'6px 14px',border:`2px solid ${techFilter==='all'?C.navy:C.border}`,borderRadius:20,background:techFilter==='all'?C.navy:'#fff',color:techFilter==='all'?'#fff':C.muted,fontWeight:600,fontSize:12,cursor:'pointer'}}>All Units</button>
            <button onClick={()=>setTechFilter('unassigned')} style={{padding:'6px 14px',border:`2px solid ${techFilter==='unassigned'?'#b7860b':C.border}`,borderRadius:20,background:techFilter==='unassigned'?'#b7860b':'#fff',color:techFilter==='unassigned'?'#fff':C.muted,fontWeight:600,fontSize:12,cursor:'pointer'}}>
              Unassigned {unassignedCount>0&&`(${unassignedCount})`}
            </button>
            {users.filter(u=>u.role==='Technician').map(u=><button key={u.id} onClick={()=>setTechFilter(u.id)} style={{padding:'6px 14px',border:`2px solid ${techFilter===u.id?C.steel:C.border}`,borderRadius:20,background:techFilter===u.id?C.steel:'#fff',color:techFilter===u.id?'#fff':C.muted,fontWeight:600,fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',gap:6}}>
              <Avatar name={u.name} size={16}/>{u.name.split(' ')[0]}
            </button>)}
          </div>
        </div>}

        {noDate.length>0&&<div style={{background:'#fff8e1',border:'1px solid #f0d060',borderRadius:12,padding:'12px 16px',marginBottom:14,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <span style={{fontSize:20}}>⬜</span>
            <div style={{fontSize:13,color:'#7a5c00'}}><strong>{noDate.length} units</strong> have no service date — daily reminders will be sent until all are logged.</div>
          </div>
          <div style={{display:'flex',gap:8}}>
            {isAdmin&&<Btn small color="#b7860b" onClick={()=>setShowBulkDate(true)}>Set All Dates</Btn>}
            <Btn small outline color="#b7860b" onClick={()=>setTab('log')}>Log Individually →</Btn>
          </div>
        </div>}

        {season==='winter'&&<div style={{background:'#e8f4fd',border:'1px solid #93c5fd',borderRadius:10,padding:'10px 16px',marginBottom:14,fontSize:12,color:'#1e40af'}}>❄️ <strong>Winter mode active.</strong> Summer-only units are OFF.</div>}

        {/* Stat cards */}
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr 1fr':'repeat(5,1fr)',gap:10,marginBottom:18}}>
          {[
            {key:'overdue', label:'Overdue',       val:dashOverdue.length,  color:'#c0392b',icon:'🔴'},
            {key:'dueSoon', label:'Due in 3 Days', val:dashDueSoon.length,  color:'#d35400',icon:'🟠'},
            {key:'upcoming',label:'This Week',     val:dashUpcoming.length, color:'#b7860b',icon:'🟡'},
            {key:'noDate',  label:'No Date',       val:dashNoDate.length,   color:'#8896a8',icon:'⬜'},
            {key:'stock',   label:'Low Stock',     val:lowStock.length,     color:C.steel,  icon:'📦'},
          ].map(k=><div key={k.key} onClick={()=>k.val>0&&setDashExpand(dashExpand===k.key?null:k.key)} style={{background:C.card,borderRadius:14,padding:'14px 16px',boxShadow:'0 1px 4px rgba(0,0,0,.07)',borderTop:`3px solid ${k.color}`,cursor:k.val>0?'pointer':'default',transition:'all .15s',transform:dashExpand===k.key?'translateY(-2px)':'none'}}><>
            <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>{k.icon} {k.label}</div>
            <div style={{fontSize:30,fontWeight:800,color:k.val>0?k.color:'#c5cfd9',lineHeight:1}}>{k.val}</div>
            {k.val>0&&<div style={{fontSize:9,color:C.muted,marginTop:4}}>tap to {dashExpand===k.key?'hide':'view'}</div>}
          </></div>)}
        </div>

        {/* Expanded detail */}
        {dashExpand&&dashExpand!=='stock'&&(()=>{
          const map={overdue:dashOverdue,dueSoon:dashDueSoon,upcoming:dashUpcoming,noDate:dashNoDate}
          const expanded=map[dashExpand]||[]
          if(!expanded.length) return null
          return <Card style={{marginBottom:16}}>
            <div style={{padding:'12px 18px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
              <span style={{fontWeight:700,fontSize:14,color:C.text}}>
                {dashExpand==='overdue'&&`🔴 ${overdue.length} Overdue`}
                {dashExpand==='dueSoon'&&`🟠 ${dueSoon.length} Due in 3 Days`}
                {dashExpand==='upcoming'&&`🟡 ${upcoming.length} Due This Week`}
                {dashExpand==='noDate'&&`⬜ ${noDate.length} No Date Set`}
              </span>
              {dashExpand!=='noDate'&&<Btn small onClick={()=>{setSelectedUnits(expanded);setLogStep(2);setLogForm({date:fmtInput(TODAY),worker:lastWorker||currentUser?.name||'',condition:'Moderate',notes:'',partialQty:{},filterStates:{},notDirtyMode:{},recheckDays:{}});setTab('log')}}>
                Log All ({expanded.length}) →
              </Btn>}
            </div>
            <ScrollTable><table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['Area','Location','Filter','Last Service','Next Due','Status',''].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
              <tbody>{expanded.map(u=><tr key={u.id} className="rh">
                <td style={{...TD,color:C.muted,fontSize:11,whiteSpace:'nowrap'}}>{u.area}</td>
                <td style={{...TD,fontWeight:700,color:C.navy}}>{u.location}</td>
                <td style={{...TD,fontFamily:'JetBrains Mono,monospace',fontSize:11,whiteSpace:'nowrap'}}>{u.size}</td>
                <td style={{...TD,color:C.muted,whiteSpace:'nowrap'}}>{u.last_changed?fmtShort(u.last_changed):'Never'}</td>
                <td style={{...TD,whiteSpace:'nowrap'}}>{u.last_changed&&getInterval(u,season)?fmtShort(addDays(new Date(u.last_changed),getInterval(u,season))):'—'}</td>
                <td style={TD}><Badge label={u.status.days!==null?(u.status.days<0?`${Math.abs(u.status.days)}d overdue`:`${u.status.days}d left`):u.status.label} color={u.status.color} bg={u.status.bg}/></td>
                <td style={TD}>{u.status.label!=='NO DATE'&&<Btn small onClick={()=>openQuickLog(u)}>Log</Btn>}</td>
              </tr>)}</tbody>
            </table></ScrollTable>
          </Card>
        })()}

        {dashExpand==='stock'&&lowStock.length>0&&<Card style={{marginBottom:16}}>
          <div style={{padding:'12px 18px',borderBottom:`1px solid ${C.border}`,fontWeight:700,fontSize:14,color:C.text}}>📦 Low Stock Items</div>
          <ScrollTable><table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Filter Size','In Stock','Min Required','Status'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>{lowStock.map(i=>{const s=getStockStatus(i);return<tr key={i.size} className="rh">
              <td style={{...TD,fontFamily:'JetBrains Mono,monospace',fontWeight:700,color:C.navy}}>{i.size}</td>
              <td style={{...TD,textAlign:'center',fontWeight:800,color:s.color,fontSize:16}}>{i.stock}</td>
              <td style={{...TD,textAlign:'center',color:C.muted}}>{i.min_stock}</td>
              <td style={TD}><Badge label={s.label} color={s.color} bg={s.bg}/></td>
            </tr>})}</tbody>
          </table></ScrollTable>
        </Card>}

        {/* Upcoming this week preview */}
        {dashUpcoming.length>0&&dashExpand!=='upcoming'&&<Card style={{marginBottom:16}}>
          <div style={{padding:'12px 18px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <span style={{fontWeight:700,fontSize:13,color:'#b7860b'}}>🟡 Coming Up This Week</span>
            <button onClick={()=>setDashExpand('upcoming')} style={{background:'none',border:'none',color:C.steel,fontSize:12,cursor:'pointer'}}>View all ({dashUpcoming.length})</button>
          </div>
          <div style={{padding:'8px 14px',display:'flex',flexWrap:'wrap',gap:6}}>
            {dashUpcoming.slice(0,6).map(u=><div key={u.id} style={{background:'#fefae6',border:'1px solid #f0d060',borderRadius:8,padding:'5px 10px',fontSize:11}}>
              <strong>{u.location}</strong> <span style={{color:C.muted}}>in {u.status.days}d</span>
            </div>)}
            {dashUpcoming.length>6&&<div style={{fontSize:11,color:C.muted,alignSelf:'center'}}>+{dashUpcoming.length-6} more</div>}
          </div>
        </Card>}

        {dashOverdue.length===0&&dashDueSoon.length===0&&lowStock.length===0&&dashNoDate.length===0&&<div style={{background:'#eafaf1',border:'1px solid #a9dfbf',borderRadius:14,padding:32,textAlign:'center',color:'#1e8449',fontSize:16,fontWeight:700}}>✅ All units on schedule · Inventory adequate</div>}
      </>}

      {/* ══ LOG CHANGE ══ */}
      {tab==='log'&&<>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:10}}>
          <div>
            <h1 style={{fontSize:isMobile?18:22,fontWeight:800,color:C.text}}>Log Filter Replacement</h1>
            <p style={{color:C.muted,fontSize:11,marginTop:2}}>{logStep===1?'Tap to select one or more units':'Enter details for selected units'}</p>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            {[1,2].map(s=><div key={s} style={{display:'flex',alignItems:'center',gap:5}}>
              <div style={{width:24,height:24,borderRadius:'50%',background:logStep>=s?C.navy:'#dde3eb',color:logStep>=s?'#fff':C.muted,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700}}>{s}</div>
              <span style={{fontSize:11,color:logStep===s?C.text:C.muted,display:isMobile?'none':'block'}}>{s===1?'Select Units':'Enter Details'}</span>
              {s===1&&<span style={{color:C.muted,fontSize:12,margin:'0 4px'}}>→</span>}
            </div>)}
          </div>
        </div>

        {logStep===1&&(()=>{
          // Sort options: due-date | area | name
          const SORT_OPTS=[{key:'due',label:'Due Date'},{key:'area',label:'Area'},{key:'name',label:'Name'}]
          const sorted=[...logOptions].sort((a,b)=>{
            if(logSort==='due'){
              // overdue first (most overdue first), then due soon, then upcoming, then no date, then OK
              const rank=u=>{if(u.status.days===null) return 9999; if(u.status.days<0) return u.status.days; return u.status.days}
              return rank(a)-rank(b)
            }
            if(logSort==='area') return a.area.localeCompare(b.area)||a.location.localeCompare(b.location)
            return a.location.localeCompare(b.location)
          })

          function daysLabel(u) {
            if(u.status.days===null) return {text:'No date set',color:'#8896a8',bg:'#f0f2f5'}
            if(u.status.days<0) return {text:`${Math.abs(u.status.days)}d overdue`,color:'#c0392b',bg:'#fdecea'}
            if(u.status.days===0) return {text:'Due today',color:'#d35400',bg:'#fef0e6'}
            if(u.status.days<=3) return {text:`${u.status.days}d left`,color:'#d35400',bg:'#fef0e6'}
            if(u.status.days<=7) return {text:`${u.status.days}d left`,color:'#b7860b',bg:'#fefae6'}
            return {text:`${u.status.days}d left`,color:'#1e8449',bg:'#eafaf1'}
          }

          return <>
          <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>
            <input value={logSearch} onChange={e=>setLogSearch(e.target.value)} placeholder="🔍 Search by area, location or filter size…" style={{flex:1,minWidth:160,...inp16,padding:'10px 14px'}}/>
            {selectedUnits.length>0&&<>
              <Btn onClick={()=>{setLogStep(2);setLogForm({date:fmtInput(TODAY),worker:lastWorker||currentUser?.name||'',condition:'Moderate',notes:'',partialQty:{},filterStates:{},notDirtyMode:{},recheckDays:{}})}}>
                Next — {selectedUnits.length} →
              </Btn>
              <Btn outline color={C.muted} small onClick={()=>setSelectedUnits([])}>Clear</Btn>
            </>}
          </div>

          {/* My Units banner for technicians */}
          {!isManager&&myUnits.length>0&&<div style={{background:'#eef5fb',border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 14px',marginBottom:10}}>
            <div style={{fontSize:11,fontWeight:700,color:C.steel,marginBottom:8,textTransform:'uppercase',letterSpacing:'.06em'}}>👤 My Assigned Units</div>
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {myUnits.filter(u=>u.status.days===null||u.status.days<=7).map(u=>{
                const sel=!!selectedUnits.find(x=>x.id===u.id)
                const dl=daysLabel(u)
                return <div key={u.id} onClick={()=>toggleUnit(u)} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',background:sel?'#e8f0fa':'#fff',borderRadius:8,cursor:'pointer',border:`2px solid ${sel?C.navy:dl.color}`,transition:'all .15s'}}>
                  <div style={{width:44,textAlign:'center',background:dl.bg,borderRadius:7,padding:'4px 2px',flexShrink:0}}>
                    <div style={{fontSize:u.status.days!==null?16:10,fontWeight:800,color:dl.color,lineHeight:1}}>{u.status.days!==null?u.status.days<0?`-${Math.abs(u.status.days)}`:u.status.days:'NO'}</div>
                    <div style={{fontSize:9,color:dl.color,fontWeight:600}}>{u.status.days!==null?'days':'DATE'}</div>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:13,color:C.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.location}</div>
                    <div style={{fontSize:11,color:C.muted}}>{u.area}</div>
                  </div>
                  {sel&&<span style={{background:C.navy,color:'#fff',borderRadius:'50%',width:20,height:20,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0}}>✓</span>}
                </div>
              })}
            </div>
          </div>}

          {/* Tech filter for managers in log change */}
          {isManager&&users.filter(u=>u.role==='Technician').length>0&&<div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10,flexWrap:'wrap'}}>
            <span style={{fontSize:11,fontWeight:700,color:C.muted}}>Filter:</span>
            <button onClick={()=>setTechFilter('all')} style={{padding:'5px 12px',border:`2px solid ${techFilter==='all'?C.navy:C.border}`,borderRadius:20,background:techFilter==='all'?C.navy:'#fff',color:techFilter==='all'?'#fff':C.muted,fontWeight:600,fontSize:11,cursor:'pointer'}}>All</button>
            <button onClick={()=>setTechFilter('unassigned')} style={{padding:'5px 12px',border:`2px solid ${techFilter==='unassigned'?'#b7860b':C.border}`,borderRadius:20,background:techFilter==='unassigned'?'#b7860b':'#fff',color:techFilter==='unassigned'?'#fff':C.muted,fontWeight:600,fontSize:11,cursor:'pointer'}}>Unassigned</button>
            {users.filter(u=>u.role==='Technician').map(u=><button key={u.id} onClick={()=>setTechFilter(u.id)} style={{padding:'5px 12px',border:`2px solid ${techFilter===u.id?C.steel:C.border}`,borderRadius:20,background:techFilter===u.id?C.steel:'#fff',color:techFilter===u.id?'#fff':C.muted,fontWeight:600,fontSize:11,cursor:'pointer',display:'flex',alignItems:'center',gap:5}}>
              <Avatar name={u.name} size={14}/>{u.name.split(' ')[0]}
            </button>)}
          </div>}

          {/* ── Filter & Sort Bar ── */}
          <div style={{background:'#fff',border:`1px solid ${C.border}`,borderRadius:14,marginBottom:12,overflow:'hidden',boxShadow:'0 1px 4px rgba(0,0,0,.06)'}}>

            {/* Sort row */}
            <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',borderBottom:`1px solid ${C.border}`,flexWrap:'wrap'}}>
              <span style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.06em'}}>Sort:</span>
              {SORT_OPTS.map(o=><button key={o.key} onClick={()=>setLogSort(o.key)} style={{background:logSort===o.key?C.navy:'#f0f3f7',color:logSort===o.key?'#fff':C.muted,border:'none',borderRadius:20,padding:'5px 14px',fontSize:12,fontWeight:600,cursor:'pointer',transition:'all .15s'}}>{o.label}</button>)}
              <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:8}}>
                {activeFilterCount>0&&<button onClick={clearAllFilters} style={{background:'#fdecea',border:'none',color:'#c0392b',fontSize:11,cursor:'pointer',fontWeight:700,borderRadius:20,padding:'4px 12px'}}>✕ Clear All ({activeFilterCount})</button>}
                <span style={{fontSize:11,color:C.muted,fontWeight:600}}>{sorted.length} of {allActive.length} units</span>
                <button onClick={()=>setShowLogFilters(f=>!f)} style={{background:showLogFilters?C.navy:'#f0f3f7',color:showLogFilters?'#fff':C.muted,border:'none',borderRadius:20,padding:'5px 14px',fontSize:12,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:5}}>
                  ⊟ Filters {showLogFilters?'▲':'▼'}
                </button>
              </div>
            </div>

            {/* Quick presets */}
            <div style={{display:'flex',gap:6,padding:'8px 14px',borderBottom:`1px solid ${C.border}`,flexWrap:'wrap',background:'#fafbfc'}}>
              <span style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.06em',alignSelf:'center',marginRight:4}}>Quick:</span>
              {[
                {label:'⚠️ Needs Attention', statuses:['OVERDUE','DUE SOON'], color:'#c0392b', bg:'#fdecea'},
                {label:'📅 Due This Week', statuses:['OVERDUE','DUE SOON','UPCOMING'], color:'#b7860b', bg:'#fefae6'},
                {label:'⬜ No Date Set', statuses:['NO DATE'], color:'#6b7c93', bg:'#f0f2f5'},
                {label:'✅ On Track', statuses:['OK'], color:'#1e8449', bg:'#eafaf1'},
              ].map(p=>{
                const isActive=p.statuses.every(s=>logFilterStatus.has(s))&&logFilterStatus.size===p.statuses.length
                return <button key={p.label} onClick={()=>{
                  if(isActive){setLogFilterStatus(new Set())}
                  else{setLogFilterStatus(new Set(p.statuses))}
                }} style={{background:isActive?p.color:p.bg,color:isActive?'#fff':p.color,border:`1px solid ${p.color}`,borderRadius:20,padding:'4px 12px',fontSize:11,fontWeight:700,cursor:'pointer',transition:'all .15s'}}>{p.label}</button>
              })}
            </div>

            {/* Expanded filters */}
            {showLogFilters&&<div style={{padding:'14px',display:'flex',flexDirection:'column',gap:14}}>

              {/* Status */}
              <div>
                <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>Status <span style={{color:C.steel,fontWeight:400}}>(multi-select)</span></div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {[
                    {key:'OVERDUE',  label:'🔴 Overdue'},
                    {key:'DUE SOON', label:'🟠 Due Soon'},
                    {key:'UPCOMING', label:'🟡 This Week'},
                    {key:'NO DATE',  label:'⬜ No Date'},
                    {key:'OK',       label:'✅ On Track'},
                  ].map(o=>{
                    const active=logFilterStatus.has(o.key)
                    const count=statusCounts[o.key]||0
                    return <button key={o.key} onClick={()=>toggleFilter(setLogFilterStatus,o.key)}
                      style={{background:active?C.navy:'#fff',color:active?'#fff':C.muted,border:`2px solid ${active?C.navy:C.border}`,borderRadius:20,padding:'5px 14px',fontSize:11,fontWeight:600,cursor:'pointer',transition:'all .15s',display:'flex',alignItems:'center',gap:6}}>
                      {o.label}
                      {count>0&&<span style={{background:active?'rgba(255,255,255,.25)':'#e2e8f0',color:active?'#fff':C.muted,borderRadius:20,padding:'1px 7px',fontSize:10,fontWeight:700}}>{count}</span>}
                    </button>
                  })}
                </div>
              </div>

              {/* Building */}
              <div>
                <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>Building <span style={{color:C.steel,fontWeight:400}}>(multi-select)</span></div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {buildings.map(b=>{
                    const active=logFilterBuilding.has(b)
                    const count=allActive.filter(u=>getBuilding(u.area)===b).length
                    return <button key={b} onClick={()=>toggleFilter(setLogFilterBuilding,b)}
                      style={{background:active?C.blue:'#fff',color:active?'#fff':C.muted,border:`2px solid ${active?C.blue:C.border}`,borderRadius:20,padding:'5px 14px',fontSize:11,fontWeight:600,cursor:'pointer',transition:'all .15s',display:'flex',alignItems:'center',gap:6}}>
                      {b}
                      <span style={{background:active?'rgba(255,255,255,.25)':'#e2e8f0',color:active?'#fff':C.muted,borderRadius:20,padding:'1px 7px',fontSize:10,fontWeight:700}}>{count}</span>
                    </button>
                  })}
                </div>
              </div>

              {/* Type */}
              <div>
                <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>Filter Type</div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {[{key:'standard',label:'🔲 Standard'},{key:'washable',label:'♻️ Washable'}].map(o=>{
                    const active=logFilterType.has(o.key)
                    const count=allActive.filter(u=>u.filter_type===o.key).length
                    return <button key={o.key} onClick={()=>toggleFilter(setLogFilterType,o.key)}
                      style={{background:active?C.steel:'#fff',color:active?'#fff':C.muted,border:`2px solid ${active?C.steel:C.border}`,borderRadius:20,padding:'5px 14px',fontSize:11,fontWeight:600,cursor:'pointer',transition:'all .15s',display:'flex',alignItems:'center',gap:6}}>
                      {o.label}
                      <span style={{background:active?'rgba(255,255,255,.25)':'#e2e8f0',color:active?'#fff':C.muted,borderRadius:20,padding:'1px 7px',fontSize:10,fontWeight:700}}>{count}</span>
                    </button>
                  })}
                </div>
              </div>

              {/* Assigned (managers) */}
              {isManager&&users.filter(u=>u.role==='Technician').length>0&&<div>
                <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>Assigned To <span style={{color:C.steel,fontWeight:400}}>(multi-select)</span></div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {[{id:'unassigned',name:'Unassigned'},...users.filter(u=>u.role==='Technician')].map(u=>{
                    const active=logFilterAssigned.has(u.id)
                    const count=u.id==='unassigned'?allActive.filter(x=>!x.assigned_to).length:allActive.filter(x=>x.assigned_to===u.id).length
                    return <button key={u.id} onClick={()=>toggleFilter(setLogFilterAssigned,u.id)}
                      style={{background:active?'#7a3060':'#fff',color:active?'#fff':C.muted,border:`2px solid ${active?'#7a3060':C.border}`,borderRadius:20,padding:'5px 14px',fontSize:11,fontWeight:600,cursor:'pointer',transition:'all .15s',display:'flex',alignItems:'center',gap:6}}>
                      {u.id!=='unassigned'&&<Avatar name={u.name} size={16}/>}
                      {u.id==='unassigned'?'Unassigned':u.name.split(' ')[0]}
                      <span style={{background:active?'rgba(255,255,255,.25)':'#e2e8f0',color:active?'#fff':C.muted,borderRadius:20,padding:'1px 7px',fontSize:10,fontWeight:700}}>{count}</span>
                    </button>
                  })}
                </div>
              </div>}

              {/* Active filter chips */}
              {activeFilterCount>0&&<div style={{borderTop:`1px solid ${C.border}`,paddingTop:12}}>
                <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>Active Filters</div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {[...logFilterStatus].map(s=><span key={s} style={{background:C.navy,color:'#fff',borderRadius:20,padding:'4px 10px',fontSize:11,fontWeight:600,display:'flex',alignItems:'center',gap:5}}>
                    {s==='OVERDUE'?'🔴':s==='DUE SOON'?'🟠':s==='UPCOMING'?'🟡':s==='NO DATE'?'⬜':'✅'} {s}
                    <button onClick={()=>toggleFilter(setLogFilterStatus,s)} style={{background:'none',border:'none',color:'rgba(255,255,255,.7)',cursor:'pointer',fontSize:14,padding:0,lineHeight:1}}>×</button>
                  </span>)}
                  {[...logFilterBuilding].map(b=><span key={b} style={{background:C.blue,color:'#fff',borderRadius:20,padding:'4px 10px',fontSize:11,fontWeight:600,display:'flex',alignItems:'center',gap:5}}>
                    🏢 {b}
                    <button onClick={()=>toggleFilter(setLogFilterBuilding,b)} style={{background:'none',border:'none',color:'rgba(255,255,255,.7)',cursor:'pointer',fontSize:14,padding:0,lineHeight:1}}>×</button>
                  </span>)}
                  {[...logFilterType].map(t=><span key={t} style={{background:C.steel,color:'#fff',borderRadius:20,padding:'4px 10px',fontSize:11,fontWeight:600,display:'flex',alignItems:'center',gap:5}}>
                    {t==='washable'?'♻️':'🔲'} {t}
                    <button onClick={()=>toggleFilter(setLogFilterType,t)} style={{background:'none',border:'none',color:'rgba(255,255,255,.7)',cursor:'pointer',fontSize:14,padding:0,lineHeight:1}}>×</button>
                  </span>)}
                  {[...logFilterAssigned].map(a=>{
                    const u=a==='unassigned'?{name:'Unassigned'}:users.find(x=>x.id===a)
                    return u?<span key={a} style={{background:'#7a3060',color:'#fff',borderRadius:20,padding:'4px 10px',fontSize:11,fontWeight:600,display:'flex',alignItems:'center',gap:5}}>
                      👤 {u.name.split(' ')[0]}
                      <button onClick={()=>toggleFilter(setLogFilterAssigned,a)} style={{background:'none',border:'none',color:'rgba(255,255,255,.7)',cursor:'pointer',fontSize:14,padding:0,lineHeight:1}}>×</button>
                    </span>:null
                  })}
                </div>
              </div>}
            </div>}
          </div>

          {selectedUnits.length>0&&<div style={{background:'#eef5fb',border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 14px',marginBottom:10,display:'flex',flexWrap:'wrap',gap:6,alignItems:'center'}}>
            <span style={{fontSize:11,fontWeight:700,color:C.muted}}>Selected:</span>
            {selectedUnits.map(u=><span key={u.id} style={{background:C.navy,color:'#fff',borderRadius:20,padding:'4px 11px',fontSize:11,fontWeight:600,display:'flex',alignItems:'center',gap:5}}>
              {u.location} <span style={{color:'rgba(255,255,255,.6)',fontSize:10}}>({u.area})</span>
              <button onClick={()=>toggleUnit(u)} style={{background:'none',border:'none',color:'rgba(255,255,255,.6)',cursor:'pointer',fontSize:15,padding:0,lineHeight:1,marginLeft:2}}>×</button>
            </span>)}
          </div>}

          <div style={{display:isMobile?'flex':'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',flexDirection:'column',gap:8,paddingRight:4}}>
            {(()=>{const seen=new Set();return sorted.filter(u=>{if(seen.has(u.id))return false;seen.add(u.id);return true}).map(u=>({...u,_allFilters:sorted.filter(x=>x.id===u.id)}))})().map(u=>{
              const sel=!!selectedUnits.find(x=>x.id===u.id)
              const dl=daysLabel(u)
              const interval=getInterval(u,season)
              const due=u.last_changed&&interval?addDays(new Date(u.last_changed),interval):null
              return <div key={u.id} className={`ucard${sel?' sel':''}`} onClick={()=>toggleUnit(u)}
                style={{border:`2px solid ${sel?C.navy:u.status.days!==null&&u.status.days<=3?u.status.color:C.border}`,borderRadius:12,padding:'14px',cursor:'pointer',background:sel?'#e8f0fa':'#fff',transition:'all .15s',display:'flex',alignItems:'center',gap:12,position:'relative'}}>

                {/* Days indicator — left side */}
                <div style={{width:64,flexShrink:0,textAlign:'center',background:dl.bg,borderRadius:10,padding:'10px 6px',minHeight:60,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
                  {u.status.days===null
                    ?<><div style={{fontSize:11,fontWeight:800,color:dl.color,lineHeight:1.2}}>NO</div>
                      <div style={{fontSize:11,fontWeight:800,color:dl.color,lineHeight:1.2}}>DATE</div></>
                    :<><div style={{fontSize:u.status.days<0||Math.abs(u.status.days)>=100?18:24,fontWeight:800,color:dl.color,lineHeight:1}}>{u.status.days<0?`-${Math.abs(u.status.days)}`:u.status.days}</div>
                    <div style={{fontSize:10,fontWeight:600,color:dl.color,marginTop:3}}>days</div></>
                  }
                </div>

                {/* Unit info */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,color:C.text,fontSize:15,marginBottom:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.area}</div>
                  <div style={{fontSize:11,color:C.muted,marginBottom:4}}>📍 {u.location}{u.description?<span style={{color:'#9aacbe'}}> · {u.description}</span>:''}</div>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    {(u._allFilters||[u]).map((f,fi)=>(
                      f.filter_type==='washable'
                        ?<span key={fi} style={{fontSize:12,color:'#1e6b44',fontWeight:700}}>♻️ Washable × {f.qty}</span>
                        :<span key={fi} style={{fontFamily:'JetBrains Mono,monospace',fontSize:13,fontWeight:700,color:C.navy,background:'#eef5fb',padding:'3px 8px',borderRadius:6}}>{f.size} <span style={{color:C.muted,fontWeight:400}}>× {f.qty}</span></span>
                    ))}
                    {due&&<span style={{fontSize:11,color:C.muted}}>Due {fmtShort(due)}</span>}
                  </div>
                </div>

                {/* Checkmark */}
                {sel&&<span style={{flexShrink:0,background:C.navy,color:'#fff',borderRadius:'50%',width:26,height:26,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>✓</span>}
              </div>
            })}
          </div>

          {selectedUnits.length>0&&<div style={{marginTop:14,position:isMobile?'sticky':'static',bottom:isMobile?72:0}}>
            <Btn onClick={()=>{setLogStep(2);setLogForm({date:fmtInput(TODAY),worker:lastWorker||currentUser?.name||'',condition:'Moderate',notes:'',partialQty:{},filterStates:{},notDirtyMode:{},recheckDays:{}})}} style={{width:'100%',textAlign:'center',padding:'14px',fontSize:14}}>
              Continue with {selectedUnits.length} unit{selectedUnits.length>1?'s':''} →
            </Btn>
          </div>}
        </>})()}

        {logStep===2&&<div style={{maxWidth:isMobile?'100%':680,paddingBottom:isMobile?100:20}}>
          <Card style={{padding:'14px 18px',marginBottom:18,borderLeft:`4px solid ${C.steel}`}}>
            <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:8,textTransform:'uppercase',letterSpacing:'.06em'}}>
              Logging for {[...new Set(selectedUnits.map(u=>u.id))].length} unit{[...new Set(selectedUnits.map(u=>u.id))].length>1?'s':''}
            </div>
            {/* Group by unit and show filter sizes with qty controls */}
            {[...new Set(selectedUnits.map(u=>u.id))].map(uid=>{
              const unitFilters = selectedUnits.filter(u=>u.id===uid)
              const u = unitFilters[0]
              // eslint-disable-next-line no-unused-vars
              const hasPartial = unitFilters.some(f=>{
                const qty = logForm.partialQty?.[f.unit_filter_id]
                return qty !== undefined && qty < f.qty
              })
              return <div key={uid} style={{background:'#f8fafc',borderRadius:10,padding:'10px 14px',marginBottom:8,border:`1px solid ${C.border}`}}>
                <div style={{fontSize:13,marginBottom:8}}>
                  <span style={{fontWeight:700,color:C.navy}}>{u.area}</span>
                  {u.location&&u.location!==u.area&&<span style={{fontSize:11,color:C.muted,marginLeft:6}}>📍 {u.location}</span>}
                </div>
                {unitFilters.map(f=>{
                  const fState = logForm.filterStates?.[f.unit_filter_id] || 'replace'
                  const qty = logForm.partialQty?.[f.unit_filter_id] ?? f.qty
                  const recheckDays = logForm.recheckDays?.[f.unit_filter_id] ?? 14
                  const borderColor = fState==='replace'?(qty<f.qty?'#f0c070':'#a9dfbf'):fState==='not_dirty'?'#9fc8e8':'#f0c070'
                  const bgColor = fState==='replace'?(qty<f.qty?'#fffdf0':'#f5fdf8'):fState==='not_dirty'?'#f0f8ff':'#fff8e8'
                  return <div key={f.unit_filter_id||f.id} style={{border:`1.5px solid ${borderColor}`,borderRadius:11,padding:'10px 12px',marginBottom:8,background:bgColor,transition:'all .15s'}}>
                    {/* Filter size label + 3-way state selector */}
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,flexWrap:'wrap'}}>
                      {f.filter_type==='washable'
                        ?<span style={{fontSize:13,color:'#1e6b44',fontWeight:700}}>♻️ Washable × {f.qty}</span>
                        :<span style={{fontFamily:'JetBrains Mono,monospace',fontSize:13,fontWeight:800,color:'#0f2237',background:'#eef5fb',padding:'3px 8px',borderRadius:6}}>{f.size}</span>
                      }
                      <div style={{display:'flex',gap:4,marginLeft:'auto',flexWrap:'wrap'}}>
                        {[
                          {v:'replace',  l:'✓ Replace',     color:'#1e8449'},
                          {v:'not_dirty',l:'👁 Not Dirty',   color:'#2d6a9f'},
                          {v:'follow_up',l:'⏰ Out of Time', color:'#b7860b'},
                        ].map(opt=><button key={opt.v} type="button"
                          onClick={()=>setLogForm({...logForm,filterStates:{...(logForm.filterStates||{}),[f.unit_filter_id]:opt.v}})}
                          style={{padding:'5px 9px',borderRadius:7,border:`1.5px solid ${fState===opt.v?opt.color:C.border}`,background:fState===opt.v?opt.color:'#fff',color:fState===opt.v?'#fff':C.muted,fontSize:11,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>{opt.l}</button>)}
                      </div>
                    </div>

                    {/* REPLACE — qty control */}
                    {fState==='replace'&&f.filter_type!=='washable'&&<QtyControl
                      value={qty} max={f.qty} originalQty={f.qty} filterSize={null}
                      onChange={newQty=>setLogForm({...logForm,partialQty:{...logForm.partialQty,[f.unit_filter_id]:newQty}})}
                      onReset={()=>{const pq={...logForm.partialQty};delete pq[f.unit_filter_id];setLogForm({...logForm,partialQty:pq})}}
                    />}

                    {/* NOT DIRTY — reset clock or recheck in X days */}
                    {fState==='not_dirty'&&<div>
                      <div style={{fontSize:11,color:'#1a5c9a',marginBottom:6}}>Filter looks clean. What should happen to the schedule?</div>
                      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                        <button type="button" onClick={()=>setLogForm({...logForm,notDirtyMode:{...(logForm.notDirtyMode||{}),[f.unit_filter_id]:'reset'}})}
                          style={{flex:1,minWidth:130,padding:'8px 10px',borderRadius:8,border:`2px solid ${(logForm.notDirtyMode?.[f.unit_filter_id]||'reset')==='reset'?'#2d6a9f':C.border}`,background:(logForm.notDirtyMode?.[f.unit_filter_id]||'reset')==='reset'?'#2d6a9f':'#fff',color:(logForm.notDirtyMode?.[f.unit_filter_id]||'reset')==='reset'?'#fff':C.muted,fontSize:11,fontWeight:700,cursor:'pointer'}}>
                          🔄 Reset full interval
                        </button>
                        <button type="button" onClick={()=>setLogForm({...logForm,notDirtyMode:{...(logForm.notDirtyMode||{}),[f.unit_filter_id]:'recheck'}})}
                          style={{flex:1,minWidth:130,padding:'8px 10px',borderRadius:8,border:`2px solid ${logForm.notDirtyMode?.[f.unit_filter_id]==='recheck'?'#2d6a9f':C.border}`,background:logForm.notDirtyMode?.[f.unit_filter_id]==='recheck'?'#2d6a9f':'#fff',color:logForm.notDirtyMode?.[f.unit_filter_id]==='recheck'?'#fff':C.muted,fontSize:11,fontWeight:700,cursor:'pointer'}}>
                          📅 Check back in...
                        </button>
                      </div>
                      {logForm.notDirtyMode?.[f.unit_filter_id]==='recheck'&&<div style={{marginTop:8,display:'flex',alignItems:'center',gap:8}}>
                        <input type="number" min={1} max={90} value={recheckDays}
                          onChange={e=>setLogForm({...logForm,recheckDays:{...(logForm.recheckDays||{}),[f.unit_filter_id]:parseInt(e.target.value)||14}})}
                          style={{width:64,border:`1px solid ${C.border}`,borderRadius:7,padding:'6px 8px',fontSize:14,outline:'none',textAlign:'center'}}/>
                        <span style={{fontSize:12,color:C.muted}}>days</span>
                      </div>}
                    </div>}

                    {/* OUT OF TIME — follow-up in X days */}
                    {fState==='follow_up'&&<div>
                      <div style={{fontSize:11,color:'#7a5c00',marginBottom:6}}>Schedule stays as-is. When should this be revisited?</div>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <input type="number" min={1} max={30} value={logForm.recheckDays?.[f.unit_filter_id]??7}
                          onChange={e=>setLogForm({...logForm,recheckDays:{...(logForm.recheckDays||{}),[f.unit_filter_id]:parseInt(e.target.value)||7}})}
                          style={{width:64,border:`1px solid ${C.border}`,borderRadius:7,padding:'6px 8px',fontSize:14,outline:'none',textAlign:'center'}}/>
                        <span style={{fontSize:12,color:C.muted}}>days · flagged on dashboard</span>
                      </div>
                    </div>}
                  </div>
                })}
              </div>
            })}
            {/* Warning if any partial replace */}
            {selectedUnits.some(f=>{
              const fState=logForm.filterStates?.[f.unit_filter_id]||'replace'
              const q=logForm.partialQty?.[f.unit_filter_id]
              return fState==='replace'&&q!==undefined&&q<f.qty
            })&&<div style={{background:'#fff8e1',border:'1px solid #f0d060',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#7a5c00',marginBottom:8}}>
              ⚠️ You are logging fewer filters than the full unit qty. Make sure only the dirty ones were changed.
            </div>}
            <button onClick={()=>setLogStep(1)} style={{marginTop:10,background:'none',border:'none',color:C.steel,fontSize:12,cursor:'pointer',padding:0,fontWeight:500}}>← Change selection</button>
          </Card>

          <div style={{marginBottom:16}}>
            <SLabel>Replaced By *</SLabel>
            <input value={logForm.worker} onChange={e=>setLogForm({...logForm,worker:e.target.value})} placeholder="Name of person who did the work" style={{...inp16,marginBottom:8}}/>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {users.map(u=><button key={u.id} onClick={()=>setLogForm({...logForm,worker:u.name})} style={{background:logForm.worker===u.name?C.navy:'#f0f3f7',color:logForm.worker===u.name?'#fff':C.muted,border:'none',borderRadius:20,padding:'7px 15px',fontSize:12,fontWeight:600,cursor:'pointer',transition:'all .15s'}}>{u.name.split(' ')[0]}</button>)}
            </div>
          </div>

          <div style={{marginBottom:16}}>
            <SLabel>Date of Replacement *</SLabel>
            <input type="date" value={logForm.date} onChange={e=>setLogForm({...logForm,date:e.target.value})} style={inp16}/>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>You can enter a past date for retroactive logging.</div>
          </div>

          <div style={{marginBottom:16}}>
            <SLabel>Filter Condition</SLabel>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {[
                {c:'Light',     color:'#1e8449'},
                {c:'Moderate',  color:'#b7860b'},
                {c:'Dirty',     color:'#d35400'},
                {c:'Very Dirty',color:'#c0392b'},
                {c:'Damaged',   color:'#922b21'},
              ].map(({c,color})=>{
                const active=logForm.condition===c
                return <button key={c} onClick={()=>setLogForm({...logForm,condition:c})}
                  style={{flex:1,minWidth:70,background:active?color:'#f8fafc',color:active?'#fff':C.muted,border:`2px solid ${active?color:C.border}`,borderRadius:8,padding:'9px 8px',fontSize:11,fontWeight:700,cursor:'pointer',transition:'all .15s'}}>{c}</button>
              })}
            </div>
          </div>

          <div style={{marginBottom:22}}>
            <SLabel>Notes (optional)</SLabel>
            <textarea value={logForm.notes} onChange={e=>setLogForm({...logForm,notes:e.target.value})} rows={2} placeholder="Any observations about the unit or filters…" style={{...inp16,resize:'vertical'}}/>
          </div>

          <div style={{display:'flex',gap:10,position:isMobile?'sticky':'static',bottom:isMobile?72:0,flexWrap:'wrap',background:isMobile?C.bg:'transparent',paddingTop:isMobile?10:0,paddingBottom:isMobile?4:0,marginTop:12}}>
            <Btn outline color={C.muted} onClick={()=>setLogStep(1)} style={{flex:1}}>← Back</Btn>
            {selectedUnits.length===1&&<Btn outline color="#1a5c9a" onClick={()=>{setInspectUnit(selectedUnits[0]);setShowInspectModal(true)}} style={{flex:1}}>👁 Inspect Only</Btn>}
            <Btn disabled={!logForm.worker.trim()||!logForm.date||logSaving} onClick={submitLog} style={{flex:2,textAlign:'center',padding:'13px',fontSize:13}}>
              {logSaving?`Saving ${selectedUnits.length} entr${selectedUnits.length>1?'ies':'y'}…`:`✓ Submit ${selectedUnits.length>1?`${selectedUnits.length} Changes`:'Change'}`}
            </Btn>
          </div>
        </div>}
      </>}

      {/* ══ INVENTORY ══ */}
      {tab==='inventory'&&<>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:10}}>
          <h1 style={{fontSize:isMobile?18:22,fontWeight:800,color:C.text,letterSpacing:'-.02em'}}>Filter Inventory</h1>
          <div style={{fontSize:12,color:C.muted}}>{inventory.length} sizes · 12 filters per box</div>
        </div>

        {lowStock.length>0&&<div style={{background:'#fdecea',border:'1px solid #f5c6c6',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#c0392b',display:'flex',alignItems:'center',gap:8}}>
          ⚠️ <strong>{lowStock.length} filter size{lowStock.length>1?'s':''}</strong> need{lowStock.length===1?'s':''} attention
        </div>}

        <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap',alignItems:'center'}}>
          <input value={invSearch} onChange={e=>setInvSearch(e.target.value)} placeholder="🔍 Search filter sizes…"
            style={{flex:1,minWidth:160,border:`1px solid ${C.border}`,borderRadius:9,padding:'10px 14px',fontSize:16,outline:'none',background:'#fff'}}/>
          <Btn small outline color={C.steel} onClick={()=>printInventorySheet(inventory,units)}>🖨️ Count Sheet</Btn>
          <Btn small outline color={C.steel} onClick={()=>exportToCSV(inventory.map(i=>({Size:i.size,Brand:i.brand||'',Stock:i.stock,MinStock:i.min_stock,Notes:i.notes||''})),'inventory.csv')}>↓ Excel</Btn>
          {isAdmin&&<label style={{background:C.steel,color:'#fff',borderRadius:8,padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer'}}>
            ↑ Import
            <input type="file" accept=".csv" style={{display:'none'}} onChange={e=>importInventoryCSV(e)}/>
          </label>}
          {isAdmin&&<Btn small onClick={()=>setShowAddFilter(true)}>+ Add Size</Btn>}
        </div>
        {/* Desktop table view */}
        {!isMobile&&<div style={{background:'#fff',borderRadius:12,boxShadow:'0 1px 4px rgba(0,0,0,.07)',overflow:'hidden',marginBottom:8}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>
              {['Filter Size','Brand','In Stock','Min','Status','Units Using This Filter','Adjust',''].map(h=><th key={h} style={TH}>{h}</th>)}
            </tr></thead>
            <tbody>
              {inventory.filter(item=>!invSearch||item.size.toLowerCase().includes(invSearch.toLowerCase())||(item.brand||'').toLowerCase().includes(invSearch.toLowerCase())).map((item,i)=>{
                const s=getStockStatus(item)
                const usedByDt=units.filter(u=>u.size===item.size&&u.filter_type!=='washable')
                async function adj(delta){await supabase.from('inventory').update({stock:Math.max(0,item.stock+delta),updated_at:new Date().toISOString()}).eq('id',item.id)}
                return <tr key={item.id} className="rh" style={{background:i%2?'#fafbfc':'#fff'}}>
                  <td style={{...TD,fontFamily:'JetBrains Mono,monospace',fontWeight:700,color:C.navy,fontSize:13}}>{item.size}</td>
                  <td style={{...TD,color:C.muted,fontSize:11}}>{item.brand||'—'}</td>
                  <td style={{...TD,textAlign:'center',fontWeight:800,color:s.color,fontSize:15}}>{item.stock}</td>
                  <td style={{...TD,textAlign:'center',color:C.muted}}>{item.min_stock}</td>
                  <td style={TD}><Badge label={s.label} color={s.color} bg={s.bg}/></td>
                  <td style={{...TD,fontSize:11,color:C.muted}}>
                    {usedByDt.length>0
                      ?<span title={usedByDt.map(u=>u.area).join(', ')}>{usedByDt.map(u=>u.area).join(', ')}</span>
                      :<span style={{color:'#c0392b',fontSize:10}}>⚠️ No units</span>}
                  </td>
                  <td style={TD}><div style={{display:'flex',gap:4}}>
                    <button onClick={()=>adj(-12)} disabled={item.stock<12} style={{background:item.stock<12?'#f4f4f4':'#fdecea',border:'none',borderRadius:6,padding:'4px 10px',fontSize:12,fontWeight:700,color:item.stock<12?'#ccc':'#c0392b',cursor:item.stock<12?'not-allowed':'pointer'}}>-Box</button>
                    <button onClick={()=>adj(12)} style={{background:'#eafaf1',border:'none',borderRadius:6,padding:'4px 10px',fontSize:12,fontWeight:700,color:'#1e8449',cursor:'pointer'}}>+Box</button>
                    <button onClick={()=>adj(-1)} disabled={item.stock===0} style={{background:item.stock===0?'#f4f4f4':'#fef5f5',border:'1px solid #f5c6c6',borderRadius:6,padding:'4px 8px',fontSize:11,fontWeight:700,color:item.stock===0?'#ccc':'#c0392b',cursor:item.stock===0?'not-allowed':'pointer'}}>-1</button>
                    <button onClick={()=>adj(1)} style={{background:'#f5fdf7',border:'1px solid #a9dfbf',borderRadius:6,padding:'4px 8px',fontSize:11,fontWeight:700,color:'#1e8449',cursor:'pointer'}}>+1</button>
                  </div></td>
                  <td style={TD}>{isAdmin&&<button onClick={()=>setEditingFilter({...item})} style={{background:'none',border:`1px solid ${C.border}`,borderRadius:6,padding:'4px 10px',fontSize:11,color:C.muted,cursor:'pointer'}}>✏️</button>}</td>
                </tr>
              })}
            </tbody>
          </table>
        </div>}

        {/* Mobile card view - compact */}
        {isMobile&&<div style={{display:'flex',flexDirection:'column',gap:6}}>
          {inventory.filter(item=>!invSearch||item.size.toLowerCase().includes(invSearch.toLowerCase())||(item.brand||'').toLowerCase().includes(invSearch.toLowerCase())).map(item=>{
            const usedBy=[...new Set(units.filter(u=>u.size===item.size&&u.filter_type!=='washable').map(u=>u.area))]
            const s=getStockStatus(item)
            const boxes=Math.floor(item.stock/12)
            const singles=item.stock%12
            const stockLabel = item.stock===0 ? 'empty' : [boxes>0?`${boxes} box${boxes>1?'es':''}`:'', singles>0?`${singles} single${singles>1?'s':''}`:''].filter(Boolean).join(' + ')
            async function adj(delta){
              await supabase.from('inventory').update({stock:Math.max(0,item.stock+delta),updated_at:new Date().toISOString()}).eq('id',item.id)
            }
            return <div key={item.id} style={{background:'#fff',borderRadius:14,boxShadow:'0 1px 4px rgba(0,0,0,.07)',overflow:'hidden',borderLeft:`4px solid ${s.color}`}}>
              <div style={{padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:`1px solid ${C.border}`}}>
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <span style={{fontFamily:'JetBrains Mono,monospace',fontWeight:800,fontSize:isMobile?15:16,color:C.navy}}>{item.size}</span>
                  {item.brand&&<span style={{fontSize:11,color:C.muted,background:'#f4f6f9',padding:'2px 8px',borderRadius:6}}>{item.brand}</span>}
                </div>
                <Badge label={s.label} color={s.color} bg={s.bg}/>
              </div>
              <div style={{padding:'14px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
                <div>
                  <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:'uppercase',letterSpacing:'.06em',fontWeight:700}}>In Stock</div>
                  <div style={{display:'flex',alignItems:'baseline',gap:8}}>
                    <span style={{fontSize:32,fontWeight:800,color:s.color,lineHeight:1}}>{item.stock}</span>
                    <span style={{fontSize:12,color:C.muted}}>filters</span>
                  </div>
                  <div style={{fontSize:12,color:C.muted,marginTop:3}}>{stockLabel}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>Min: {item.min_stock} filters</div>
                {usedBy.length>0&&<div style={{fontSize:10,color:C.steel,marginTop:4,lineHeight:1.4}}>
                  <strong>Units:</strong> {usedBy.join(', ')}
                </div>}
                {usedBy.length===0&&<div style={{fontSize:10,color:'#c0392b',marginTop:3}}>⚠️ No active units use this size</div>}
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontSize:11,color:C.muted,fontWeight:600,width:52,textAlign:'right'}}>Box ×12</span>
                    <button onClick={()=>adj(-12)} disabled={item.stock<12} style={{width:48,height:48,background:item.stock<12?'#f4f4f4':'#fdecea',border:'none',borderRadius:12,fontSize:22,fontWeight:800,color:item.stock<12?'#ccc':'#c0392b',cursor:item.stock<12?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>−</button>
                    <button onClick={()=>adj(12)} style={{width:48,height:48,background:'#eafaf1',border:'none',borderRadius:12,fontSize:22,fontWeight:800,color:'#1e8449',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>+</button>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontSize:11,color:C.muted,fontWeight:600,width:52,textAlign:'right'}}>Single</span>
                    <button onClick={()=>adj(-1)} disabled={item.stock===0} style={{width:48,height:48,background:item.stock===0?'#f4f4f4':'#fef5f5',border:`1px solid ${item.stock===0?'#e2e8f0':'#f5c6c6'}`,borderRadius:12,fontSize:22,fontWeight:800,color:item.stock===0?'#ccc':'#c0392b',cursor:item.stock===0?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>−</button>
                    <button onClick={()=>adj(1)} style={{width:48,height:48,background:'#f5fdf7',border:'1px solid #a9dfbf',borderRadius:12,fontSize:22,fontWeight:800,color:'#1e8449',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>+</button>
                  </div>
                </div>
              </div>
              {item.min_stock>0&&<div style={{padding:'0 14px 10px'}}>
                <div style={{background:'#e2e8f0',borderRadius:20,height:4,overflow:'hidden'}}>
                  <div style={{width:`${Math.min(100,Math.round((item.stock/item.min_stock)*100))}%`,background:s.color,height:'100%',borderRadius:20,transition:'width .3s'}}/>
                </div>
                <div style={{fontSize:10,color:C.muted,marginTop:2,textAlign:'right'}}>{Math.min(100,Math.round((item.stock/item.min_stock)*100))}% of minimum</div>
              </div>}
              {/* Edit button */}
              {isAdmin&&<div style={{padding:'6px 12px',borderTop:`1px solid ${C.border}`,display:'flex',justifyContent:'flex-end'}}>
                <button onClick={()=>setEditingFilter({...item})} style={{background:'none',border:`1px solid ${C.border}`,borderRadius:6,padding:'3px 10px',fontSize:11,color:C.muted,cursor:'pointer'}}>✏️ Edit</button>
              </div>}
            </div>
          })}
        </div>}
      </>}

            {/* ══ ORDERS ══ */}
      {tab==='orders'&&<>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:10}}>
          <h1 style={{fontSize:isMobile?18:22,fontWeight:800,color:C.text,letterSpacing:'-.02em'}}>Purchase Orders</h1>
          {isManager&&<Btn onClick={openNewOrder}>+ New Order</Btn>}
        </div>

        {showOrderForm&&<Card style={{padding:isMobile?16:28,marginBottom:16,maxWidth:isMobile?'100%':900}}>
          <h2 style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:14}}>New Purchase Order</h2>
          <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:12,marginBottom:12}}>
            <div><SLabel>Plan for</SLabel><div style={{display:'flex',gap:6}}>{[3,6,12].map(m=><button key={m} onClick={()=>{setOrderMo(m);setOrderItems(calcOrderNeeds(units,inventory,m).map(r=>({size:r.size,brand:r.brand||'',boxes:r.boxes,filtersOrdered:r.boxes*12,cost_per_box:r.cost_per_box||0})))}} style={{flex:1,background:orderMonths===m?C.navy:'#f0f3f7',color:orderMonths===m?'#fff':C.muted,border:'none',borderRadius:8,padding:'9px 0',fontSize:12,fontWeight:600,cursor:'pointer'}}>{m} mo</button>)}</div></div>
            <div><SLabel>Supplier Email</SLabel><input value={orderSupplier.email} onChange={e=>setOrderSup({...orderSupplier,email:e.target.value})} style={inp16}/></div>
          </div>
          <div style={{marginBottom:12}}><SLabel>Supplier Name</SLabel><input value={orderSupplier.name} onChange={e=>setOrderSup({...orderSupplier,name:e.target.value})} style={inp16}/></div>
          {orderItems.length===0
            ?<div style={{background:'#eafaf1',borderRadius:8,padding:'12px 16px',fontSize:13,color:'#1e8449',marginBottom:12}}>✅ Stock is sufficient for {orderMonths} months. No order needed.</div>
            :<ScrollTable><table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:12}}>
              <thead><tr>{['Filter Size','Boxes Needed','Filters'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
              <tbody>{orderItems.map((item,ii)=><tr key={ii} style={{borderBottom:`1px solid ${C.border}`}}>
                <td style={{...TD,fontFamily:'JetBrains Mono,monospace',fontWeight:700,color:C.navy}}>{item.size}</td>
                <td style={TD}><input type="number" min={0} value={item.boxes} onChange={e=>{const v=Math.max(0,parseInt(e.target.value)||0);setOrderItems(orderItems.map((x,i)=>i===ii?{...x,boxes:v,filtersOrdered:v*12}:x))}} style={{width:70,border:`1px solid ${C.border}`,borderRadius:6,padding:'6px 8px',fontSize:14,textAlign:'center',outline:'none'}}/></td>
                <td style={{...TD,textAlign:'center',color:C.muted}}>{item.boxes*12}</td>
              </tr>)}</tbody>
              <tfoot>
                <tr style={{background:'#f8fafc',borderTop:`2px solid ${C.border}`}}>
                  <td style={{padding:'12px',fontWeight:700,color:C.text,fontSize:12}}>
                    {(()=>{const {summerMonths,winterMonths}=getSeasonSplit(orderMonths);void 0;return<>
                      📋 {orderMonths}-month plan<br/>
                      <span style={{fontSize:11,fontWeight:400,color:C.muted}}>
                        ☀️ {summerMonths} mo summer + ❄️ {winterMonths} mo winter
                      </span>
                    </>})()}
                  </td>
                  <td style={{padding:'12px',textAlign:'center'}}>
                    <div style={{fontWeight:800,fontSize:20,color:C.navy}}>{orderItems.filter(i=>i.boxes>0).reduce((s,i)=>s+i.boxes,0)}</div>
                    <div style={{fontSize:10,color:C.muted,fontWeight:600,textTransform:'uppercase'}}>boxes</div>
                  </td>
                  <td style={{padding:'12px',textAlign:'center'}}>
                    <div style={{fontWeight:800,fontSize:20,color:'#1e8449'}}>{orderItems.filter(i=>i.boxes>0).reduce((s,i)=>s+i.boxes*12,0)}</div>
                    <div style={{fontSize:10,color:C.muted,fontWeight:600,textTransform:'uppercase'}}>filters</div>
                  </td>
                </tr>
              </tfoot>
            </table></ScrollTable>
          }
          <div style={{background:'#fefae6',border:'1px solid #f0d060',borderRadius:8,padding:'9px 14px',fontSize:12,color:'#7a5c00',marginBottom:14}}>📋 Saved as Pending Approval — a manager must approve before sending.</div>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <Btn outline color={C.muted} onClick={()=>setShowOF(false)}>Cancel</Btn>
            <Btn disabled={orderItems.filter(i=>i.boxes>0).length===0} onClick={submitOrder}>Submit for Approval →</Btn>
          </div>
        </Card>}

        {ordersWithItems.length===0&&!showOrderForm&&<div style={{background:'#eef5fb',borderRadius:14,padding:40,textAlign:'center',color:C.muted}}>No purchase orders yet.{isManager&&<> Click <strong>+ New Order</strong> to create one.</>}</div>}

        {ordersWithItems.map(po=>{
          const s=PO_STATUS[po.status]
          const canApprove=po.status==='pending_approval'&&isManager
          const canReject=['pending_approval','approved'].includes(po.status)&&isManager
          const canSend=po.status==='approved'&&isManager
          const canReceive=['sent','partial'].includes(po.status)
          const canDelete=['pending_approval','rejected','draft'].includes(po.status)&&isManager
          const canResubmit=po.status==='rejected'&&isManager
          const totalRec=po.items.reduce((s,i)=>s+(i.boxes_received||0)*12,0)
          const totalOrd=po.items.reduce((s,i)=>s+i.filters_ordered,0)
          return<Card key={po.id} style={{marginBottom:12}}>
            <div style={{padding:'14px 18px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
              <div>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:3}}>
                  <span style={{fontFamily:'JetBrains Mono,monospace',fontWeight:800,fontSize:14,color:C.navy}}>{po.po_number}</span>
                  <Badge label={s.label} color={s.color} bg={s.bg}/>
                </div>
                <div style={{fontSize:11,color:C.muted}}>{po.supplier_name||'No supplier'} · {fmtShort(po.created_at)} · by {po.created_by}</div>
                <div style={{display:'flex',gap:10,flexWrap:'wrap',marginTop:4,fontSize:10,color:C.muted}}>
                  {po.approved_by&&<span>✅ Approved by {po.approved_by}</span>}
                  {po.rejected_by&&<span style={{color:'#c0392b'}}>✗ Rejected by {po.rejected_by}{po.rejection_reason?` — ${po.rejection_reason}`:''}</span>}
                  {po.received_by&&<span>📦 Received by {po.received_by}</span>}
                </div>
                {po.rejection_reason&&<div style={{fontSize:11,color:'#c0392b',marginTop:4}}>❌ Rejected: {po.rejection_reason}</div>}
              </div>
              <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                {canApprove&&<Btn small color="#1e6b44" onClick={()=>approvePO(po.id)}>✓ Approve</Btn>}
                {canReject&&<Btn small color="#c0392b" onClick={()=>setRejectModal(po)}>✗ Reject</Btn>}
                {canResubmit&&<Btn small color={C.steel} onClick={()=>resubmitPO(po.id)}>↺ Resubmit</Btn>}
                {!isManager&&po.status==='pending_approval'&&<span style={{fontSize:11,color:C.muted,fontStyle:'italic'}}>Awaiting approval</span>}
                {canSend&&<Btn small color={C.steel} onClick={()=>sendPO(po)}>📧 Send</Btn>}
                {canReceive&&<Btn small color="#d35400" onClick={()=>openReceive(po)}>📦 Receive</Btn>}
                {canDelete&&<Btn small danger onClick={()=>setDeleteConfirmPO(po)}>🗑</Btn>}
              </div>
            </div>
            <ScrollTable><table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead><tr>{['Filter','Boxes','Filters','Received','Status'].map(h=><th key={h} style={{...TH,background:'#fff'}}>{h}</th>)}</tr></thead>
              <tbody>{po.items.map((item,ii)=>{
                const rec=item.boxes_received
                const rs=rec===null?null:rec>=item.boxes_ordered?{label:'✓ Complete',color:'#1e8449',bg:'#eafaf1'}:rec===0?{label:'Not Received',color:'#c0392b',bg:'#fdecea'}:{label:'Partial Delivery',color:'#d35400',bg:'#fef0e6'}
                return<tr key={ii} style={{background:ii%2?'#fafbfc':'#fff'}}>
                  <td style={{...TD,fontFamily:'JetBrains Mono,monospace',fontWeight:700,color:C.navy}}>{item.size}</td>
                  <td style={{...TD,textAlign:'center',fontWeight:600}}>{item.boxes_ordered}</td>
                  <td style={{...TD,textAlign:'center',color:C.muted}}>{item.filters_ordered}</td>
                  <td style={{...TD,textAlign:'center',fontWeight:600}}>{rec===null?'—':`${rec} box${rec!==1?'es':''}`}</td>
                  <td style={TD}>{rs?<Badge label={rs.label} color={rs.color} bg={rs.bg}/>:<span style={{color:C.muted,fontSize:11}}>Pending</span>}</td>
                </tr>
              })}</tbody>
            </table></ScrollTable>
            {['partial','complete','sent'].includes(po.status)&&totalOrd>0&&<div style={{padding:'10px 18px',borderTop:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:12}}>
              <div style={{flex:1,background:'#e2e8f0',borderRadius:20,height:7,overflow:'hidden'}}><div style={{width:`${Math.min(100,(totalRec/totalOrd)*100)}%`,background:'#1e8449',height:'100%',borderRadius:20,transition:'width .3s'}}/></div>
              <span style={{fontSize:11,color:C.muted,whiteSpace:'nowrap'}}>{totalRec}/{totalOrd} filters received</span>
            </div>}
          </Card>
        })}
      </>}

      {/* ══ CALENDAR ══ */}
      {tab==='calendar'&&<CalendarView units={withStatus} season={season} onQuickLog={openQuickLog} currentUser={currentUser}/>}

      {/* ══ REPORTS ══ */}
      {tab==='reports'&&<ReportsView units={withStatus} changelog={changelog} inventory={inventory} users={users} season={season}/>}

      {/* ══ CHANGE LOG ══ */}
      {tab==='history'&&<>
        {/* ── Header ── */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:10}}>
          <div>
            <h1 style={{fontSize:isMobile?18:22,fontWeight:800,color:C.text,letterSpacing:'-.02em'}}>Change Log</h1>
            <p style={{color:C.muted,fontSize:11,marginTop:2}}>{filteredLog.length} entr{filteredLog.length!==1?'ies':'y'}</p>
          </div>
          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
            <Btn small outline color={C.steel} onClick={()=>printChangeLog(filteredLog,histDateFrom,histDateTo)}>🖨️ Print</Btn>
            <Btn small outline color={C.steel} onClick={()=>exportToCSV(filteredLog.map(l=>({Date:l.change_date,Unit:l.area,Location:l.location,Filter:l.size,Qty:l.qty_used,Worker:l.worker_name,EnteredBy:l.logged_by_name,Season:l.season,Condition:l.condition,Notes:l.notes})),'change-log.csv')}>↓ CSV</Btn>
          </div>
        </div>

        {/* ── Active / Deleted tabs ── */}
        <div style={{display:'flex',gap:0,marginBottom:14,borderBottom:`1px solid ${C.border}`}}>
          {[{key:'active',label:'Active Entries'},{key:'deleted',label:`🗑 Deleted${changelog.filter(l=>l.deleted).length>0?` (${changelog.filter(l=>l.deleted).length})`:''}`}].map(t=><button key={t.key} onClick={()=>setHistTab(t.key)} style={{background:'transparent',border:'none',padding:'10px 20px',fontSize:13,fontWeight:histTab===t.key?700:400,color:histTab===t.key?C.navy:C.muted,cursor:'pointer',borderBottom:histTab===t.key?`2px solid ${C.navy}`:'2px solid transparent',marginBottom:-1}}>{t.label}</button>)}
        </div>

        {/* ── Filter bar ── */}
        <div style={{background:'#fff',border:`1px solid ${C.border}`,borderRadius:12,padding:'12px 14px',marginBottom:12,display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
          <input value={histSearch} onChange={e=>setHistSearch(e.target.value)} placeholder="🔍 Search unit, worker, filter size…"
            style={{flex:1,minWidth:160,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 12px',fontSize:14,outline:'none'}}/>
          <input type="date" value={histDateFrom} onChange={e=>setHistDateFrom(e.target.value)}
            style={{border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',fontSize:13,outline:'none',width:140}}/>
          <input type="date" value={histDateTo} onChange={e=>setHistDateTo(e.target.value)}
            style={{border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',fontSize:13,outline:'none',width:140}}/>
          <select value={histWorker} onChange={e=>setHistWorker(e.target.value)}
            style={{border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',fontSize:13,outline:'none',background:'#fff'}}>
            <option value="">All Workers</option>
            {[...new Set(changelog.map(l=>l.worker_name).filter(Boolean))].sort().map(w=><option key={w} value={w}>{w}</option>)}
          </select>
          <div style={{display:'flex',alignItems:'center',gap:6,marginLeft:'auto'}}>
            <span style={{fontSize:11,fontWeight:700,color:C.muted,whiteSpace:'nowrap'}}>Sort:</span>
            <select value={histSort} onChange={e=>setHistSort(e.target.value)}
              style={{border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 12px',fontSize:13,outline:'none',background:'#fff'}}>
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
              <option value="worker">Worker</option>
              <option value="logged-by">Entered By</option>
              <option value="area">Unit Name</option>
              <option value="condition">Condition</option>
              <option value="size">Filter Size</option>
              <option value="due">Due Date</option>
            </select>
          </div>
          {(histSearch||histWorker||histDateFrom||histDateTo)&&<button onClick={()=>{setHistSearch('');setHistWorker('');setHistDateFrom('');setHistDateTo('')}} style={{background:'#fdecea',border:'none',color:'#c0392b',borderRadius:8,padding:'9px 12px',fontSize:12,fontWeight:700,cursor:'pointer'}}>✕ Clear</button>}
        </div>

        {filteredLog.length===0
          ?<div style={{background:'#eef5fb',borderRadius:12,padding:32,textAlign:'center',color:C.muted}}>No entries match your filters.</div>
          :<>

          {/* ── DESKTOP TABLE ── */}
          {!isMobile&&<Card>
            <ScrollTable><table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>
                <th style={{...TH,width:90}}>Date</th>
                <th style={TH}>Unit</th>
                <th style={{...TH,width:110}}>Filter</th>
                <th style={{...TH,width:55,textAlign:'center'}}>Qty</th>
                <th style={{...TH,width:150}}>Replaced By</th>
                <th style={{...TH,width:110}}>Condition</th>
                <th style={{...TH,width:130}}>Stock</th>
                {isAdmin&&<th style={{...TH,width:100,textAlign:'center'}}>Actions</th>}
              </tr></thead>
              <tbody>{filteredLog.map(l=>{
                const isInspect=l.log_type==='inspection'
                const isFollowUp=l.log_type==='skipped_followup'
                const badgeInfo = isFollowUp
                  ? {bg:'#fff8e1',color:'#b7860b',text:l.recheck_date?`⏰ Out of Time · recheck ${fmtShort(l.recheck_date)}`:'⏰ Out of Time'}
                  : isInspect&&l.recheck_date
                    ? {bg:'#eef5fb',color:'#1a5c9a',text:`👁 Not Dirty · recheck ${fmtShort(l.recheck_date)}`}
                    : isInspect
                      ? {bg:'#eef5fb',color:'#1a5c9a',text:l.postpone_days?`👁 Inspection · +${l.postpone_days}d`:'👁 Not Dirty · reset'}
                      : {bg:'#eafaf1',color:'#1e8449',text:'✓ Replaced'}
                const condColors={Light:{c:'#1e8449',bg:'#eafaf1'},Moderate:{c:'#b7860b',bg:'#fefae6'},Dirty:{c:'#d35400',bg:'#fef0e6'},'Very Dirty':{c:'#c0392b',bg:'#fdecea'},Damaged:{c:'#922b21',bg:'#fadbd8'}}
                const cc=condColors[l.condition]||{c:C.muted,bg:'#f4f6f9'}
                return <tr key={l.id} style={{borderLeft:`3px solid ${isFollowUp?'#b7860b':isInspect?'#1a5c9a':'transparent'}`,background:isFollowUp?'#fffaf0':isInspect?'#f0f8ff':'#fff',opacity:l.deleted?0.6:1}}>
                  <td style={{...TD,verticalAlign:'top',paddingTop:14}}>
                    <div style={{fontWeight:700,color:C.text,fontSize:13}}>{fmtShort(l.change_date)}</div>
                    <div style={{fontSize:10,color:C.muted}}>{new Date(l.change_date).getFullYear()}</div>
                    {l.edited_at&&<span style={{fontSize:9,color:'#b7860b',background:'#fff8e1',padding:'1px 5px',borderRadius:4,marginTop:3,display:'inline-block'}}>✏ edited</span>}
                  </td>
                  <td style={{...TD,verticalAlign:'top',paddingTop:12}}>
                    <div style={{fontWeight:700,color:C.navy,fontSize:13}}>{l.area}</div>
                    {l.location&&l.location!==l.area&&<div style={{fontSize:10,color:C.muted,marginTop:1}}>📍 {l.location}</div>}
                    <span style={{display:'inline-flex',alignItems:'center',gap:3,padding:'2px 7px',borderRadius:20,fontSize:10,fontWeight:700,marginTop:3,background:badgeInfo.bg,color:badgeInfo.color}}>{badgeInfo.text}</span>
                  </td>
                  <td style={{...TD,verticalAlign:'middle'}}>
                    <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,fontWeight:700,color:C.navy,background:'#eef5fb',padding:'3px 7px',borderRadius:5}}>{l.size||'—'}</span>
                  </td>
                  <td style={{...TD,textAlign:'center',verticalAlign:'middle'}}>
                    {(isInspect||isFollowUp)?<span style={{color:C.muted}}>—</span>
                      :<span style={{fontWeight:700,fontSize:13,color:l.qty_used<(units.find(u=>u.id===l.unit_id)?.qty||l.qty_used)?'#d35400':C.text}}>
                        {l.qty_used}
                      </span>}
                  </td>
                  <td style={{...TD,verticalAlign:'middle'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <Avatar name={l.worker_name} size={22}/>
                      <span style={{fontSize:12}}>{l.worker_name}</span>
                    </div>
                  </td>
                  <td style={{...TD,verticalAlign:'middle'}}>
                    {l.condition?<span style={{background:cc.bg,color:cc.c,padding:'3px 9px',borderRadius:20,fontSize:11,fontWeight:700}}>{l.condition}</span>:<span style={{color:C.muted}}>—</span>}
                  </td>
                  <td style={{...TD,verticalAlign:'middle'}}>
                    {(isInspect||isFollowUp)
                      ?<span style={{background:'#f0f2f5',color:C.muted,padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:700}}>➖ No deduction</span>
                      :l.stock_deducted>0
                        ?<span style={{background:'#eafaf1',color:'#1e8449',padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:700}}>✅ −{l.stock_deducted} filters</span>
                        :<span style={{background:'#f0f2f5',color:C.muted,padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:700}}>➖ Retroactive</span>}
                  </td>
                  {isAdmin&&<td style={{...TD,verticalAlign:'middle'}}>
                    {!l.deleted&&histTab==='active'&&<div style={{display:'flex',gap:6,justifyContent:'center'}}>
                      <button onClick={()=>{setEditLogModal(l);setEditLogForm({change_date:l.change_date,worker_name:l.worker_name,condition:l.condition||'Moderate',notes:l.notes||'',qty_used:l.qty_used||1,season:l.season||'summer',deduct_stock:true})}} style={{width:38,height:38,borderRadius:8,border:'1px solid #c3daf5',background:'#eef5fb',cursor:'pointer',fontSize:16,display:'flex',alignItems:'center',justifyContent:'center'}}>✏️</button>
                      <button onClick={()=>setDeleteLogModal(l)} style={{width:38,height:38,borderRadius:8,border:'1px solid #f5c6c6',background:'#fdecea',cursor:'pointer',fontSize:16,display:'flex',alignItems:'center',justifyContent:'center'}}>🗑️</button>
                    </div>}
                    {l.deleted&&histTab==='deleted'&&<button onClick={()=>restoreLogEntry(l)} style={{background:'#eafaf1',border:'none',borderRadius:7,padding:'5px 10px',fontSize:11,color:'#1e8449',cursor:'pointer',fontWeight:700}}>↺ Restore</button>}
                  </td>}
                </tr>
              })}</tbody>
            </table></ScrollTable>
          </Card>}

          {/* ── MOBILE CARDS ── */}
          {isMobile&&<div style={{display:'flex',flexDirection:'column',gap:8}}>
            {filteredLog.map(l=>{
              const isInspect=l.log_type==='inspection'
              const isFollowUp=l.log_type==='skipped_followup'
              const mBadge = isFollowUp
                ? {bg:'#fff8e1',color:'#b7860b',text:'⏰ Out of Time'}
                : isInspect&&l.recheck_date
                  ? {bg:'#eef5fb',color:'#1a5c9a',text:'👁 Not Dirty'}
                  : isInspect
                    ? {bg:'#eef5fb',color:'#1a5c9a',text:l.postpone_days?'👁 Inspection':'👁 Not Dirty'}
                    : {bg:'#eafaf1',color:'#1e8449',text:'✓ Replaced'}
              const condColors={Light:{c:'#1e8449',bg:'#eafaf1'},Moderate:{c:'#b7860b',bg:'#fefae6'},Dirty:{c:'#d35400',bg:'#fef0e6'},'Very Dirty':{c:'#c0392b',bg:'#fdecea'},Damaged:{c:'#922b21',bg:'#fadbd8'}}
              const cc=condColors[l.condition]||{c:C.muted,bg:'#f4f6f9'}
              return <div key={l.id} style={{background:'#fff',borderRadius:12,boxShadow:'0 1px 3px rgba(0,0,0,.06)',overflow:'hidden',borderLeft:`4px solid ${isFollowUp?'#b7860b':isInspect?'#1a5c9a':'#e2e8f0'}`,opacity:l.deleted?0.6:1}}>
                {/* Card header */}
                <div style={{padding:'12px 14px 8px',display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div>
                    <div style={{fontWeight:700,color:C.navy,fontSize:14}}>{l.area}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:1}}>
                      {l.location&&l.location!==l.area&&`📍 ${l.location} · `}{fmtShort(l.change_date)}, {new Date(l.change_date).getFullYear()}
                      {l.edited_at&&<span style={{fontSize:10,color:'#b7860b',background:'#fff8e1',padding:'1px 5px',borderRadius:4,marginLeft:5}}>✏ edited</span>}
                    </div>
                  </div>
                  <span style={{display:'inline-flex',alignItems:'center',gap:3,padding:'3px 9px',borderRadius:20,fontSize:10,fontWeight:700,background:mBadge.bg,color:mBadge.color,whiteSpace:'nowrap',flexShrink:0,marginLeft:8}}>{mBadge.text}</span>
                </div>
                {/* Card body - badges */}
                <div style={{padding:'0 14px 10px',display:'flex',flexWrap:'wrap',gap:6,alignItems:'center'}}>
                  <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,fontWeight:700,color:C.navy,background:'#eef5fb',padding:'3px 7px',borderRadius:5}}>{l.size}</span>
                  {!isInspect&&!isFollowUp&&<span style={{fontWeight:700,fontSize:13}}>{l.qty_used} filters</span>}
                  {isInspect&&l.postpone_days&&<span style={{fontSize:11,color:'#1a5c9a'}}>+{l.postpone_days} days postponed</span>}
                  {l.recheck_date&&<span style={{fontSize:11,color:mBadge.color,fontWeight:600}}>recheck {fmtShort(l.recheck_date)}</span>}
                  {l.condition&&<span style={{background:cc.bg,color:cc.c,padding:'3px 8px',borderRadius:20,fontSize:11,fontWeight:700}}>{l.condition}</span>}
                  {(isInspect||isFollowUp)
                    ?<span style={{background:'#f0f2f5',color:C.muted,padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:700}}>➖ No deduction</span>
                    :l.stock_deducted>0
                      ?<span style={{background:'#eafaf1',color:'#1e8449',padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:700}}>✅ −{l.stock_deducted}</span>
                      :<span style={{background:'#f0f2f5',color:C.muted,padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:700}}>➖ Retro</span>}
                </div>
                {/* Card footer */}
                <div style={{padding:'10px 14px',borderTop:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <Avatar name={l.worker_name} size={24}/>
                    <span style={{fontSize:12,fontWeight:600}}>{l.worker_name}</span>
                  </div>
                  {isAdmin&&!l.deleted&&histTab==='active'&&<div style={{display:'flex',gap:8}}>
                    <button onClick={()=>{setEditLogModal(l);setEditLogForm({change_date:l.change_date,worker_name:l.worker_name,condition:l.condition||'Moderate',notes:l.notes||'',qty_used:l.qty_used||1,season:l.season||'summer',deduct_stock:true})}} style={{padding:'8px 16px',borderRadius:8,border:'none',background:'#eef5fb',color:'#2d6a9f',fontWeight:700,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',gap:5}}>✏️ Edit</button>
                    <button onClick={()=>setDeleteLogModal(l)} style={{padding:'8px 16px',borderRadius:8,border:'none',background:'#fdecea',color:'#c0392b',fontWeight:700,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',gap:5}}>🗑 Delete</button>
                  </div>}
                  {l.deleted&&histTab==='deleted'&&<div>
                    <div style={{fontSize:10,color:C.muted,marginBottom:4,fontStyle:'italic'}}>{l.delete_reason}</div>
                    <button onClick={()=>restoreLogEntry(l)} style={{padding:'8px 14px',borderRadius:8,border:'none',background:'#eafaf1',color:'#1e8449',fontWeight:700,fontSize:12,cursor:'pointer'}}>↺ Restore</button>
                  </div>}
                </div>
              </div>
            })}
          </div>}
        </>}
      </>}

      {/* ══ CONFIG ══ */}
      {tab==='config'&&<>
        <h1 style={{fontSize:isMobile?18:22,fontWeight:800,color:C.text,marginBottom:16,letterSpacing:'-.02em'}}>Settings</h1>
        <div style={{display:'flex',gap:0,marginBottom:20,borderBottom:`1px solid ${C.border}`,overflowX:'auto'}}>
          {[{key:'units',label:'HVAC Units'},{key:'users',label:'Users'},{key:'scheduling',label:'Scheduling'},{key:'season',label:'Season'}].map(t=><button key={t.key} onClick={()=>setConfigTab(t.key)} style={{background:'transparent',border:'none',padding:'10px 18px',fontSize:13,fontWeight:configTab===t.key?700:400,color:configTab===t.key?C.navy:C.muted,cursor:'pointer',borderBottom:configTab===t.key?`2px solid ${C.navy}`:'2px solid transparent',marginBottom:-1,whiteSpace:'nowrap'}}>{t.label}</button>)}
        </div>

        {/* Units — two-level building hierarchy */}
        {configTab==='units'&&<>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:8}}>
            <div style={{display:'flex',alignItems:'center',gap:8,flex:1,minWidth:0}}>
              <input value={configSearch||''} onChange={e=>setConfigSearch(e.target.value)}
                placeholder="🔍 Search units..."
                style={{flex:1,minWidth:120,maxWidth:260,border:`1px solid ${C.border}`,borderRadius:8,padding:'7px 12px',fontSize:13,outline:'none'}}/>
              <div style={{fontSize:12,color:C.muted,whiteSpace:'nowrap'}}>{units.length} units</div>
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {isManager&&<Btn small outline color={C.steel} onClick={()=>setShowQRModal(true)}>🖨️ QR Codes</Btn>}
              {isManager&&<Btn small outline color={C.steel} onClick={()=>printUnitList(units,season)}>📋 Print List</Btn>}
              {canEditUnits&&<Btn onClick={()=>{setEditingUnit(null);setShowUnitForm(true)}}>+ Add Unit</Btn>}
            </div>
          </div>
          
          {/* Bulk assignment */}
          {canEditUnits&&<div style={{background:'#f4f6f9',borderRadius:10,padding:'12px 14px',marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Bulk Assignment</div>
            <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
              <select value={bulkAssignTo} onChange={e=>setBulkAssignTo(e.target.value)} style={{border:`1px solid ${C.border}`,borderRadius:8,padding:'7px 12px',fontSize:13,outline:'none',background:'#fff'}}>
                <option value="">Select technician…</option>
                <option value="unassign">Unassign</option>
                {users.map(u=><option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
              <Btn small disabled={!bulkAssignTo} onClick={async()=>{
                if(!bulkAssignTo) return
                const ids=[...bulkAssignSelected.size>0?bulkAssignSelected:new Set(units.map(u=>u.id))]
                for(const id of ids){
                  await supabase.from('units').update({assigned_to:bulkAssignTo==='unassign'?null:bulkAssignTo}).eq('id',id)
                }
                setBulkAssignSelected(new Set()); setBulkAssignTo(''); loadAll()
              }}>Assign {bulkAssignSelected.size>0?`${bulkAssignSelected.size} Selected`:'All'}</Btn>
              {bulkAssignSelected.size>0&&<Btn small outline color={C.muted} onClick={()=>setBulkAssignSelected(new Set())}>Clear Selection</Btn>}
              <span style={{fontSize:11,color:C.muted}}>{bulkAssignSelected.size>0?`${bulkAssignSelected.size} units selected`:'Leave blank to assign all units'}</span>
            </div>
          </div>}
          {!canEditUnits&&<div style={{background:'#fefae6',border:'1px solid #f0d060',borderRadius:8,padding:'8px 14px',marginBottom:14,fontSize:12,color:'#7a5c00'}}>🔒 Only Admins can add, edit, or remove units.</div>}

          {Object.entries(buildingGroups)
            .filter(([building])=>configBuildingFilter==='all'||building===configBuildingFilter)
            .map(([building,areaMap])=>{
            // Filter areas/units by search
            const filteredAreaMap={}
            Object.entries(areaMap).forEach(([area,areaUnits])=>{
              const filtered=configSearch?areaUnits.filter(u=>`${u.location} ${u.area} ${u.description||''} ${u.size}`.toLowerCase().includes(configSearch.toLowerCase())):areaUnits
              if(filtered.length>0) filteredAreaMap[area]=filtered
            })
            if(Object.keys(filteredAreaMap).length===0) return null
            return <div key={building} style={{marginBottom:22}}>
            {/* Building header */}
            <div style={{background:`linear-gradient(135deg, ${C.navy} 0%, ${C.blue} 100%)`,color:'#fff',padding:'10px 16px',borderRadius:'12px 12px 0 0',fontSize:13,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span>🏢 {building}</span>
              <span style={{fontSize:10,opacity:.6}}>{Object.values(filteredAreaMap).flat().length} units</span>
            </div>
            {/* ONE table per building — fixes column alignment */}
            <div style={{background:'#fff',borderRadius:'0 0 12px 12px',boxShadow:'0 2px 8px rgba(0,0,0,.06)',overflow:'hidden'}}>
              <ScrollTable><table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr>{[canEditUnits?'':'','Location','Type','Filter','Qty','Summer','Winter','Assigned To','Status','History',canEditUnits?'Actions':''].filter(Boolean).map(h=><th key={h} style={TH}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {Object.entries(filteredAreaMap).map(([area,areaUnits])=><>
                    {Object.keys(filteredAreaMap).length>1&&<tr key={`ah-${area}`}>
                      <td colSpan={canEditUnits?11:9} style={{background:'#eef5fb',padding:'5px 14px',fontSize:11,fontWeight:700,color:C.steel,borderBottom:`1px solid ${C.border}`,borderTop:`1px solid ${C.border}`}}>📍 {area}</td>
                    </tr>}
                    {areaUnits.map((u,i)=><tr key={u.id} className="rh" style={{background:i%2?'#fafbfc':'#fff'}} draggable={canEditUnits} onDragStart={()=>handleDragStart(area,i)} onDragEnter={()=>handleDragEnter(area,i)} onDragEnd={()=>handleDrop(area)} onDragOver={e=>e.preventDefault()}>
                      {canEditUnits&&<td style={{...TD,width:36,cursor:'grab',color:'#7a9ab8',textAlign:'center',fontSize:20,userSelect:'none'}}>⠿</td>}
                      <td style={{...TD,fontWeight:600,color:C.text}}>{u.location}</td>
                      <td style={TD}>{u.filter_type==='washable'?<Badge label="♻️ Washable" color="#1e6b44" bg="#eafaf1"/>:<Badge label="Standard" color={C.steel} bg="#eef5fb"/>}</td>
                      <td style={{...TD,fontFamily:'JetBrains Mono,monospace',fontSize:11}}>{u.size}</td>
                      <td style={{...TD,textAlign:'center'}}>{u.qty}</td>
                      <td style={{...TD,textAlign:'center',color:C.muted}}>{u.summer_interval}d</td>
                      <td style={{...TD,textAlign:'center',color:C.muted}}>{u.winter_interval?`${u.winter_interval}d`:<span style={{color:'#e0e0e0'}}>OFF</span>}</td>
                      <td style={TD}>
                        {canEditUnits
                          ?<select value={u.assigned_to||''} onChange={async e=>{await supabase.from('units').update({assigned_to:e.target.value||null}).eq('id',u.id)}} style={{border:`1px solid ${C.border}`,borderRadius:7,padding:'5px 8px',fontSize:11,outline:'none',background:'#fff',maxWidth:120}}>
                            <option value=''>Unassigned</option>
                            {users.map(usr=><option key={usr.id} value={usr.id}>{usr.name.split(' ')[0]}</option>)}
                          </select>
                          :<span style={{fontSize:11,color:u.assigned_to?C.text:C.muted}}>
                            {u.assigned_to?users.find(usr=>usr.id===u.assigned_to)?.name||'—':'Unassigned'}
                          </span>
                        }
                      </td>
                      <td style={TD}><Badge label={u.status.label} color={u.status.color} bg={u.status.bg}/></td>
                      <td style={TD}>
                        <button onClick={()=>setUnitHistoryModal(u)} style={{background:'#eef5fb',border:'none',borderRadius:7,padding:'4px 10px',fontSize:11,color:C.steel,cursor:'pointer',fontWeight:600}}>
                          {changelog.filter(l=>l.unit_id===u.id).length} logs
                        </button>
                      </td>
                      {canEditUnits&&<td style={TD}><div style={{display:'flex',gap:6}}>
                        <Btn small color={C.steel} onClick={()=>{setEditingUnit(u);setShowUnitForm(true)}}>Edit</Btn>
                        <Btn small danger onClick={()=>deleteUnit(u)}>Remove</Btn>
                      </div></td>}
                    </tr>)}
                  </>)}
                </tbody>
              </table></ScrollTable>
            </div>
          </div>
          })}
        </>}

        {/* Users */}
        {configTab==='scheduling'&&isManager&&<>
        {/* ── Blocked Days ── */}
        <Card style={{marginBottom:16,padding:'18px 20px'}}>
          <div style={{fontWeight:800,color:C.navy,fontSize:15,marginBottom:14}}>📅 Blocked Days — No Filter Changes</div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Days of Week</div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d,i)=>{
                const blocked=schedulingRules.blocked_dow?.includes(i)
                return <button key={i} onClick={()=>{
                  const curr=schedulingRules.blocked_dow||[]
                  const next=blocked?curr.filter(x=>x!==i):[...curr,i]
                  saveSchedRules({blocked_dow:next})
                }} style={{padding:'8px 14px',borderRadius:9,border:`2px solid ${blocked?'#c0392b':C.border}`,background:blocked?'#fdecea':'#fff',color:blocked?'#c0392b':C.muted,fontWeight:700,fontSize:13,cursor:'pointer'}}>
                  {d}{blocked&&' 🚫'}
                </button>
              })}
            </div>
          </div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Jewish Holidays</div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {[
                {key:'block_yomtov',   label:'Yom Tov (Rosh Hashana, Yom Kippur, Pesach, etc.)'},
                {key:'block_chol_hamoed', label:'Chol Hamoed'},
              ].map(({key,label})=>{
                const val=schedulingRules[key]!==false
                return <div key={key} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0',borderBottom:`1px solid ${C.border}`}}>
                  <span style={{fontSize:13,color:C.text}}>{label}</span>
                  <button onClick={()=>saveSchedRules({[key]:!val})} style={{width:44,height:24,borderRadius:12,border:'none',cursor:'pointer',background:val?'#c0392b':'#ccc',position:'relative',transition:'all .2s'}}>
                    <div style={{position:'absolute',top:2,left:val?22:2,width:20,height:20,borderRadius:'50%',background:'#fff',transition:'all .2s'}}/>
                  </button>
                </div>
              })}
            </div>
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>US Federal Holidays</div>
            {(()=>{
              const val=schedulingRules.block_us_holidays===true
              return <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span style={{fontSize:13,color:C.text}}>Block US Federal Holidays</span>
                <button onClick={()=>saveSchedRules({block_us_holidays:!val})} style={{width:44,height:24,borderRadius:12,border:'none',cursor:'pointer',background:val?'#c0392b':'#ccc',position:'relative',transition:'all .2s'}}>
                  <div style={{position:'absolute',top:2,left:val?22:2,width:20,height:20,borderRadius:'50%',background:'#fff',transition:'all .2s'}}/>
                </button>
              </div>
            })()}
          </div>
        </Card>

        {/* ── Push Direction ── */}
        <Card style={{marginBottom:16,padding:'18px 20px'}}>
          <div style={{fontWeight:800,color:C.navy,fontSize:15,marginBottom:4}}>↔️ When Due Date Falls on Blocked Day</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:12}}>Should the filter change be scheduled earlier or later?</div>
          <div style={{display:'flex',gap:8}}>
            {[{v:'earlier',l:'← Do it Earlier (default)',desc:'Change filters before the blocked day'},{v:'later',l:'→ Do it Later',desc:'Change filters after the blocked day'}].map(({v,l,desc})=>{
              const active=schedulingRules.push_direction===v||(v==='earlier'&&!schedulingRules.push_direction)
              return <button key={v} onClick={()=>saveSchedRules({push_direction:v})} style={{flex:1,padding:'12px',borderRadius:10,border:`2px solid ${active?C.navy:C.border}`,background:active?C.navy:'#fff',color:active?'#fff':C.muted,cursor:'pointer',textAlign:'left'}}>
                <div style={{fontWeight:700,fontSize:13}}>{l}</div>
                <div style={{fontSize:11,marginTop:3,opacity:.8}}>{desc}</div>
              </button>
            })}
          </div>
        </Card>

        {/* ── Unit Groups ── */}
        <Card style={{marginBottom:16,padding:'18px 20px'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
            <div style={{fontWeight:800,color:C.navy,fontSize:15}}>👥 Unit Groups</div>
            <Btn small onClick={()=>{setEditGroup({name:'',description:'',unit_ids:[]});setShowGroupModal(true)}}>+ New Group</Btn>
          </div>
          {unitGroups.length===0&&<div style={{color:C.muted,fontSize:13,textAlign:'center',padding:'16px 0'}}>No groups yet. Create groups to assign events to multiple units at once.</div>}
          {unitGroups.map(g=><div key={g.id} style={{border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 14px',marginBottom:8}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div>
                <div style={{fontWeight:700,color:C.navy,fontSize:14}}>{g.name}</div>
                {g.description&&<div style={{fontSize:11,color:C.muted,marginTop:2}}>{g.description}</div>}
                <div style={{fontSize:11,color:C.steel,marginTop:4}}>{g.unit_ids?.length||0} units assigned</div>
              </div>
              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>{setEditGroup(g);setShowGroupModal(true)}} style={{background:'#eef5fb',border:'none',borderRadius:7,padding:'5px 10px',fontSize:12,color:'#2d6a9f',cursor:'pointer',fontWeight:600}}>✏️ Edit</button>
                <button onClick={()=>{if(window.confirm('Delete this group?')) deleteUnitGroup(g.id)}} style={{background:'#fdecea',border:'none',borderRadius:7,padding:'5px 10px',fontSize:12,color:'#c0392b',cursor:'pointer',fontWeight:600}}>🗑</button>
              </div>
            </div>
          </div>)}
        </Card>

        {/* ── Pre-Event Rules ── */}
        <Card style={{marginBottom:16,padding:'18px 20px'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
            <div>
              <div style={{fontWeight:800,color:C.navy,fontSize:15}}>⏰ Pre-Event Deadlines</div>
              <div style={{fontSize:11,color:C.muted,marginTop:2}}>Define when specific units must be serviced before important events</div>
            </div>
            <Btn small onClick={()=>{setEditEvent({name:'',trigger_type:'hebrew_date',trigger_value:'7-1',recurrence:'annual',days_before:7,apply_to:'all',group_ids:[],unit_ids:[],active:true});setShowEventModal(true)}}>+ New Event</Btn>
          </div>
          {schedulingEvents.length===0&&<div style={{color:C.muted,fontSize:13,textAlign:'center',padding:'16px 0'}}>No events yet.</div>}
          {schedulingEvents.map(evt=>{
            const evtDate=getNextEventDate(evt)
            const deadline=evtDate?new Date(evtDate):null
            if(deadline) deadline.setDate(deadline.getDate()-(evt.days_before||7))
            return <div key={evt.id} style={{border:`1px solid ${evt.active?C.border:'#f0f2f5'}`,borderRadius:10,padding:'12px 14px',marginBottom:8,opacity:evt.active?1:.6}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8}}>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,color:C.navy,fontSize:14}}>{evt.name}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:3}}>
                    {evt.days_before} days before · {evt.apply_to==='all'?'All units':evt.apply_to==='groups'?`${evt.group_ids?.length||0} group(s)`:evt.apply_to==='units'?`${evt.unit_ids?.length||0} unit(s)`:'Groups + Units'}
                    {evtDate&&` · Next: ${fmtShort(evtDate)} (deadline ${fmtShort(deadline)})`}
                  </div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                    {evt.trigger_type==='hebrew_date'?`Hebrew date: ${evt.trigger_value}`:evt.trigger_type==='gregorian_date'?`Date: ${evt.trigger_value}`:`Holiday`}
                    {' · '}{evt.recurrence==='annual'?'Annual':'One-time'}
                  </div>
                </div>
                <div style={{display:'flex',gap:6,alignItems:'center'}}>
                  <button onClick={()=>saveSchedEvent({...evt,active:!evt.active})} style={{background:evt.active?'#eafaf1':'#f0f2f5',border:'none',borderRadius:7,padding:'5px 10px',fontSize:11,color:evt.active?'#1e8449':C.muted,cursor:'pointer',fontWeight:600}}>{evt.active?'Active':'Off'}</button>
                  <button onClick={()=>{setEditEvent(evt);setShowEventModal(true)}} style={{background:'#eef5fb',border:'none',borderRadius:7,padding:'5px 10px',fontSize:12,color:'#2d6a9f',cursor:'pointer',fontWeight:600}}>✏️</button>
                  <button onClick={()=>{if(window.confirm('Delete this event?')) deleteSchedEvent(evt.id)}} style={{background:'#fdecea',border:'none',borderRadius:7,padding:'5px 10px',fontSize:12,color:'#c0392b',cursor:'pointer',fontWeight:600}}>🗑</button>
                </div>
              </div>
            </div>
          })}
        </Card>
      </>}

      {configTab==='users'&&<>
        {/* My Calendar Preferences */}
        {currentUser&&<Card style={{marginBottom:18,padding:'16px 20px'}}>
          <div style={{fontWeight:800,color:C.navy,fontSize:15,marginBottom:14}}>📅 My Calendar Preferences</div>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {[
              {key:'showHebrew',   label:'Show Hebrew dates on calendar', default:true},
              {key:'showParsha',   label:'Show Parsha on Shabbos',        default:true},
              {key:'showHolidays', label:'Show Jewish holidays',          default:true},
            ].map(({key,label,default:def})=>{
              const val = currentUser.preferences?.[key] !== undefined ? currentUser.preferences[key] : def
              return <div key={key} style={{display:'flex',alignItems:'center',justifyContent:'space-between',paddingBottom:10,borderBottom:`1px solid ${C.border}`}}>
                <span style={{fontSize:13,fontWeight:600,color:C.text}}>{label}</span>
                <button onClick={()=>saveUserPreferences({[key]:!val})}
                  style={{width:44,height:24,borderRadius:12,border:'none',cursor:'pointer',background:val?'#1e8449':'#ccc',position:'relative',transition:'all .2s'}}>
                  <div style={{position:'absolute',top:2,left:val?22:2,width:20,height:20,borderRadius:'50%',background:'#fff',transition:'all .2s'}}/>
                </button>
              </div>
            })}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span style={{fontSize:13,fontWeight:600,color:C.text}}>Holiday language</span>
              <div style={{display:'flex',gap:6}}>
                {[{v:'english',l:'English'},{v:'hebrew',l:'עברית'}].map(({v,l})=>{
                  const active=(currentUser.preferences?.holidayLang||'english')===v
                  return <button key={v} onClick={()=>saveUserPreferences({holidayLang:v})}
                    style={{padding:'5px 14px',border:`2px solid ${active?C.navy:C.border}`,borderRadius:8,background:active?C.navy:'#fff',color:active?'#fff':C.muted,fontWeight:700,fontSize:12,cursor:'pointer'}}>
                    {l}
                  </button>
                })}
              </div>
            </div>
          </div>
        </Card>}

          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:8}}>
            <div style={{fontSize:13,color:C.muted}}>{users.length} active users</div>
            {isAdmin&&<Btn onClick={()=>setShowUserForm(!showUserForm)}>+ Invite User</Btn>}
          </div>
          {!isAdmin&&<div style={{background:'#fefae6',border:'1px solid #f0d060',borderRadius:8,padding:'8px 14px',marginBottom:12,fontSize:12,color:'#7a5c00'}}>🔒 Only Admins can manage users.</div>}
          {inviteMsg&&<div style={{background:inviteMsg.startsWith('✅')?'#eafaf1':'#fdecea',border:`1px solid ${inviteMsg.startsWith('✅')?'#a9dfbf':'#f5c6c6'}`,borderRadius:8,padding:'10px 14px',fontSize:12,color:inviteMsg.startsWith('✅')?'#1e8449':'#c0392b',marginBottom:12}}>{inviteMsg}</div>}

          {showUserForm&&isAdmin&&<Card style={{padding:isMobile?16:24,marginBottom:16,maxWidth:isMobile?'100%':700}}>
            <h2 style={{fontSize:14,fontWeight:700,marginBottom:14,color:C.text}}>Invite New User</h2>
            <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:10,marginBottom:12}}>
              <div><SLabel>Full Name</SLabel><input value={userForm.name} onChange={e=>setUserForm({...userForm,name:e.target.value})} placeholder="First Last" style={inp16}/></div>
              <div><SLabel>Email Address</SLabel><input type="email" value={userForm.email} onChange={e=>setUserForm({...userForm,email:e.target.value})} placeholder="their@email.com" style={inp16}/></div>
            </div>
            <div style={{marginBottom:14}}>
              <SLabel>Role</SLabel>
              <div style={{display:'flex',gap:8}}>
                {['Admin','Manager','Technician'].map(r=><button key={r} onClick={()=>setUserForm({...userForm,role:r})} style={{flex:1,padding:'10px 0',border:`2px solid ${userForm.role===r?C.navy:C.border}`,borderRadius:9,background:userForm.role===r?C.navy:'#fff',color:userForm.role===r?'#fff':C.muted,fontWeight:600,fontSize:12,cursor:'pointer'}}>{r}</button>)}
              </div>
            </div>
            <div style={{background:'#eef5fb',border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 14px',fontSize:12,color:C.steel,marginBottom:14}}>
              📧 An invitation email will be sent. They click the link to set their own password and gain access based on their role.
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <Btn outline color={C.muted} onClick={()=>{setShowUserForm(false);setInviteMsg('')}}>Cancel</Btn>
              <Btn disabled={!userForm.name.trim()||!userForm.email.trim()||inviting} onClick={inviteUser}>{inviting?'Sending invite…':'Send Invite →'}</Btn>
            </div>
          </Card>}

          <Card>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['User','Email','Role',''].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
              <tbody>{users.map((u,i)=><tr key={u.id} className="rh" style={{background:i%2?'#fafbfc':'#fff'}}>
                <td style={TD}><div style={{display:'flex',alignItems:'center',gap:10}}><Avatar name={u.name} size={32}/><div><div style={{fontWeight:600,color:C.text,fontSize:13}}>{u.name}</div><div style={{fontSize:10,color:C.muted}}>{u.auth_id?'✓ Active':'⏳ Invite pending'}</div></div></div></td>
                <td style={{...TD,color:C.muted,fontSize:12}}>{u.email||'—'}</td>
                <td style={TD}><Badge label={u.role} color={u.role==='Admin'?'#7a3060':u.role==='Manager'?C.navy:C.steel} bg={u.role==='Admin'?'#f5e8f0':u.role==='Manager'?'#e8f0fa':'#eef5fb'}/></td>
                <td style={TD}>{isAdmin&&u.id!==currentUser?.id&&<Btn small danger onClick={async()=>{if(window.confirm(`Remove ${u.name}?`))await supabase.from('users').update({active:false}).eq('id',u.id)}}>Remove</Btn>}</td>
              </tr>)}</tbody>
            </table>
          </Card>
        </>}

        {/* Season */}
        {configTab==='season'&&<Card style={{padding:isMobile?18:30,maxWidth:500}}>
          <h2 style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:6}}>Season Toggle</h2>
          <p style={{fontSize:13,color:C.muted,marginBottom:24,lineHeight:1.6}}>Switching season instantly changes filter intervals for all units across all devices in real time.</p>
          <SeasonToggle season={season} onToggle={toggleSeason} canToggle={isManager}/>
          {!isManager&&<p style={{fontSize:12,color:C.muted,marginTop:12}}>🔒 Managers and Admins only.</p>}
          <div style={{marginTop:24,background:'#f4f6f9',borderRadius:12,padding:'16px 18px'}}>
            <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:10}}>What changes when you switch?</div>
            <div style={{fontSize:12,color:C.muted,lineHeight:2}}>
              • Units with a winter interval switch to that interval<br/>
              • Units with no winter interval show as <strong>OFF</strong><br/>
              • All due dates recalculate instantly on every device<br/>
              • Purchase order calculations use the active season's schedule
            </div>
          </div>
        </Card>}
      </>}

    </main>

    {/* ── Modals ── */}
    {/* ── First-time stock deduction prompt ── */}
    {stockPrompt&&<div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,backdropFilter:'blur(3px)',padding:20}}>
      <div style={{background:'#fff',borderRadius:18,width:'100%',maxWidth:420,boxShadow:'0 24px 80px rgba(0,0,0,.4)',overflow:'hidden'}}>
        {/* Header */}
        <div style={{background:'linear-gradient(135deg,#0f2237,#1a3d5c)',padding:'18px 22px'}}>
          <div style={{color:'#fff',fontSize:16,fontWeight:800}}>📦 First Time Logging</div>
          <div style={{color:'#7a9ab8',fontSize:12,marginTop:3}}>
            {stockPrompt.units.length} unit{stockPrompt.units.length>1?'s':''} logged for the first time
          </div>
        </div>
        {/* Unit list */}
        <div style={{padding:'14px 22px',borderBottom:`1px solid ${C.border}`}}>
          {stockPrompt.units.map(u=><div key={u.id} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:12}}>
            <span style={{fontWeight:600,color:C.text}}>{u.area}</span>
            <span style={{fontFamily:'JetBrains Mono,monospace',color:C.steel}}>{u.size} × {u.qty}</span>
          </div>)}
        </div>
        {/* Checkbox */}
        <div style={{padding:'16px 22px',borderBottom:`1px solid ${C.border}`}}>
          <label style={{display:'flex',alignItems:'flex-start',gap:12,cursor:'pointer'}}
            onTouchEnd={e=>{e.preventDefault();setStockPrompt({...stockPrompt,deduct:!stockPrompt.deduct})}}>
            <input type="checkbox" checked={stockPrompt.deduct!==false}
              onChange={e=>setStockPrompt({...stockPrompt,deduct:e.target.checked})}
              style={{width:22,height:22,marginTop:1,cursor:'pointer',accentColor:C.navy,flexShrink:0}}/>
            <div>
              <div style={{fontWeight:700,fontSize:14,color:C.text}}>Remove from inventory</div>
              <div style={{fontSize:12,color:C.muted,marginTop:2,lineHeight:1.5}}>
                Uncheck if these filters were already removed from inventory previously.
              </div>
            </div>
          </label>
        </div>
        {/* Confirm button */}
        <div style={{padding:'14px 22px'}}>
          <button
            onTouchEnd={e=>{e.preventDefault();doSubmitLog(stockPrompt.deduct!==false)}}
            onClick={()=>doSubmitLog(stockPrompt.deduct!==false)}
            style={{width:'100%',padding:'14px',background:C.navy,color:'#fff',border:'none',borderRadius:11,fontSize:15,fontWeight:700,cursor:'pointer',touchAction:'manipulation',WebkitTapHighlightColor:'transparent',userSelect:'none'}}>
            ✓ Confirm &amp; Submit
          </button>
        </div>
      </div>
    </div>}



    {/* ══ INVENTORY EDIT MODAL ══ */}
    {editingFilter&&isAdmin&&<div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,backdropFilter:'blur(3px)',padding:16}}>
      <div style={{background:'#fff',borderRadius:18,width:'100%',maxWidth:420,boxShadow:'0 24px 80px rgba(0,0,0,.4)',overflow:'hidden'}}>
        <div style={{background:'linear-gradient(135deg,#0f2237,#1a3d5c)',padding:'18px 22px'}}>
          <div style={{color:'#fff',fontSize:16,fontWeight:800}}>Edit Filter Size</div>
          <div style={{color:'#7a9ab8',fontSize:12,marginTop:3,fontFamily:'JetBrains Mono,monospace'}}>{editingFilter.size}</div>
        </div>
        <div style={{padding:'20px 22px',display:'flex',flexDirection:'column',gap:14}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div>
              <SLabel>Current Stock (filters)</SLabel>
              <input type="number" min={0} value={editingFilter.stock||0}
                onChange={e=>setEditingFilter({...editingFilter,stock:parseInt(e.target.value)||0})}
                style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'11px 13px',fontSize:16,outline:'none'}}/>
              <div style={{fontSize:11,color:C.muted,marginTop:4}}>
                {Math.floor((editingFilter.stock||0)/12)} boxes + {(editingFilter.stock||0)%12} singles
              </div>
            </div>
            <div>
              <SLabel>Min Stock (filters)</SLabel>
              <input type="number" min={0} value={editingFilter.min_stock||0}
                onChange={e=>setEditingFilter({...editingFilter,min_stock:parseInt(e.target.value)||0})}
                style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'11px 13px',fontSize:16,outline:'none'}}/>
            </div>
          </div>
          <div>
            <SLabel>Brand (optional)</SLabel>
            <input value={editingFilter.brand||''} onChange={e=>setEditingFilter({...editingFilter,brand:e.target.value})}
              placeholder="e.g. Filtrete"
              style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'11px 13px',fontSize:14,outline:'none'}}/>
          </div>
          <div>
            <SLabel>Notes (optional)</SLabel>
            <input value={editingFilter.notes||''} onChange={e=>setEditingFilter({...editingFilter,notes:e.target.value})}
              placeholder="Any notes..."
              style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'11px 13px',fontSize:14,outline:'none'}}/>
          </div>
        </div>
        <div style={{padding:'14px 22px',borderTop:`1px solid ${C.border}`,display:'flex',gap:10}}>
          <Btn outline color={C.muted} onClick={()=>setEditingFilter(null)} style={{flex:1}}>Cancel</Btn>
          <Btn onClick={saveFilterEdit} style={{flex:2,textAlign:'center'}}>💾 Save Changes</Btn>
        </div>
      </div>
    </div>}

    {/* ══ UNIT GROUP MODAL ══ */}
    {showGroupModal&&editGroup&&<div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,padding:16}}>
      <div style={{background:'#fff',borderRadius:18,width:'100%',maxWidth:500,maxHeight:'90vh',display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 24px 80px rgba(0,0,0,.4)'}}>
        <div style={{background:'linear-gradient(135deg,#0f2237,#1a3d5c)',padding:'18px 22px',flexShrink:0}}>
          <div style={{color:'#fff',fontSize:16,fontWeight:800}}>{editGroup.id?'Edit Group':'New Unit Group'}</div>
          <div style={{color:'#7a9ab8',fontSize:12,marginTop:3}}>Group units together for pre-event scheduling rules</div>
        </div>
        <div style={{padding:'20px 22px',overflowY:'auto',display:'flex',flexDirection:'column',gap:14}}>
          <div>
            <SLabel>Group Name</SLabel>
            <input value={editGroup.name} onChange={e=>setEditGroup({...editGroup,name:e.target.value})}
              placeholder="e.g. Main Shul Large Units"
              style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'10px 12px',fontSize:14,outline:'none'}}/>
          </div>
          <div>
            <SLabel>Description (Optional)</SLabel>
            <input value={editGroup.description||''} onChange={e=>setEditGroup({...editGroup,description:e.target.value})}
              placeholder="e.g. All large AC units in the main shul"
              style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'10px 12px',fontSize:14,outline:'none'}}/>
          </div>
          <div>
            <SLabel>Units in this Group ({(editGroup.unit_ids||[]).length} selected)</SLabel>
            <div style={{maxHeight:240,overflowY:'auto',border:`1px solid ${C.border}`,borderRadius:9,padding:8,display:'flex',flexDirection:'column',gap:4}}>
              {[...new Set(units.map(u=>u.id))].map(uid=>{
                const u=units.find(x=>x.id===uid)
                if(!u) return null
                const selected=(editGroup.unit_ids||[]).includes(uid)
                return <div key={uid} onClick={()=>{
                  const curr=editGroup.unit_ids||[]
                  setEditGroup({...editGroup,unit_ids:selected?curr.filter(x=>x!==uid):[...curr,uid]})
                }} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',borderRadius:7,cursor:'pointer',background:selected?'#eef5fb':'transparent',border:`1px solid ${selected?'#c3daf5':'transparent'}`}}>
                  <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${selected?C.navy:C.border}`,background:selected?C.navy:'#fff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    {selected&&<span style={{color:'#fff',fontSize:11,fontWeight:900}}>✓</span>}
                  </div>
                  <div>
                    <div style={{fontWeight:600,fontSize:13,color:C.text}}>{u.area}</div>
                    {u.location&&u.location!==u.area&&<div style={{fontSize:11,color:C.muted}}>📍 {u.location}</div>}
                  </div>
                </div>
              })}
            </div>
          </div>
        </div>
        <div style={{padding:'14px 22px',borderTop:`1px solid ${C.border}`,display:'flex',gap:10,flexShrink:0}}>
          <Btn outline color={C.muted} onClick={()=>{setShowGroupModal(false);setEditGroup(null)}} style={{flex:1}}>Cancel</Btn>
          <Btn onClick={async()=>{await saveUnitGroup(editGroup);setShowGroupModal(false);setEditGroup(null)}} style={{flex:2}} disabled={!editGroup.name.trim()}>💾 Save Group</Btn>
        </div>
      </div>
    </div>}

    {/* ══ SCHEDULING EVENT MODAL ══ */}
    {showEventModal&&editEvent&&<div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,padding:16}}>
      <div style={{background:'#fff',borderRadius:18,width:'100%',maxWidth:520,maxHeight:'92vh',display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 24px 80px rgba(0,0,0,.4)'}}>
        <div style={{background:'linear-gradient(135deg,#0f2237,#1a3d5c)',padding:'18px 22px',flexShrink:0}}>
          <div style={{color:'#fff',fontSize:16,fontWeight:800}}>{editEvent.id?'Edit Pre-Event Rule':'New Pre-Event Rule'}</div>
          <div style={{color:'#7a9ab8',fontSize:12,marginTop:3}}>Ensure units are serviced before an important date</div>
        </div>
        <div style={{padding:'20px 22px',overflowY:'auto',display:'flex',flexDirection:'column',gap:14}}>
          {/* Name */}
          <div>
            <SLabel>Rule Name</SLabel>
            <input value={editEvent.name} onChange={e=>setEditEvent({...editEvent,name:e.target.value})}
              placeholder="e.g. Before Rosh Hashana, Yeshiva Dinner"
              style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'10px 12px',fontSize:14,outline:'none'}}/>
          </div>
          {/* Trigger Type + Date Picker */}
          <div>
            <SLabel>Date Trigger</SLabel>
            {/* Tab switcher */}
            <div style={{display:'flex',gap:0,background:'#f0f2f5',borderRadius:10,padding:3,marginBottom:12}}>
              {[
                {v:'hebrew_date',  l:'✡️ Hebrew Date'},
                {v:'holiday_key',  l:'🕍 Holiday'},
                {v:'gregorian_date',l:'📅 Gregorian'},
              ].map(({v,l})=>{
                const active=editEvent.trigger_type===v
                return <button key={v} onClick={()=>setEditEvent({...editEvent,trigger_type:v,trigger_value:v==='hebrew_date'?'7-1':v==='holiday_key'?'7-1':v==='gregorian_date'?'':''})}
                  style={{flex:1,padding:'7px 4px',border:'none',borderRadius:8,background:active?'#fff':'transparent',color:active?C.navy:C.muted,fontWeight:active?700:500,fontSize:11,cursor:'pointer',boxShadow:active?'0 1px 4px rgba(0,0,0,.1)':'none',transition:'all .15s'}}>
                  {l}
                </button>
              })}
            </div>

            {/* ── HEBREW DATE PICKER ── */}
            {editEvent.trigger_type==='hebrew_date'&&(()=>{
              const HMONTHS_HE=['ניסן','אייר','סיון','תמוז','אב','אלול','תשרי','חשון','כסלו','טבת','שבט','אדר']
              const HMONTHS_EN=['Nissan','Iyar','Sivan','Tammuz','Av','Elul','Tishrei','Cheshvan','Kislev','Tevet','Shevat','Adar']
              const HDAYS=[30,29,30,29,30,29,30,29,30,29,30,29]
              const HOL={'7-1':'Rosh Hashana','7-3':'Tzom Gedaliah','7-10':'Yom Kippur','7-15':'Sukkos','7-21':'Hoshana Raba','7-22':'Shemini Atzeres','7-23':'Simchas Torah','9-25':'Chanuka','10-10':'Asara BeTevet','11-15':'Tu BiShvat','12-13':'Taanis Esther','12-14':'Purim','1-15':'Pesach','1-21':'Shvii Shel Pesach','2-14':'Pesach Sheni','2-18':'Lag BaOmer','2-28':'Yom Yerushalayim','3-6':'Shavuos','4-17':'17 Tammuz','5-1':'Rosh Chodesh Av','5-9':'Tisha BeAv','5-15':'Tu BeAv','6-1':'Rosh Chodesh Elul'}
              const [selM,selD]=(editEvent.trigger_value||'7-1').split('-').map(Number)
              const monthIdx=selM-1
              const days=HDAYS[monthIdx]||29
              const YEAR_HE='תשפ״ז'
              return <div style={{border:`1.5px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}>
                {/* Header with month + year */}
                <div style={{background:'#f8fafc',borderBottom:`1px solid ${C.border}`,padding:'10px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <button onClick={()=>{const m=selM<=1?12:selM-1;setEditEvent({...editEvent,trigger_value:`${m}-${Math.min(selD,HDAYS[m-2]||29)}`})}}
                    style={{border:`1px solid ${C.border}`,background:'#fff',borderRadius:7,width:32,height:32,cursor:'pointer',fontSize:15,color:C.muted}}>‹</button>
                  <div style={{textAlign:'center'}}>
                    <div style={{fontWeight:800,fontSize:15,color:C.navy,direction:'rtl'}}>{HMONTHS_HE[monthIdx]}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:1}}>{HMONTHS_EN[monthIdx]} · repeats annually</div>
                  </div>
                  <button onClick={()=>{const m=selM>=12?1:selM+1;setEditEvent({...editEvent,trigger_value:`${m}-${Math.min(selD,HDAYS[m-1]||29)}`})}}
                    style={{border:`1px solid ${C.border}`,background:'#fff',borderRadius:7,width:32,height:32,cursor:'pointer',fontSize:15,color:C.muted}}>›</button>
                </div>
                {/* Day grid - 7 columns */}
                <div style={{padding:'8px 10px'}}>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',marginBottom:4}}>
                    {['א','ב','ג','ד','ה','ו','ש'].map((d,i)=><div key={i} style={{textAlign:'center',fontSize:9,fontWeight:700,color:i===6?'#1a5c9a':C.muted,paddingBottom:3,direction:'rtl'}}>{d}</div>)}
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:3}}>
                    {Array.from({length:days},(_,i)=>{
                      const d=i+1
                      const key=`${selM}-${d}`
                      const active=selD===d
                      const hol=HOL[key]
                      return <button key={d} onClick={()=>setEditEvent({...editEvent,trigger_value:key})}
                        style={{aspectRatio:'1',borderRadius:7,border:`1.5px solid ${active?C.navy:hol?'#f0c070':'transparent'}`,background:active?C.navy:hol?'#fff8f0':'transparent',color:active?'#fff':hol?'#c0392b':C.text,fontWeight:active?800:hol?700:400,fontSize:11,cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:1,transition:'all .1s'}}>
                        {d}
                        {hol&&!active&&<div style={{width:3,height:3,borderRadius:'50%',background:'#c0392b',marginTop:1}}/>}
                      </button>
                    })}
                  </div>
                </div>
                {/* Selected date display */}
                <div style={{background:'#f0f8ff',borderTop:`1px solid ${C.border}`,padding:'10px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <div style={{fontWeight:700,color:C.navy,fontSize:13}}>{selD} {HMONTHS_EN[monthIdx]}</div>
                    {HOL[`${selM}-${selD}`]&&<div style={{fontSize:11,color:'#c0392b',marginTop:1,fontWeight:600}}>🕍 {HOL[`${selM}-${selD}`]}</div>}
                    {!HOL[`${selM}-${selD}`]&&<div style={{fontSize:11,color:C.muted,marginTop:1}}>No holiday · repeats annually</div>}
                  </div>
                  <div style={{textAlign:'right',direction:'rtl'}}>
                    <div style={{fontSize:13,color:'#1a5c9a',fontWeight:700}}>{selD} {HMONTHS_HE[monthIdx]}</div>
                    <div style={{fontSize:10,color:C.muted,direction:'ltr',marginTop:1}}>{YEAR_HE} (example year)</div>
                  </div>
                </div>
              </div>
            })()}

            {/* ── HOLIDAY PICKER ── */}
            {editEvent.trigger_type==='holiday_key'&&<div style={{border:`1.5px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}>
              {/* Jewish Holidays */}
              <div style={{background:'#f8fafc',padding:'8px 14px',borderBottom:`1px solid ${C.border}`}}>
                <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.06em'}}>✡️ Jewish Holidays</div>
              </div>
              {[
                {section:'Tishrei'},
                {v:'7-1', en:'Rosh Hashana',        he:'ראש השנה',     date:'1 Tishrei'},
                {v:'7-3', en:'Tzom Gedaliah',        he:'צום גדליה',    date:'3 Tishrei'},
                {v:'7-10',en:'Yom Kippur',            he:'יום כיפור',    date:'10 Tishrei'},
                {v:'7-15',en:'Sukkos',                he:'סוכות',        date:'15 Tishrei'},
                {v:'7-21',en:'Hoshana Raba',          he:'הושענא רבה',   date:'21 Tishrei'},
                {v:'7-22',en:'Shemini Atzeres',       he:'שמיני עצרת',   date:'22 Tishrei'},
                {v:'7-23',en:'Simchas Torah',         he:'שמחת תורה',    date:'23 Tishrei'},
                {section:'Winter'},
                {v:'9-25', en:'Chanuka',              he:'חנוכה',        date:'25 Kislev'},
                {v:'10-10',en:'Asara BeTevet',        he:'עשרה בטבת',    date:'10 Tevet'},
                {v:'11-15',en:'Tu BiShvat',           he:'ט״ו בשבט',     date:'15 Shevat'},
                {v:'12-13',en:'Taanis Esther',        he:'תענית אסתר',   date:'13 Adar'},
                {v:'12-14',en:'Purim',                he:'פורים',        date:'14 Adar'},
                {section:'Spring'},
                {v:'1-15', en:'Pesach',               he:'פסח',          date:'15 Nissan'},
                {v:'1-21', en:'Shvii Shel Pesach',   he:'שביעי של פסח', date:'21 Nissan'},
                {v:'2-14', en:'Pesach Sheni',         he:'פסח שני',      date:'14 Iyar'},
                {v:'2-18', en:'Lag BaOmer',           he:'ל״ג בעומר',    date:'18 Iyar'},
                {v:'2-28', en:'Yom Yerushalayim',    he:'יום ירושלים',  date:'28 Iyar'},
                {v:'3-6',  en:'Shavuos',              he:'שבועות',       date:'6 Sivan'},
                {section:'Summer'},
                {v:'4-17', en:'17 Tammuz',            he:'י״ז בתמוז',    date:'17 Tammuz'},
                {v:'5-1',  en:'Rosh Chodesh Av',      he:'ראש חודש אב',  date:'1 Av'},
                {v:'5-9',  en:'Tisha BeAv',            he:'תשעה באב',     date:'9 Av'},
                {v:'5-15', en:'Tu BeAv',               he:'ט״ו באב',      date:'15 Av'},
                {v:'6-1',  en:'Rosh Chodesh Elul',    he:'ראש חודש אלול',date:'1 Elul'},
                {section:'us', label:'🇺🇸 US Federal Holidays'},
                {v:'us-0101',en:"New Year's Day",      he:'',date:'Jan 1'},
                {v:'us-mlk', en:'MLK Day',             he:'',date:'3rd Mon Jan'},
                {v:'us-pres',en:"Presidents' Day",     he:'',date:'3rd Mon Feb'},
                {v:'us-mem', en:'Memorial Day',        he:'',date:'Last Mon May'},
                {v:'us-0704',en:'Independence Day',    he:'',date:'Jul 4'},
                {v:'us-lab', en:'Labor Day',           he:'',date:'1st Mon Sep'},
                {v:'us-col', en:'Columbus Day',        he:'',date:'2nd Mon Oct'},
                {v:'us-vet', en:"Veterans' Day",       he:'',date:'Nov 11'},
                {v:'us-thx', en:'Thanksgiving',        he:'',date:'4th Thu Nov'},
                {v:'us-1225',en:'Christmas Day',       he:'',date:'Dec 25'},
              ].map((item,i)=>{
                if(item.section==='us') return <div key={i} style={{background:'#f8fafc',padding:'8px 14px',borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.06em'}}>{item.label||'🇺🇸 US Federal Holidays'}</div>
                </div>
                if(item.section) return <div key={i} style={{padding:'5px 14px 3px',background:'#fafbfc',borderBottom:`1px solid ${C.border}`}}>
                  <div style={{fontSize:9,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.08em'}}>{item.section}</div>
                </div>
                const active=editEvent.trigger_value===item.v
                const isUS=item.v&&item.v.startsWith('us-')
                return <div key={i} onClick={()=>setEditEvent({...editEvent,trigger_value:item.v})}
                  style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 14px',cursor:'pointer',background:active?C.navy:'#fff',borderBottom:`1px solid ${C.border}`,transition:'all .1s'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    {isUS&&<span style={{fontSize:12}}>🇺🇸</span>}
                    <div>
                      <div style={{fontWeight:600,fontSize:12,color:active?'#fff':C.text}}>{item.en}</div>
                      {item.he&&<div style={{fontSize:10,color:active?'#7a9ab8':C.muted,direction:'rtl',textAlign:'left'}}>{item.he}</div>}
                    </div>
                  </div>
                  <div style={{fontSize:10,color:active?'#7a9ab8':C.muted,whiteSpace:'nowrap',marginLeft:8,fontWeight:600}}>{item.date}</div>
                </div>
              })}
            </div>}

            {/* ── GREGORIAN DATE PICKER ── */}
            {editEvent.trigger_type==='gregorian_date'&&<div>
              <input type="date" value={editEvent.trigger_value||''} onChange={e=>setEditEvent({...editEvent,trigger_value:e.target.value})}
                style={{width:'100%',border:`1.5px solid ${C.border}`,borderRadius:10,padding:'11px 14px',fontSize:15,outline:'none',marginBottom:8}}/>
              <div style={{background:'#f8fafc',border:`1px solid ${C.border}`,borderRadius:9,padding:'9px 14px',fontSize:12,color:C.muted}}>
                📅 One-time date — use recurrence setting below to make it annual
              </div>
            </div>}
          </div>

                    {/* Days before + Recurrence */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div>
              <SLabel>Days Before</SLabel>
              <input type="number" min={1} max={60} value={editEvent.days_before} onChange={e=>setEditEvent({...editEvent,days_before:parseInt(e.target.value)||7})}
                style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'10px 12px',fontSize:16,outline:'none'}}/>
            </div>
            <div>
              <SLabel>Recurrence</SLabel>
              <div style={{display:'flex',gap:6,marginTop:4}}>
                {[{v:'annual',l:'🔄 Annual'},{v:'once',l:'1️⃣ Once'}].map(({v,l})=>{
                  const active=editEvent.recurrence===v
                  return <button key={v} onClick={()=>setEditEvent({...editEvent,recurrence:v})}
                    style={{flex:1,padding:'9px 0',border:`2px solid ${active?C.navy:C.border}`,borderRadius:9,background:active?C.navy:'#fff',color:active?'#fff':C.muted,fontWeight:700,fontSize:12,cursor:'pointer'}}>{l}</button>
                })}
              </div>
            </div>
          </div>
          {/* Apply To */}
          <div>
            <SLabel>Apply To</SLabel>
            <div style={{display:'flex',gap:8,marginBottom:10}}>
              {[{v:'all',l:'All Units'},{v:'groups',l:'Groups'},{v:'units',l:'Specific Units'}].map(({v,l})=>{
                const active=editEvent.apply_to===v
                return <button key={v} onClick={()=>setEditEvent({...editEvent,apply_to:v})}
                  style={{flex:1,padding:'8px 4px',border:`2px solid ${active?C.navy:C.border}`,borderRadius:9,background:active?C.navy:'#fff',color:active?'#fff':C.muted,fontWeight:700,fontSize:12,cursor:'pointer'}}>{l}</button>
              })}
            </div>
            {editEvent.apply_to==='groups'&&<div style={{border:`1px solid ${C.border}`,borderRadius:9,padding:8,maxHeight:160,overflowY:'auto',display:'flex',flexDirection:'column',gap:4}}>
              {unitGroups.length===0
                ?<div style={{color:C.muted,fontSize:12,padding:8}}>No groups yet. Create groups in the Unit Groups section first.</div>
                :unitGroups.map(g=>{
                  const selected=(editEvent.group_ids||[]).includes(g.id)
                  return <div key={g.id} onClick={()=>{
                    const curr=editEvent.group_ids||[]
                    setEditEvent({...editEvent,group_ids:selected?curr.filter(x=>x!==g.id):[...curr,g.id]})
                  }} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',borderRadius:7,cursor:'pointer',background:selected?'#eef5fb':'transparent',border:`1px solid ${selected?'#c3daf5':'transparent'}`}}>
                    <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${selected?C.navy:C.border}`,background:selected?C.navy:'#fff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      {selected&&<span style={{color:'#fff',fontSize:11,fontWeight:900}}>✓</span>}
                    </div>
                    <div>
                      <div style={{fontWeight:600,fontSize:13}}>{g.name}</div>
                      <div style={{fontSize:11,color:C.muted}}>{(g.unit_ids||[]).length} units</div>
                    </div>
                  </div>
                })}
            </div>}
            {editEvent.apply_to==='units'&&<div style={{border:`1px solid ${C.border}`,borderRadius:9,padding:8,maxHeight:200,overflowY:'auto',display:'flex',flexDirection:'column',gap:4}}>
              {[...new Set(units.map(u=>u.id))].map(uid=>{
                const u=units.find(x=>x.id===uid)
                if(!u) return null
                const selected=(editEvent.unit_ids||[]).includes(uid)
                return <div key={uid} onClick={()=>{
                  const curr=editEvent.unit_ids||[]
                  setEditEvent({...editEvent,unit_ids:selected?curr.filter(x=>x!==uid):[...curr,uid]})
                }} style={{display:'flex',alignItems:'center',gap:10,padding:'7px 10px',borderRadius:7,cursor:'pointer',background:selected?'#eef5fb':'transparent',border:`1px solid ${selected?'#c3daf5':'transparent'}`}}>
                  <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${selected?C.navy:C.border}`,background:selected?C.navy:'#fff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    {selected&&<span style={{color:'#fff',fontSize:11,fontWeight:900}}>✓</span>}
                  </div>
                  <div>
                    <div style={{fontWeight:600,fontSize:13}}>{u.area}</div>
                    {u.location&&u.location!==u.area&&<div style={{fontSize:11,color:C.muted}}>📍 {u.location}</div>}
                  </div>
                </div>
              })}
            </div>}
          </div>
        </div>
        <div style={{padding:'14px 22px',borderTop:`1px solid ${C.border}`,display:'flex',gap:10,flexShrink:0}}>
          <Btn outline color={C.muted} onClick={()=>{setShowEventModal(false);setEditEvent(null)}} style={{flex:1}}>Cancel</Btn>
          <Btn onClick={async()=>{await saveSchedulingEvent(editEvent);setShowEventModal(false);setEditEvent(null)}} style={{flex:2}} disabled={!editEvent.name.trim()}>💾 Save Rule</Btn>
        </div>
      </div>
    </div>}

    {/* Edit Log Modal */}
    {editLogModal&&isAdmin&&<div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,backdropFilter:'blur(3px)',padding:16}}>
      <div style={{background:'#fff',borderRadius:18,width:'100%',maxWidth:500,boxShadow:'0 24px 80px rgba(0,0,0,.4)',overflow:'hidden',maxHeight:'95vh',display:'flex',flexDirection:'column'}}>
        {/* Dark header */}
        <div style={{background:'linear-gradient(135deg,#0f2237,#1a3d5c)',padding:'18px 22px',display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexShrink:0}}>
          <div>
            <div style={{color:'#fff',fontSize:16,fontWeight:800,marginBottom:3}}>{editLogModal.area}</div>
            <div style={{color:'#7a9ab8',fontSize:12}}>
              {editLogModal.location&&editLogModal.location!==editLogModal.area&&`📍 ${editLogModal.location} · `}Originally logged {fmtShort(editLogModal.change_date)}
            </div>
          </div>
          <span style={{background:'rgba(255,255,255,.12)',color:'#fff',borderRadius:20,padding:'4px 10px',fontSize:11,fontWeight:700,flexShrink:0,marginLeft:10}}>
            {editLogModal.log_type==='inspection'?'👁 Inspection':'✓ Replacement'}
          </span>
        </div>
        {/* Original values strip */}
        <div style={{background:'#f8fafc',borderBottom:`1px solid ${C.border}`,padding:'10px 22px',display:'flex',gap:16,flexWrap:'wrap',flexShrink:0}}>
          {editLogModal.size&&<span style={{fontSize:11,color:C.muted}}>Filter: <strong style={{color:C.text,fontFamily:'JetBrains Mono,monospace'}}>{editLogModal.size}</strong></span>}
          <span style={{fontSize:11,color:C.muted}}>Original qty: <strong style={{color:C.text}}>{editLogModal.qty_used}</strong></span>
          <span style={{fontSize:11,color:C.muted}}>Worker: <strong style={{color:C.text}}>{editLogModal.worker_name}</strong></span>
          {editLogModal.condition&&<span style={{fontSize:11,color:C.muted}}>Condition: <strong style={{color:C.text}}>{editLogModal.condition}</strong></span>}
        </div>
        {/* Scrollable body */}
        <div style={{padding:'20px 22px',overflowY:'auto',display:'flex',flexDirection:'column',gap:14}}>
          {/* Date + Worker */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div>
              <SLabel>Date of Replacement</SLabel>
              <input type="date" value={editLogForm.change_date} onChange={e=>setEditLogForm({...editLogForm,change_date:e.target.value})}
                style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'10px',fontSize:16,outline:'none'}}/>
            </div>
            <div>
              <SLabel>Replaced By</SLabel>
              <select value={editLogForm.worker_name} onChange={e=>setEditLogForm({...editLogForm,worker_name:e.target.value})}
                style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'11px 12px',fontSize:15,outline:'none',background:'#fff'}}>
                {[...new Set([editLogForm.worker_name,...users.map(u=>u.name)])].filter(Boolean).map(n=><option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
          {/* Qty + Season */}
          {editLogModal?.log_type!=='inspection'&&<div>
            <SLabel>Qty Changed ({editLogModal?.size})</SLabel>
            {(()=>{
              // Max = unit's full qty, not just what was originally logged
              const unitMax = units.find(u=>u.id===editLogModal?.unit_id&&u.size===editLogModal?.size)?.qty || editLogModal?.qty_used || 1
              return <QtyControl
                value={editLogForm.qty_used||1}
                max={unitMax}
                originalQty={editLogModal?.qty_used||1}
                filterSize={null}
                onChange={v=>setEditLogForm({...editLogForm,qty_used:v})}
                onReset={()=>setEditLogForm({...editLogForm,qty_used:editLogModal?.qty_used||1})}
              />
            })()}
          </div>}
          <div>
            <SLabel>Season</SLabel>
            <div style={{display:'flex',gap:6}}>
              {['summer','winter'].map(s=>{
                const active=editLogForm.season===s
                return <button key={s} onClick={()=>setEditLogForm({...editLogForm,season:s})} style={{flex:1,padding:'10px 0',border:`2px solid ${active?C.navy:C.border}`,borderRadius:9,background:active?C.navy:'#fff',color:active?'#fff':C.muted,fontWeight:700,fontSize:13,cursor:'pointer'}}>
                  {s==='summer'?'☀️ Summer':'❄️ Winter'}
                </button>
              })}
            </div>
          </div>
          {/* Condition */}
          <div>
            <SLabel>Filter Condition</SLabel>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {[{c:'Light',color:'#1e8449',bg:'#eafaf1'},{c:'Moderate',color:'#b7860b',bg:'#fefae6'},{c:'Dirty',color:'#d35400',bg:'#fef0e6'},{c:'Very Dirty',color:'#c0392b',bg:'#fdecea'},{c:'Damaged',color:'#922b21',bg:'#fadbd8'}].map(({c,color,bg})=>{
                const active=editLogForm.condition===c
                return <button key={c} onClick={()=>setEditLogForm({...editLogForm,condition:c})}
                  style={{flex:1,minWidth:60,background:active?color:bg,color:active?'#fff':color,border:`2px solid ${color}`,borderRadius:8,padding:'8px 4px',fontSize:11,fontWeight:700,cursor:'pointer'}}>{c}</button>
              })}
            </div>
          </div>
          {/* Notes */}
          <div>
            <SLabel>Notes</SLabel>
            <textarea value={editLogForm.notes} onChange={e=>setEditLogForm({...editLogForm,notes:e.target.value})} rows={2}
              style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'10px',fontSize:14,outline:'none',resize:'vertical'}}/>
          </div>
          {/* Stock panel */}
          {editLogModal?.log_type!=='inspection'&&<div style={{borderRadius:10,overflow:'hidden',border:`1px solid ${C.border}`}}>
            <div style={{background:'#f8fafc',padding:'10px 14px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontSize:12,fontWeight:700,color:C.text}}>📦 Inventory Impact</span>
              <span style={{fontSize:12,fontWeight:700,color:editLogModal?.stock_deducted>0?'#1e8449':C.muted}}>
                {editLogModal?.stock_deducted>0?`✅ Deducted (${editLogModal.stock_deducted} filters)`:'➖ No deduction recorded'}
              </span>
            </div>
            <div style={{padding:'12px 14px'}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <input type="checkbox" id="editDeductStock" checked={editLogForm.deduct_stock} onChange={e=>setEditLogForm({...editLogForm,deduct_stock:e.target.checked})} style={{width:18,height:18,cursor:'pointer'}}/>
                <label htmlFor="editDeductStock" style={{fontSize:13,fontWeight:600,color:C.text,cursor:'pointer'}}>Update inventory stock when saving</label>
              </div>
              {editLogForm.deduct_stock&&(()=>{
                const oldD=editLogModal?.stock_deducted||0
                const newD=editLogForm.qty_used||0
                const delta=newD-oldD
                const inv=inventory.find(i=>i.size===editLogModal?.size)
                return <div style={{marginTop:10,background:'#fff',border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px',fontSize:12}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}><span style={{color:C.muted}}>Old deduction:</span><span style={{fontWeight:700}}>{oldD>0?`${oldD} filters`:'None'}</span></div>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}><span style={{color:C.muted}}>New deduction:</span><span style={{fontWeight:700,color:'#c0392b'}}>{newD} filters</span></div>
                  <div style={{borderTop:`1px solid ${C.border}`,paddingTop:6,display:'flex',justifyContent:'space-between',fontWeight:700}}>
                    <span style={{color:C.text}}>Stock after save:</span>
                    <span style={{color:delta>0?'#c0392b':delta<0?'#1e8449':C.muted}}>{inv?inv.stock:'-'} → {inv?Math.max(0,inv.stock-delta):'-'} {delta!==0?`(${delta>0?'−':'+'} ${Math.abs(delta)})`:'(no change)'}</span>
                  </div>
                </div>
              })()}
            </div>
          </div>}
          {/* Audit note */}
          <div style={{background:'#fff8e1',border:'1px solid #f0d060',borderRadius:9,padding:'9px 13px',fontSize:11,color:'#7a5c00'}}>
            ⚠️ This edit will be recorded — edited by <strong>{currentUser?.name}</strong>
          </div>
        </div>
        {/* Footer */}
        <div style={{padding:'14px 22px',borderTop:`1px solid ${C.border}`,display:'flex',gap:10,flexShrink:0}}>
          <Btn outline color={C.muted} onClick={()=>setEditLogModal(null)} style={{flex:1}}>Cancel</Btn>
          <Btn onClick={()=>saveLogEdit(editLogModal,editLogForm)} style={{flex:2,textAlign:'center',padding:'13px'}}>💾 Save Changes</Btn>
        </div>
      </div>
    </div>}

    {/* Delete Log Modal */}
    {deleteLogModal&&isAdmin&&<div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,backdropFilter:'blur(3px)',padding:20}}>
      <div style={{background:'#fff',borderRadius:18,padding:28,width:'100%',maxWidth:440,boxShadow:'0 12px 48px rgba(0,0,0,.25)'}}>
        <div style={{fontSize:36,textAlign:'center',marginBottom:12}}>🗑️</div>
        <h2 style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:4,textAlign:'center'}}>Delete Log Entry?</h2>
        <p style={{fontSize:13,color:C.muted,marginBottom:4,textAlign:'center'}}><strong>{deleteLogModal.location}</strong> · {fmtShort(deleteLogModal.change_date)}</p>
        {deleteLogModal.stock_deducted>0&&<div style={{background:'#eafaf1',border:'1px solid #a9dfbf',borderRadius:8,padding:'8px 14px',fontSize:12,color:'#1e8449',marginBottom:12,textAlign:'center'}}>
          ✅ Will restore <strong>{deleteLogModal.stock_deducted} filters</strong> ({deleteLogModal.size}) back to inventory
        </div>}
        <div style={{marginBottom:16}}>
          <SLabel>Reason for deletion (required)</SLabel>
          <textarea value={deleteLogReason} onChange={e=>setDeleteLogReason(e.target.value)} rows={2}
            placeholder="e.g. Entered wrong unit, duplicate entry…"
            style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'10px',fontSize:14,outline:'none',resize:'vertical'}}/>
        </div>
        <div style={{display:'flex',gap:8}}>
          <Btn outline color={C.muted} onClick={()=>{setDeleteLogModal(null);setDeleteLogReason('')}} style={{flex:1}}>Cancel</Btn>
          <Btn danger disabled={!deleteLogReason.trim()} onClick={()=>deleteLogEntry(deleteLogModal,deleteLogReason)} style={{flex:2,textAlign:'center'}}>Delete Entry</Btn>
        </div>
      </div>
    </div>}

    {/* Inspect / Postpone Modal */}
    {showInspectModal&&inspectUnit&&<div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.75)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:999,backdropFilter:'blur(3px)'}}>
      <div style={{background:'#fff',borderRadius:'18px 18px 0 0',padding:'24px 20px',width:'100%',maxWidth:520,maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{width:40,height:4,background:'#e2e8f0',borderRadius:4,margin:'0 auto 16px'}}/>
        <h2 style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:4}}>👁 Inspection Only — No Replacement Needed</h2>
        <p style={{fontSize:12,color:C.muted,marginBottom:16}}><strong>{inspectUnit.location}</strong> · {inspectUnit.area}</p>
        <div style={{background:'#eef5fb',borderRadius:8,padding:'10px 14px',fontSize:12,color:C.steel,marginBottom:16}}>
          Filter was inspected and does not need changing yet. The next due date will be pushed forward by the selected number of days.
        </div>
        <div style={{marginBottom:14}}>
          <SLabel>Filter Condition</SLabel>
          <div style={{display:'flex',gap:6}}>
            {[
              {c:'Light',color:'#1e8449',bg:'#eafaf1'},
              {c:'Moderate',color:'#b7860b',bg:'#fefae6'},
            ].map(({c,color,bg})=><button key={c} onClick={()=>setInspectForm({...inspectForm,condition:c})}
              style={{flex:1,padding:'10px 0',border:`2px solid ${inspectForm.condition===c?color:C.border}`,borderRadius:9,background:inspectForm.condition===c?color:bg,color:inspectForm.condition===c?'#fff':color,fontWeight:700,fontSize:13,cursor:'pointer'}}>{c}</button>)}
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <SLabel>Postpone by (max 30 days)</SLabel>
          <div style={{display:'flex',gap:6,marginBottom:8}}>
            {[7,14,21,30].map(d=><button key={d} onClick={()=>setInspectForm({...inspectForm,postpone_days:d})} style={{flex:1,padding:'10px 0',border:`2px solid ${inspectForm.postpone_days===d?'#1a5c9a':C.border}`,borderRadius:9,background:inspectForm.postpone_days===d?'#1a5c9a':'#fff',color:inspectForm.postpone_days===d?'#fff':C.muted,fontWeight:700,fontSize:14,cursor:'pointer'}}>{d}d</button>)}
          </div>
          <input type="number" min={1} max={30} value={inspectForm.postpone_days} onChange={e=>setInspectForm({...inspectForm,postpone_days:Math.min(30,Math.max(1,parseInt(e.target.value)||1))})}
            style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'10px',fontSize:16,outline:'none'}} placeholder="Or enter custom days (max 30)"/>
        </div>
        <div style={{marginBottom:20}}>
          <SLabel>Notes</SLabel>
          <textarea value={inspectForm.notes} onChange={e=>setInspectForm({...inspectForm,notes:e.target.value})}
            rows={2} placeholder="Why is the change being postponed?"
            style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'10px',fontSize:14,outline:'none',resize:'vertical'}}/>
        </div>
        <div style={{display:'flex',gap:8}}>
          <Btn outline color={C.muted} onClick={()=>{setShowInspectModal(false);setInspectUnit(null)}} style={{flex:1}}>Cancel</Btn>
          <Btn disabled={inspecting} onClick={submitInspection} color="#1a5c9a" style={{flex:2,textAlign:'center',padding:'13px'}}>
            {inspecting?'Saving…':'👁 Log Inspection & Postpone'}
          </Btn>
        </div>
      </div>
    </div>}

    {/* QR Code Modal */}
    {showQRModal&&isManager&&<QRModal units={units} onClose={()=>setShowQRModal(false)}/>}

    {/* Print To-Do Modal */}
    {showTodoPrint&&<div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,backdropFilter:'blur(3px)',padding:20}}>
      <div style={{background:'#fff',borderRadius:18,padding:28,width:'100%',maxWidth:420,boxShadow:'0 12px 48px rgba(0,0,0,.25)'}}>
        <h2 style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:4}}>🖨️ Print To-Do List</h2>
        <p style={{fontSize:12,color:C.muted,marginBottom:20}}>Generate a printable checklist for your team.</p>
        <div style={{marginBottom:14}}>
          <SLabel>Period</SLabel>
          <div style={{display:'flex',gap:8}}>
            {[{key:'today',label:'Today'},{key:'week',label:'This Week'}].map(p=><button key={p.key} onClick={()=>setTodoPeriod(p.key)} style={{flex:1,padding:'10px 0',border:`2px solid ${todoPeriod===p.key?C.navy:C.border}`,borderRadius:9,background:todoPeriod===p.key?C.navy:'#fff',color:todoPeriod===p.key?'#fff':C.muted,fontWeight:600,fontSize:13,cursor:'pointer'}}>{p.label}</button>)}
          </div>
        </div>
        <div style={{marginBottom:20}}>
          <SLabel>Worker</SLabel>
          <select value={todoWorker} onChange={e=>setTodoWorker(e.target.value)} style={{width:'100%',border:`1px solid ${C.border}`,borderRadius:9,padding:'10px 13px',fontSize:16,outline:'none',background:'#fff'}}>
            <option value="all">All Workers</option>
            {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div style={{display:'flex',gap:8}}>
          <Btn outline color={C.muted} onClick={()=>setShowTodoPrint(false)} style={{flex:1}}>Cancel</Btn>
          <Btn onClick={()=>{printTodoList(units,changelog,season,todoPeriod,todoWorker,users);setShowTodoPrint(false)}} style={{flex:2,textAlign:'center'}}>🖨️ Print</Btn>
        </div>
      </div>
    </div>}

    {showUnitForm&&canEditUnits&&<UnitFormModal unit={editingUnit} onClose={()=>{setShowUnitForm(false);setEditingUnit(null)}} onSave={()=>{setShowUnitForm(false);setEditingUnit(null);loadAll()}}/>}
    {showChangePassword&&<ChangePasswordModal onClose={()=>setShowChangePassword(false)}/>}
    {unitHistoryModal&&<UnitHistoryModal unit={unitHistoryModal} changelog={changelog} onClose={()=>setUnitHistoryModal(null)}/>}
    {showBulkDate&&<BulkDateModal units={withStatus} onClose={()=>setShowBulkDate(false)} onSave={()=>{setShowBulkDate(false);loadAll()}}/>}

    {/* Stock confirmation */}
    {stockConfirm&&<div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,padding:20}}>
      <div style={{background:'#fff',borderRadius:16,padding:24,maxWidth:360,width:'100%',boxShadow:'0 12px 40px rgba(0,0,0,.2)'}}>
        <h3 style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:8}}>Confirm Stock Change</h3>
        <p style={{fontSize:13,color:C.muted,marginBottom:6}}><strong style={{fontFamily:'JetBrains Mono,monospace',color:C.navy}}>{stockConfirm.item.size}</strong></p>
        <p style={{fontSize:13,color:C.muted,marginBottom:20}}>{stockConfirm.label}<br/>New stock: <strong>{Math.max(0,stockConfirm.item.stock+stockConfirm.delta)}</strong> (was {stockConfirm.item.stock})</p>
        <div style={{display:'flex',gap:8}}>
          <Btn outline color={C.muted} onClick={()=>setStockConfirm(null)} style={{flex:1}}>Cancel</Btn>
          <Btn onClick={()=>adjustStock(stockConfirm.item,stockConfirm.delta)} style={{flex:2,textAlign:'center'}} color={stockConfirm.delta>0?'#1e8449':'#c0392b'}>Confirm</Btn>
        </div>
      </div>
    </div>}

    {/* Receive delivery modal */}
    {receiveModal&&<div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.7)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:999,backdropFilter:'blur(3px)'}}>
      <div style={{background:'#fff',borderRadius:'18px 18px 0 0',padding:'24px 20px',width:'100%',maxWidth:520,maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{width:40,height:4,background:'#e2e8f0',borderRadius:4,margin:'0 auto 16px'}}/>
        <h2 style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:14}}>Receive Delivery</h2>
        <div style={{background:'#f4f6f9',borderRadius:9,padding:'9px 13px',marginBottom:14,fontSize:12,color:C.muted}}>
          <strong style={{color:C.navy,fontFamily:'JetBrains Mono,monospace'}}>{receiveModal.po_number}</strong> · {receiveModal.supplier_name}
        </div>
        <ScrollTable><table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:16}}>
          <thead><tr>{['Filter Size','Ordered','Received','Difference'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
          <tbody>{receiveModal.items.map((item,ii)=>{
            const entered=parseInt(receiveForm[item.size])||0
            const diff=receiveForm[item.size]===''?null:entered-item.boxes_ordered
            return<tr key={ii} style={{borderBottom:`1px solid ${C.border}`}}>
              <td style={{...TD,fontFamily:'JetBrains Mono,monospace',fontWeight:700,color:C.navy}}>{item.size}</td>
              <td style={{...TD,textAlign:'center',fontWeight:600}}>{item.boxes_ordered} boxes</td>
              <td style={TD}><input type="number" min={0} value={receiveForm[item.size]} onChange={e=>setReceiveForm({...receiveForm,[item.size]:e.target.value})} style={{width:70,border:`1px solid ${C.border}`,borderRadius:7,padding:'6px 8px',fontSize:15,textAlign:'center',outline:'none'}}/></td>
              <td style={TD}>{diff===null?<span style={{color:C.muted}}>—</span>:diff===0?<span style={{color:'#1e8449',fontWeight:700}}>✓ Match</span>:diff>0?<span style={{color:'#1a5c9a',fontWeight:700}}>+{diff} extra</span>:<span style={{color:'#c0392b',fontWeight:700}}>{diff} short</span>}</td>
            </tr>
          })}</tbody>
        </table></ScrollTable>
        <div style={{display:'flex',gap:8}}>
          <Btn outline color={C.muted} onClick={()=>setReceiveModal(null)} style={{flex:1}}>Cancel</Btn>
          <Btn onClick={submitReceive} style={{flex:2,textAlign:'center'}}>📦 Confirm & Update Stock</Btn>
        </div>
      </div>
    </div>}
  </div>
}
function AppWithBoundary() {
  return <ErrorBoundary><App/></ErrorBoundary>
}
export default AppWithBoundary